/**
 * SmartPasteInterface - Intelligent D&D Content Paste Interface
 *
 * This component provides an elegant interface for users to paste D&D content
 * and have it automatically parsed, structured, and integrated into their
 * character or monster data.
 *
 * Features:
 * - Auto-detection of content type (spell, item, action, etc.)
 * - Live preview of parsed content with "What We Found" summary
 * - Multiple placement suggestions with field-by-field breakdown
 * - Confidence indicators with detailed explanations
 * - Quick-help panel with example formats
 * - Drag-and-drop support
 * - Undo/redo functionality
 *
 * @date_of_change 2025-09-18 - Initial implementation of smart paste interface
 * @date_of_change 2025-12-05 - Major UX enhancement with What We Found summary, examples panel, and improved visual feedback
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

import IntelligentContentParser from '../../utils/IntelligentContentParser';
import SmartPasteAggregator from '../../utils/SmartPasteAggregator';
import {
  buildSmartPasteImportMeta,
  findSmartPasteConflictCandidates
} from '../../utils/SmartPasteEntryMerge';
import { normalizeEquipmentInventoryEntry } from '../../utils/EquipmentState';
import {
  assessCharacterCombatReadiness,
  classifyCombatReadiness
} from '../../utils/CharacterCombatReadiness';
import { projectSmartPasteCharacterRecords } from '../../utils/SmartPasteCharacterImport';
import { DND_BEYOND_SOURCE_FORMAT } from '../../utils/DndBeyondCharacterAdapter';
import { buildCombatActionCatalog, buildCombatActionEconomy } from '../../utils/combat/CombatActionCatalog';
import {
  getRememberedConflictPreference,
  getRememberedConflictResolution as getStoredConflictResolution,
  loadConflictPreferenceStore,
  recordConflictResolutions,
  saveConflictPreferenceStore
} from '../../utils/SmartPasteConflictMemory';
import {
  getRememberedReviewCategories,
  loadReviewDecisionStore,
  recordReviewDecisions,
  saveReviewDecisionStore
} from '../../utils/SmartPasteReviewMemory';
import {
  getRememberedAssociation,
  loadAssociationStore,
  recordAssociations,
  saveAssociationStore
} from '../../utils/SmartPasteAssociationMemory';
import contentUsageNotice from '../../config/contentUsageNotice';
import './SmartPasteInterface.css';
import '../../styles/governance-report.css';

// date_of_change: 2026-05-03
// rule_reference: D&D Character Data Governance Standards - Smart Paste pre-processing
import { CharacterGovernanceValidator, AIValidatorStub } from '../../utils/CharacterGovernanceValidator';
import GovernanceReportPanel from './GovernanceReportPanel';

const getEquipmentEntriesFromData = (data) => {
  const equipment = data?.equipment || {};
  return [
    ...(Array.isArray(equipment?.weapons)
      ? equipment.weapons.map((entry) => (
          entry && typeof entry === 'object'
            ? { ...entry, type: entry.type || 'weapon' }
            : entry
        ))
      : []),
    ...(Array.isArray(equipment?.armor) ? equipment.armor : []),
    ...(Array.isArray(equipment?.items) ? equipment.items : []),
    ...(Array.isArray(equipment?.valuables) ? equipment.valuables : [])
  ].filter((entry) => entry && typeof entry === 'object' && entry.name);
};

const buildAssociationParentFingerprint = (entry) => (
  entry && typeof entry === 'object'
    ? IntelligentContentParser.buildParsedEntryFingerprint(
        entry,
        entry?.sourceSection || entry?.parserMeta?.sourceSection || entry?.type || 'equipment'
      )
    : ''
);

const getAssociationCandidatesFromData = (data, associationKind) => {
  const equipmentEntries = getEquipmentEntriesFromData(data).map((entry) => {
    const sourceSection = entry?.sourceSection || entry?.parserMeta?.sourceSection || 'equipment';
    return {
      ...entry,
      sourceSection,
      fingerprint: buildAssociationParentFingerprint({ ...entry, sourceSection })
    };
  });

  return associationKind === 'weapon_rider'
    ? equipmentEntries.filter((entry) => entry?.type === 'weapon')
    : equipmentEntries.filter((entry) => entry?.type !== 'weapon');
};

const scoreAssociationCandidate = (rider, candidate, associationKind, rememberedAssociation = null) => {
  if (!candidate?.name) return -1;

  let score = 0;
  const normalizedRiderName = IntelligentContentParser.normalizeParsedEntryName(rider?.name || '');
  const normalizedCandidateName = IntelligentContentParser.normalizeParsedEntryName(candidate?.name || '');
  const riderTokens = normalizedRiderName.split(' ').filter((token) => token.length >= 4);
  const candidateTokens = new Set(normalizedCandidateName.split(' ').filter((token) => token.length >= 4));
  const riderLine = Number.isInteger(rider?.parserMeta?.lineStart) ? rider.parserMeta.lineStart : null;
  const candidateLine = Number.isInteger(candidate?.parserMeta?.lineStart) ? candidate.parserMeta.lineStart : null;
  const sameSection = IntelligentContentParser.normalizeParsedEntryName(
    rider?.parserMeta?.sourceSection || ''
  ) === IntelligentContentParser.normalizeParsedEntryName(
    candidate?.parserMeta?.sourceSection || candidate?.sourceSection || ''
  );

  if (rememberedAssociation?.parentFingerprint && candidate.fingerprint === rememberedAssociation.parentFingerprint) {
    score += 200;
  } else if (
    rememberedAssociation?.parentName &&
    IntelligentContentParser.normalizeParsedEntryName(rememberedAssociation.parentName) === normalizedCandidateName
  ) {
    score += 170;
  }

  if (rider?.suggestedParentFingerprint && candidate.fingerprint === rider.suggestedParentFingerprint) {
    score += 150;
  } else if (
    rider?.suggestedParent &&
    IntelligentContentParser.normalizeParsedEntryName(rider.suggestedParent) === normalizedCandidateName
  ) {
    score += 120;
  }

  if (sameSection) score += 20;
  if (associationKind === 'weapon_rider' && candidate?.type === 'weapon') score += 15;
  if (associationKind === 'equipment_rider' && candidate?.type !== 'weapon') score += 15;

  if (riderLine !== null && candidateLine !== null && candidateLine <= riderLine) {
    score += Math.max(0, 40 - (riderLine - candidateLine));
  }

  riderTokens.forEach((token) => {
    if (candidateTokens.has(token)) score += 10;
  });

  return score;
};

const rankAssociationCandidates = (rider, candidates, associationKind, rememberedAssociation = null) => (
  (Array.isArray(candidates) ? candidates : [])
    .map((candidate) => ({
      ...candidate,
      associationScore: scoreAssociationCandidate(rider, candidate, associationKind, rememberedAssociation)
    }))
    .sort((left, right) => {
      if (right.associationScore !== left.associationScore) {
        return right.associationScore - left.associationScore;
      }
      const leftLine = Number.isInteger(left?.parserMeta?.lineStart) ? left.parserMeta.lineStart : Number.MAX_SAFE_INTEGER;
      const rightLine = Number.isInteger(right?.parserMeta?.lineStart) ? right.parserMeta.lineStart : Number.MAX_SAFE_INTEGER;
      if (leftLine !== rightLine) return leftLine - rightLine;
      return String(left?.name || '').localeCompare(String(right?.name || ''));
    })
);

const conflictAwareFieldSet = new Set([
  'features',
  'classFeatures',
  'feats',
  'speciesTraits',
  'actions',
  'bonusActions',
  'reactions',
  'equipment'
]);

// @date_of_change: 2026-03-19
// rule_reference: SmartPaste exposes full-sheet replacement and Unified Character save as standalone import plans.
// They are intentionally exclusive with section-level checklist imports so the UI matches the apply semantics.
const standaloneImportFieldSet = new Set(['all', 'unifiedEntity']);
const isStandaloneImportSuggestion = (suggestion) => standaloneImportFieldSet.has(suggestion?.field);
const getStandaloneImportDescription = (suggestion) => {
  if (suggestion?.field === 'all') {
    return 'Replace the current character sheet with all of the pasted details. Use this instead of choosing individual sections.';
  }
  if (suggestion?.field === 'unifiedEntity') {
    return 'Save the pasted character as a new character in your library without changing this sheet.';
  }
  return '';
};

const normalizeImportField = (field) => (
  field === 'traits' ? 'speciesTraits' : field
);

const normalizeCharacterReviewField = (field) => (
  normalizeImportField(field) === 'race' ? 'species' : normalizeImportField(field)
);

const SMART_PASTE_SKIP_REASSIGNMENT = '__smartpaste_skip__';
const SMART_PASTE_APPLY_OPTIONS = Symbol.for('vcs.smartPaste.applyOptions');

const characterFieldLabelMap = {
  name: 'Name',
  class: 'Class',
  classes: 'Classes',
  all: 'Complete Character',
  subclass: 'Subclass',
  level: 'Level',
  experience: 'XP',
  race: 'Species',
  species: 'Species',
  background: 'Background',
  alignment: 'Alignment',
  hp: 'Hit Points',
  ac: 'Armor Class',
  speed: 'Speed',
  initiative: 'Initiative',
  proficiencyBonus: 'Proficiency Bonus',
  passivePerception: 'Passive Perception',
  abilities: 'Ability Scores',
  equipment: 'Equipment',
  currency: 'Coins',
  features: 'Features',
  classFeatures: 'Class Features',
  feats: 'Feats',
  speciesTraits: 'Species Traits',
  actions: 'Actions',
  bonusActions: 'Bonus Actions',
  reactions: 'Reactions',
  spells: 'Spells',
  spellcasting: 'Spellcasting',
  notes: 'Notes',
  personality: 'Personality',
  ideals: 'Ideals',
  bonds: 'Bonds',
  flaws: 'Flaws',
  backstory: 'Backstory',
  appearance: 'Appearance',
  toolProficiencies: 'Tool Proficiencies',
  proficiencies: 'Proficiencies',
  savingThrows: 'Saving Throws',
  skills: 'Skills'
};

const characterPrimaryGroups = [
  {
    key: 'identity',
    title: 'Identity',
    description: 'Confirm the basics before the sheet is updated.',
    fields: ['name', 'class', 'classes', 'subclass', 'level', 'experience', 'race', 'species', 'background', 'alignment']
  },
  {
    key: 'combat',
    title: 'Combat Stats',
    description: 'Core numbers for adventuring and combat.',
    fields: ['hp', 'ac', 'speed', 'initiative', 'proficiencyBonus', 'passivePerception', 'savingThrows', 'skills', 'spellcasting']
  },
  {
    key: 'abilities',
    title: 'Ability Scores',
    description: 'Review the six core stats as one block.',
    fields: ['abilities']
  },
  {
    key: 'equipment',
    title: 'Equipment',
    description: 'Equipped weapons, armor, general inventory gear, and coins.',
    fields: ['equipment', 'currency']
  },
  {
    key: 'extras',
    title: 'Optional Extras',
    description: 'Features, actions, spells, and any leftover notes.',
    fields: [
      'features',
      'classFeatures',
      'feats',
      'speciesTraits',
      'actions',
      'bonusActions',
      'reactions',
      'spells',
      'notes',
      'personality',
      'ideals',
      'bonds',
      'flaws',
      'backstory',
      'appearance',
      'toolProficiencies',
      'proficiencies'
    ]
  }
];

const characterFieldGroupMap = characterPrimaryGroups.reduce((accumulator, group) => {
  group.fields.forEach((field) => {
    accumulator[field] = group.key;
  });
  return accumulator;
}, {});

const editableScalarFieldSet = new Set([
  'name',
  'class',
  'subclass',
  'level',
  'experience',
  'race',
  'species',
  'background',
  'alignment',
  'hp',
  'ac',
  'speed',
  'initiative',
  'proficiencyBonus',
  'passivePerception'
]);

const numericCharacterFieldSet = new Set([
  'level',
  'experience',
  'hp',
  'ac',
  'initiative',
  'proficiencyBonus',
  'passivePerception'
]);

const primaryReviewDestinationOptions = [
  { value: '', label: 'Skip for now' },
  { value: 'abilities', label: 'Ability Scores' },
  { value: 'savingThrows', label: 'Saving Throws' },
  { value: 'skills', label: 'Skills' },
  { value: 'spells', label: 'Spells' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'classFeatures', label: 'Class Features' },
  { value: 'features', label: 'Features' },
  { value: 'feats', label: 'Feats' },
  { value: 'speciesTraits', label: 'Species Traits' },
  { value: 'actions', label: 'Actions' },
  { value: 'bonusActions', label: 'Bonus Actions' },
  { value: 'reactions', label: 'Reactions' },
  { value: 'notes', label: 'Notes' }
];

const formatCharacterFieldLabel = (field) => characterFieldLabelMap[normalizeImportField(field)] || field;

const formatRecentChange = (change) => {
  const field = normalizeImportField(change?.field || 'details');
  if (field === 'all') return 'Updated the character sheet';
  if (field === 'unifiedEntity') return 'Saved a new character to the library';

  const fieldLabel = formatCharacterFieldLabel(field);
  if (change?.action === 'append') return `Added ${fieldLabel}`;
  if (change?.action === 'remove') return `Removed ${fieldLabel}`;
  return `Updated ${fieldLabel}`;
};

const combatActionEconomyFieldSet = new Set([
  'actions',
  'bonusActions',
  'reactions',
  'features',
  'classFeatures',
  'feats',
  'speciesTraits',
  'equipment',
  'spells',
  'spellcasting',
  'speed'
]);

const formatCountLabel = (count, singular, plural = `${singular}s`) => `${count} ${count === 1 ? singular : plural}`;

const formatMovementSummary = (speed) => {
  if (speed === null || speed === undefined || speed === '') return '';
  if (typeof speed === 'number') return `${speed} ft.`;
  if (typeof speed === 'string') return speed;
  if (typeof speed !== 'object') return String(speed);

  const speedEntries = Object.entries(speed).filter(([, value]) => value !== null && value !== undefined && value !== '');
  if (!speedEntries.length) return '';

  return speedEntries.map(([mode, value]) => `${mode} ${value} ft.`).join(', ');
};

const formatCombatRangeSummary = (range) => {
  if (!range) return '';
  if (typeof range === 'number') return `${range} ft.`;
  if (typeof range === 'string') return range;
  if (typeof range !== 'object') return String(range);

  const normal = range.normal ?? range.max ?? range.value ?? null;
  const long = range.long ?? range.maximum ?? null;
  if (normal !== null && long !== null) {
    return `${normal}/${long} ft.`;
  }
  if (normal !== null) {
    return `${normal} ft.`;
  }
  return '';
};

const formatCombatEntryTypeLabel = (type) => ({
  spell: 'Spell',
  weapon: 'Weapon',
  attack: 'Attack',
  action: 'Action',
  reaction: 'Reaction',
  bonus: 'Bonus',
  move: 'Movement',
  free: 'Free',
  object: 'Object',
  legendary: 'Legendary'
}[String(type || '').toLowerCase()] || String(type || 'Action').replace(/^./, (char) => char.toUpperCase()));

const normalizeReviewCategories = (categories) => Array.from(new Set(
  (Array.isArray(categories) ? categories : [])
    .map((category) => normalizeImportField(category))
    .filter(Boolean)
));

const isContentUseAcknowledged = (pastedValue, confirmedValue) => {
  const normalizedPastedValue = String(pastedValue || '').trim();
  const normalizedConfirmedValue = String(confirmedValue || '').trim();
  return normalizedPastedValue.length > 0 && normalizedPastedValue === normalizedConfirmedValue;
};

const getKnowledgeSourceFromValue = (value) => String(
  value?.parserMeta?.knowledgeSource ||
  value?.proficienciesMeta?.knowledgeSource ||
  value?.data?.parserMeta?.knowledgeSource ||
  ''
).toLowerCase();

const collectKnowledgeSourcesFromValue = (value) => {
  const sources = new Set();
  const seen = new WeakSet();

  const visit = (candidate) => {
    if (!candidate || typeof candidate !== 'object') return;

    const directSource = getKnowledgeSourceFromValue(candidate);
    if (directSource) {
      sources.add(directSource);
    }

    if (seen.has(candidate)) return;
    seen.add(candidate);

    if (Array.isArray(candidate)) {
      candidate.forEach(visit);
      return;
    }

    Object.values(candidate).forEach(visit);
  };

  visit(value);
  return Array.from(sources);
};

const doesKnowledgeSourceRequireConfirmation = (knowledgeSource) => (
  ['inferred', 'mixed'].includes(String(knowledgeSource || '').toLowerCase())
);

const getSuggestionKnowledgeSources = (suggestion) => {
  if (!suggestion) return [];
  return collectKnowledgeSourcesFromValue(suggestion);
};

const suggestionRequiresKnowledgeConfirmation = (suggestion) => (
  getSuggestionKnowledgeSources(suggestion).some((source) => doesKnowledgeSourceRequireConfirmation(source))
);

const SmartPasteInterface = ({
  onContentParsed,
  onApplyChanges,
  context = {},
  disabled = false,
  placeholder = "Paste a character sheet, stat block, spell list, or exported JSON here..."
}) => {
  const [pastedText, setPastedText] = useState('');
  const [parsedContent, setParsedContent] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  // date_of_change: 2026-05-03
  // rule_reference: Governance validation report for Smart Paste quality assurance
  const [governanceReport, setGovernanceReport] = useState(null);
  const [, setSelectedSuggestion] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [_selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [selectedSuggestionIndexes, setSelectedSuggestionIndexes] = useState([]);
  const [appliedSuggestionIndexes, setAppliedSuggestionIndexes] = useState([]);

  const [isProcessing, setIsProcessing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [showExamples, setShowExamples] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showWhatWeFound, setShowWhatWeFound] = useState(true);
  const [characterAdvancedPanels, setCharacterAdvancedPanels] = useState({
    reviewTools: false,
    simulator: false,
    previewHelp: false
  });
  const [uncategorizedDecisions, setUncategorizedDecisions] = useState({});
  // date_of_change: 2026-02-27
  // rule_reference: SmartPaste association prompts must actually store which parent item a rider attaches to.
  // Shape: { [riderKey]: { status: 'pending'|'applied'|'skipped', parentName?: string, mode?: 'attach'|'standalone' } }
  const [riderAssociations, setRiderAssociations] = useState({});
  const [expandedReviewItems, setExpandedReviewItems] = useState({});
  const [splitItems, setSplitItems] = useState([]);
  const [extrasModel, setExtrasModel] = useState(null);
  const [rememberedReviewSelections, setRememberedReviewSelections] = useState({});
  const [confirmedReviewItems, setConfirmedReviewItems] = useState({});
  // date_of_change: 2026-02-26
  // Tracks per-item reassignment within array suggestions (features, feats, speciesTraits)
  // Shape: { [suggestionIndex]: { [itemIndex]: newField } }
  const [suggestionReassignments, setSuggestionReassignments] = useState({});
  const [suggestionFieldOverrides, setSuggestionFieldOverrides] = useState({});
  const [suggestionDataOverrides, setSuggestionDataOverrides] = useState({});
  const [confirmedSuggestionIndexes, setConfirmedSuggestionIndexes] = useState({});
  const [suggestionConflictResolutions, setSuggestionConflictResolutions] = useState({});
  const [suggestionConflictTargets, setSuggestionConflictTargets] = useState({});
  const [reviewConflictResolutions, setReviewConflictResolutions] = useState({});
  const [reviewConflictTargets, setReviewConflictTargets] = useState({});
  const [rememberedConflictPreferences, setRememberedConflictPreferences] = useState({});
  const [rememberedReviewPreferences, setRememberedReviewPreferences] = useState({});
  const [rememberedAssociationPreferences, setRememberedAssociationPreferences] = useState({});
  // @date_of_change 2025-12-09 - Track multi-select merge state for extras review items
  const [reviewMergeSelection, setReviewMergeSelection] = useState({});
  const [reviewFilterMode, setReviewFilterMode] = useState('all');
  const [reviewBulkCategory, setReviewBulkCategory] = useState('features');
  const [reviewBulkConflictResolution, setReviewBulkConflictResolution] = useState('merge');
  const [contentUseConfirmedText, setContentUseConfirmedText] = useState('');

  const textareaRef = useRef(null);
  const dropZoneRef = useRef(null);
  const parseContentRef = useRef(null);
  const hasCollapsedCharacterSourceRef = useRef(false);
  const [isSourceExpanded, setIsSourceExpanded] = useState(true);
  const conflictPreferenceCharacterId = String(context?.characterId || 'unsaved-character');
  const normalizedPastedText = String(pastedText || '').trim();
  const hasConfirmedContentUse = isContentUseAcknowledged(pastedText, contentUseConfirmedText);
  const contentUseNeedsRefresh = Boolean(contentUseConfirmedText) && normalizedPastedText.length > 0 && !hasConfirmedContentUse;

  const exampleFormats = [
    {
      label: 'Character Stat Block',
      icon: '👤',
      example: `Name: Aragorn
Classes: Ranger 10 / Fighter 5
Race: Human
Ability Scores: STR 18 DEX 16 CON 14 INT 12 WIS 14 CHA 16
AC: 17 (studded leather + shield)
HP: 95
Speed: 30 ft.
Saving Throws: STR +8, DEX +7
Skills: Athletics, Survival, Perception`
    },
    {
      label: 'Spell',
      icon: '✨',
      example: `Fireball. 3rd level evocation.
Casting time: 1 action
Range: 150 feet
Components: V, S, M (a tiny ball of bat guano)
Duration: Instantaneous
A bright streak flashes and explodes into flame.`
    },
    {
      label: 'Monster Action',
      icon: '⚔️',
      example: `Multiattack. The dragon makes three attacks: one with its bite and two with its claws.

Bite. Melee Weapon Attack: +14 to hit, reach 10 ft., one target. Hit: 19 (2d10 + 8) piercing damage.`
    },
    {
      label: 'Equipment',
      icon: '🎒',
      example: `Flame Tongue Longsword. Weapon (longsword), rare (requires attunement)
While attuned, you can use a bonus action to speak the command word, causing flames to erupt. +2d6 fire damage on hit.`
    }
  ];

  const parseContent = useCallback(async (text) => {
    setIsProcessing(true);
    setError(null);

    try {
      let result = IntelligentContentParser.parseContent(text, context);

      // date_of_change: 2026-05-03
      // rule_reference: D&D Character Data Governance Standards - Pre-process to clean AI-generated data
      // Run governance validation to normalize equipment, filter disqualified actions, coerce types
      // Note: Only run if we have complete character data (skip in tests with partial mocks)
      if (result.type === 'character' && result.data && result.data.name) {
        const validator = new CharacterGovernanceValidator({
          maxRetries: 2,
          aiValidator: new AIValidatorStub() // AI not implemented yet per requirements
        });
        
        try {
          const validationResult = validator.validate(result.data, {
            source: 'smart_paste',
            entityType: context.entityType || 'character'
          });

          result = {
            ...result,
            data: validationResult.finalData
          };
          
          const report = validator.generateReport(validationResult);
          setGovernanceReport(report);
          
          if (validationResult.corrections.length > 0) {
            console.log('[SmartPaste] Governance corrections applied:', 
              validationResult.corrections.map(c => `${c.path} (${c.confidence})`).join(', '));
          }
        } catch (err) {
          console.error('[SmartPaste] Governance validation error:', err);
        }
      }

      if (result.type === 'unknown' || result.confidence < 0.3) {
        setError(
          String(context?.entityType || '').toLowerCase() === 'character'
            ? 'I could not find enough character details in that paste. Try including a name, class, level, or ability scores.'
            : 'I could not find enough game details in that paste. Add a name and a few key stats, then try again.'
        );
        setParsedContent(null);
        setExtrasModel(null);
        setRiderAssociations({});
        setRememberedReviewSelections({});
        setConfirmedReviewItems({});
        setReviewFilterMode('all');
        setReviewBulkCategory('features');
        setReviewBulkConflictResolution('merge');
      } else {
        const extras = SmartPasteAggregator.buildExtrasModel(result);
        setExtrasModel(extras);
        setConfirmedSuggestionIndexes({});
        setSuggestionFieldOverrides({});
        setSuggestionDataOverrides({});
        setSuggestionConflictResolutions({});
        setSuggestionConflictTargets({});
        setReviewConflictResolutions({});
        setReviewConflictTargets({});
        setConfirmedReviewItems({});
        const availableWeaponParents = getAssociationCandidatesFromData(result?.data, 'weapon_rider');
        const availableEquipmentParents = getAssociationCandidatesFromData(result?.data, 'equipment_rider');
        const rememberedRiderAssociations = {};
        (result?.data?.weaponRiders || []).forEach((rider, idx) => {
          const rememberedAssociation = getRememberedAssociation(
            rememberedAssociationPreferences,
            'weapon_rider',
            rider,
            availableWeaponParents
          );
          if (!rememberedAssociation) return;
          rememberedRiderAssociations[`weapon_rider_${idx}`] = {
            status: 'remembered',
            parentName: rememberedAssociation.parentName || '',
            parentFingerprint: rememberedAssociation.parentFingerprint || '',
            mode: rememberedAssociation.mode || 'attach'
          };
        });
        (result?.data?.equipmentRiders || []).forEach((rider, idx) => {
          const rememberedAssociation = getRememberedAssociation(
            rememberedAssociationPreferences,
            'equipment_rider',
            rider,
            availableEquipmentParents
          );
          if (!rememberedAssociation) return;
          rememberedRiderAssociations[`equip_rider_${idx}`] = {
            status: 'remembered',
            parentName: rememberedAssociation.parentName || '',
            parentFingerprint: rememberedAssociation.parentFingerprint || '',
            mode: rememberedAssociation.mode || 'attach'
          };
        });
        setRiderAssociations(rememberedRiderAssociations);
        setAppliedSuggestionIndexes([]);

        // @date_of_change 2026-02-28
        // rule_reference: Default-select extras into their inferred categories so users can apply without extra clicks.
        if (extras && Array.isArray(extras.items)) {
          const seeded = {};
          const remembered = {};
          extras.items.forEach((item) => {
            const rememberedCats = normalizeReviewCategories(
              getRememberedReviewCategories(rememberedReviewPreferences, item)
            );
            const autoCats = rememberedCats.length > 0
              ? rememberedCats
              : deriveInitialCategoriesFromItem(item);
            if (autoCats.length) seeded[item.key] = autoCats;
            if (rememberedCats.length > 0) {
              remembered[item.key] = rememberedCats;
            }
          });
          setUncategorizedDecisions(seeded);
          setRememberedReviewSelections(remembered);
        } else {
          setRememberedReviewSelections({});
        }

        setParsedContent(result);
        setShowPreview(true);
        if (result.type === 'character' && !hasCollapsedCharacterSourceRef.current) {
          hasCollapsedCharacterSourceRef.current = true;
          setIsSourceExpanded(false);
        }
        setCharacterAdvancedPanels({
          reviewTools: false,
          simulator: false,
          previewHelp: false
        });

        const nextSuggestions = IntelligentContentParser.getPlacementSuggestions(result, context);
        setSuggestions(nextSuggestions);
        if (nextSuggestions.length > 0) {
          const initialIndexes = result.type === 'character'
            ? nextSuggestions
              .map((suggestion, suggestionIndex) => (
                !isStandaloneImportSuggestion(suggestion) && !suggestionRequiresKnowledgeConfirmation(suggestion)
                  ? suggestionIndex
                  : -1
              ))
              .filter((suggestionIndex) => suggestionIndex !== -1)
            : (() => {
              const firstReadySectionIndex = nextSuggestions.findIndex((suggestion) => (
                !isStandaloneImportSuggestion(suggestion) && !suggestionRequiresKnowledgeConfirmation(suggestion)
              ));
              const firstReadyPlanIndex = nextSuggestions.findIndex((suggestion) => (
                isStandaloneImportSuggestion(suggestion) && !suggestionRequiresKnowledgeConfirmation(suggestion)
              ));
              return firstReadySectionIndex !== -1
                ? [firstReadySectionIndex]
                : (firstReadyPlanIndex !== -1 ? [firstReadyPlanIndex] : []);
            })();
          setSelectedSuggestionIndexes(initialIndexes);
          setSelectedSuggestion(nextSuggestions[initialIndexes[0]] || null);
          setSelectedSuggestionIndex(initialIndexes[0] ?? -1);
        } else {
          setSelectedSuggestion(null);
          setSelectedSuggestionIndex(-1);
          setSelectedSuggestionIndexes([]);
        }

        if (onContentParsed) {
          onContentParsed(result, nextSuggestions);
        }
      }
    } catch (err) {
      console.error('[SmartPasteInterface] Parsing error:', err);
      setError(`Parsing failed: ${err.message}`);
      setParsedContent(null);
        setExtrasModel(null);
        setRiderAssociations({});
        setAppliedSuggestionIndexes([]);
        setConfirmedSuggestionIndexes({});
        setReviewConflictResolutions({});
        setReviewConflictTargets({});
        setRememberedReviewSelections({});
        setConfirmedReviewItems({});
        setReviewFilterMode('all');
      setReviewBulkCategory('features');
      setReviewBulkConflictResolution('merge');
      setCharacterAdvancedPanels({
        reviewTools: false,
        simulator: false,
        previewHelp: false
      });
    } finally {
      setIsProcessing(false);
    }
  }, [context, onContentParsed, rememberedAssociationPreferences, rememberedReviewPreferences]);

  useEffect(() => {
    parseContentRef.current = parseContent;
  }, [parseContent]);

  useEffect(() => {
    setRememberedConflictPreferences(loadConflictPreferenceStore(conflictPreferenceCharacterId));
    setRememberedReviewPreferences(loadReviewDecisionStore(conflictPreferenceCharacterId));
    setRememberedAssociationPreferences(loadAssociationStore(conflictPreferenceCharacterId));
  }, [conflictPreferenceCharacterId]);

  useEffect(() => {
    if (pastedText.trim()) {
      const debounceTimer = setTimeout(() => {
        parseContentRef.current?.(pastedText);
      }, 500);
      return () => clearTimeout(debounceTimer);
    } else {
      setParsedContent(null);
      setSuggestions([]);
      setSelectedSuggestionIndex(-1);
      setSelectedSuggestion(null);
      setSelectedSuggestionIndexes([]);
      setAppliedSuggestionIndexes([]);
      setConfirmedSuggestionIndexes({});
      setSuggestionFieldOverrides({});
      setSuggestionDataOverrides({});
      setShowPreview(false);
      setExtrasModel(null);
      setRiderAssociations({});
      setSuggestionConflictResolutions({});
      setSuggestionConflictTargets({});
      setReviewConflictResolutions({});
      setReviewConflictTargets({});
      setRememberedReviewSelections({});
      setConfirmedReviewItems({});
      setReviewFilterMode('all');
      setReviewBulkCategory('features');
      setReviewBulkConflictResolution('merge');
      setContentUseConfirmedText('');
      setCharacterAdvancedPanels({
        reviewTools: false,
        simulator: false,
        previewHelp: false
      });
    }
  }, [pastedText]);

  const handleTextChange = (e) => {
    setPastedText(e.target.value);
  };

  const handlePaste = (e) => {
    setTimeout(() => {
      const text = e.target.value;
      setPastedText(text);
    }, 10);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const textFile = files.find(f => f.type === 'text/plain' || f.name.endsWith('.txt') || f.name.endsWith('.md'));
    
    if (textFile) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setPastedText(event.target.result);
      };
      reader.onerror = () => {
        setError('Failed to read file. Please try copying and pasting the text directly.');
      };
      reader.readAsText(textFile);
    } else if (e.dataTransfer.getData('text')) {
      setPastedText(e.dataTransfer.getData('text'));
    } else {
      setError('Please drop a text file (.txt or .md) or paste text directly.');
    }
  };

  const applyExampleTextFromPanel = (exampleText) => {
    setPastedText(exampleText);
    setShowExamples(false);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleContentUseAcknowledgementChange = (event) => {
    if (!event.target.checked || !normalizedPastedText) {
      setContentUseConfirmedText('');
      return;
    }
    setContentUseConfirmedText(normalizedPastedText);
    setError(null);
  };

  const ensureContentUseAcknowledged = () => {
    if (hasConfirmedContentUse) return true;
    setError('Confirm that the pasted text is yours or otherwise permitted before applying it to VCS.');
    return false;
  };

  const getCurrentEntriesForImportField = (field) => {
    const normalizedField = normalizeImportField(field);
    const currentData = context?.currentData || {};

    switch (normalizedField) {
      case 'equipment': {
        const equipment = currentData?.equipment || {};
        return [
          ...(Array.isArray(equipment?.weapons) ? equipment.weapons : []),
          ...(Array.isArray(equipment?.armor) ? equipment.armor : []),
          ...(Array.isArray(equipment?.items) ? equipment.items : []),
          ...(Array.isArray(equipment?.valuables) ? equipment.valuables : []),
          ...(Array.isArray(currentData?.inventory?.equipment) ? currentData.inventory.equipment : [])
        ];
      }
      case 'actions':
        return Array.isArray(currentData?.actions?.actions)
          ? currentData.actions.actions
          : (Array.isArray(currentData?.actions) ? currentData.actions : []);
      case 'bonusActions':
        return Array.isArray(currentData?.bonusActions)
          ? currentData.bonusActions
          : (Array.isArray(currentData?.actions?.bonusActions) ? currentData.actions.bonusActions : []);
      case 'reactions':
        return Array.isArray(currentData?.reactions)
          ? currentData.reactions
          : (Array.isArray(currentData?.actions?.reactions) ? currentData.actions.reactions : []);
      default:
        return Array.isArray(currentData?.[normalizedField]) ? currentData[normalizedField] : [];
    }
  };

  const getImportItemConflicts = (item, field) => {
    const normalizedField = normalizeImportField(field);
    if (!item || typeof item !== 'object' || !conflictAwareFieldSet.has(normalizedField)) return [];
    return findSmartPasteConflictCandidates(
      getCurrentEntriesForImportField(normalizedField),
      item,
      normalizedField
    ).slice(0, 3);
  };

  const getDefaultConflictResolution = (conflicts) => {
    if (!Array.isArray(conflicts) || conflicts.length === 0) return 'merge';
    return conflicts.some(conflict => conflict.matchType === 'exact') ? 'merge' : 'keepBoth';
  };

  const getRememberedConflictResolutionForField = (item, field, conflicts) => (
    getStoredConflictResolution(
      rememberedConflictPreferences,
      normalizeImportField(field),
      item,
      conflicts
    )
  );

  const getRememberedConflictTargetForField = (item, field, conflicts) => (
    getRememberedConflictPreference(
      rememberedConflictPreferences,
      normalizeImportField(field),
      item,
      conflicts
    )?.targetFingerprint || null
  );

  const persistApplyPreferenceUpdates = ({
    conflictDecisionsToRemember = [],
    reviewDecisionsToRemember = [],
    associationDecisionsToRemember = []
  } = {}) => {
    if (conflictDecisionsToRemember.length > 0) {
      const nextConflictPreferences = recordConflictResolutions(rememberedConflictPreferences, conflictDecisionsToRemember);
      setRememberedConflictPreferences(nextConflictPreferences);
      saveConflictPreferenceStore(conflictPreferenceCharacterId, nextConflictPreferences);
    }
    if (reviewDecisionsToRemember.length > 0) {
      const nextReviewPreferences = recordReviewDecisions(rememberedReviewPreferences, reviewDecisionsToRemember);
      setRememberedReviewPreferences(nextReviewPreferences);
      saveReviewDecisionStore(conflictPreferenceCharacterId, nextReviewPreferences);
    }
    if (associationDecisionsToRemember.length > 0) {
      const nextAssociationPreferences = recordAssociations(rememberedAssociationPreferences, associationDecisionsToRemember);
      setRememberedAssociationPreferences(nextAssociationPreferences);
      saveAssociationStore(conflictPreferenceCharacterId, nextAssociationPreferences);
    }
  };

  const resetSmartPasteSession = () => {
    setPastedText('');
    setParsedContent(null);
    setSuggestions([]);
    setSelectedSuggestion(null);
    setSelectedSuggestionIndex(-1);
    setSelectedSuggestionIndexes([]);
    setAppliedSuggestionIndexes([]);
    setConfirmedSuggestionIndexes({});
    setShowPreview(false);
    setError(null);
    setUncategorizedDecisions({});
    setExpandedReviewItems({});
    setSplitItems([]);
    setExtrasModel(null);
    setRememberedReviewSelections({});
    setConfirmedReviewItems({});
    setReviewMergeSelection({});
    setReviewFilterMode('all');
    setReviewBulkCategory('features');
    setReviewBulkConflictResolution('merge');
    setSuggestionReassignments({});
    setSuggestionFieldOverrides({});
    setSuggestionDataOverrides({});
    setSuggestionConflictResolutions({});
    setSuggestionConflictTargets({});
    setReviewConflictResolutions({});
    setReviewConflictTargets({});
    setRiderAssociations({});
    setContentUseConfirmedText('');
    setIsSourceExpanded(true);
    hasCollapsedCharacterSourceRef.current = false;
    setCharacterAdvancedPanels({
      reviewTools: false,
      simulator: false,
      previewHelp: false
    });
  };

  const buildApplyPayload = ({
    includeSuggestions = true,
    includeReviewItems = true,
    reviewItemKeys = null,
    includeRiders = true,
    suggestionIndexes = selectedSuggestionIndexes
  } = {}) => {
    if (!parsedContent) {
      return {
        records: [],
        conflictDecisionsToRemember: [],
        reviewDecisionsToRemember: [],
        associationDecisionsToRemember: [],
        appliedReviewItemKeys: []
      };
    }

    const baseTimestamp = new Date().toISOString();
    const reassignableFields = new Set(['equipment', 'features', 'classFeatures', 'feats', 'speciesTraits', 'actions', 'bonusActions', 'reactions']);
    const conflictDecisionsToRemember = [];
    const reviewDecisionsToRemember = [];
    const associationDecisionsToRemember = [];
    const appliedReviewItemKeys = [];
    const contentUsageMeta = {
      acknowledged: true,
      confirmationSource: 'smart-paste-notice',
      confirmedAt: baseTimestamp
    };
    const reviewItemKeySet = reviewItemKeys instanceof Set
      ? reviewItemKeys
      : (Array.isArray(reviewItemKeys) ? new Set(reviewItemKeys) : null);
    const buildKnowledgeConfirmationMeta = (existingMeta) => IntelligentContentParser.mergeParserMeta(
      existingMeta,
      {
        knowledgeConfirmed: true,
        knowledgeConfirmationSource: 'session',
        knowledgeConfirmedAt: baseTimestamp
      }
    );
    const applySuggestionKnowledgeConfirmation = (value, suggestionIdx, seen = new WeakMap()) => {
      if (!confirmedSuggestionIndexes[suggestionIdx] || !value || typeof value !== 'object') return value;

      if (seen.has(value)) {
        return seen.get(value);
      }

      if (Array.isArray(value)) {
        const nextArray = [];
        seen.set(value, nextArray);
        let didChange = false;

        value.forEach((item, itemIdx) => {
          const nextItem = applySuggestionKnowledgeConfirmation(item, suggestionIdx, seen);
          nextArray[itemIdx] = nextItem;
          if (nextItem !== item) {
            didChange = true;
          }
        });

        if (!didChange) {
          seen.set(value, value);
          return value;
        }

        return nextArray;
      }

      const nextValue = { ...value };
      seen.set(value, nextValue);
      let didChange = false;

      if (
        value.parserMeta &&
        doesKnowledgeSourceRequireConfirmation(String(value.parserMeta.knowledgeSource || '').toLowerCase())
      ) {
        nextValue.parserMeta = buildKnowledgeConfirmationMeta(value.parserMeta);
        didChange = true;
      }

      if (
        value.proficienciesMeta &&
        doesKnowledgeSourceRequireConfirmation(String(value.proficienciesMeta.knowledgeSource || '').toLowerCase())
      ) {
        nextValue.proficienciesMeta = buildKnowledgeConfirmationMeta(value.proficienciesMeta);
        didChange = true;
      }

      Object.entries(value).forEach(([key, childValue]) => {
        if (key === 'parserMeta' || key === 'proficienciesMeta') return;
        const nextChild = applySuggestionKnowledgeConfirmation(childValue, suggestionIdx, seen);
        if (nextChild !== childValue) {
          nextValue[key] = nextChild;
          didChange = true;
        }
      });

      if (!didChange) {
        seen.set(value, value);
        return value;
      }

      return nextValue;
    };

    const applyConflictResolutionToItem = (item, targetField, explicitResolution = null, explicitTargetFingerprint = null) => {
      const normalizedField = normalizeImportField(targetField);
      if (!item || typeof item !== 'object' || !conflictAwareFieldSet.has(normalizedField)) return item;

      const conflicts = getImportItemConflicts(item, normalizedField);
      if (!conflicts.length) return item;

      const rememberedPreference = getRememberedConflictPreference(
        rememberedConflictPreferences,
        normalizedField,
        item,
        conflicts
      );
      const rememberedResolution = getRememberedConflictResolutionForField(item, normalizedField, conflicts);
      const resolution = explicitResolution || rememberedResolution || getDefaultConflictResolution(conflicts);
      const selectedTarget = (() => {
        if (explicitTargetFingerprint) {
          return conflicts.find(conflict => conflict.fingerprint === explicitTargetFingerprint) || conflicts[0];
        }
        if (rememberedPreference?.targetFingerprint) {
          return conflicts.find(conflict => conflict.fingerprint === rememberedPreference.targetFingerprint) || conflicts[0];
        }
        return conflicts[0];
      })();
      conflictDecisionsToRemember.push({
        field: normalizedField,
        item,
        conflicts,
        resolution,
        targetFingerprint: selectedTarget?.fingerprint || null,
        targetName: selectedTarget?.name || null
      });
      return {
        ...item,
        importMeta: {
          ...(buildSmartPasteImportMeta(item, normalizedField) || {}),
          conflictResolution: resolution,
          conflictTargetFingerprint: selectedTarget?.fingerprint || undefined,
          conflictTargetName: selectedTarget?.name || undefined
        }
      };
    };

    const records = [];
    const selectedSuggestionIndexSet = new Set(
      (Array.isArray(suggestionIndexes) ? suggestionIndexes : [])
        .filter((idx) => !appliedSuggestionIndexes.includes(idx))
    );

    if (includeSuggestions) {
      (suggestions || []).forEach((suggestion, idx) => {
        if (!selectedSuggestionIndexSet.has(idx) || !suggestion) return;
        const targetField = suggestionFieldOverrides[idx] || normalizeCharacterReviewField(suggestion.field);
        if (targetField === SMART_PASTE_SKIP_REASSIGNMENT) return;
        const suggestionData = Object.prototype.hasOwnProperty.call(suggestionDataOverrides, idx)
          ? suggestionDataOverrides[idx]
          : suggestion.data;
        const reassignMap = suggestionReassignments[idx];
        if (reassignMap && Array.isArray(suggestionData) && reassignableFields.has(suggestion.field)) {
          const buckets = {};
          suggestionData.forEach((item, itemIdx) => {
            const itemTargetField = reassignMap[itemIdx] || suggestion.field;
            if (itemTargetField === SMART_PASTE_SKIP_REASSIGNMENT) return;
            if (!buckets[itemTargetField]) buckets[itemTargetField] = [];
            buckets[itemTargetField].push(applyConflictResolutionToItem(
              applySuggestionKnowledgeConfirmation(item, idx),
              itemTargetField,
              suggestionConflictResolutions?.[idx]?.[itemIdx] || null,
              suggestionConflictTargets?.[idx]?.[itemIdx] || null
            ));
          });
          Object.entries(buckets).forEach(([field, items]) => {
            records.push({
              timestamp: baseTimestamp,
              action: suggestion.action,
              field,
              data: items,
              originalText: pastedText,
              parsedContent
            });
          });
        } else {
          const recordData = Array.isArray(suggestionData) && conflictAwareFieldSet.has(normalizeImportField(targetField))
            ? suggestionData.map((item, itemIdx) => applyConflictResolutionToItem(
              applySuggestionKnowledgeConfirmation(item, idx),
              targetField,
              suggestionConflictResolutions?.[idx]?.[itemIdx] || null,
              suggestionConflictTargets?.[idx]?.[itemIdx] || null
            ))
            : applySuggestionKnowledgeConfirmation(suggestionData, idx);
          records.push({
            timestamp: baseTimestamp,
            action: suggestion.action,
            field: targetField,
            data: recordData,
            originalText: pastedText,
            parsedContent
          });
        }
      });
    }

    if (includeRiders) {
      const attachRidersIntoEquipmentRecords = () => {
        const riderEntries = [];
        const weaponRiders = parsedContent?.data?.weaponRiders || [];
        const equipmentRiders = parsedContent?.data?.equipmentRiders || [];
        weaponRiders.forEach((r, idx) => riderEntries.push({ kind: 'weapon', idx, rider: r, key: `weapon_rider_${idx}` }));
        equipmentRiders.forEach((r, idx) => riderEntries.push({ kind: 'equipment', idx, rider: r, key: `equip_rider_${idx}` }));

        if (riderEntries.length === 0) return;

        const patchTargets = records
          .map((r, i) => ({ r, i }))
          .filter(({ r }) => (
            (r?.field === 'equipment' && r?.action === 'append' && Array.isArray(r?.data)) ||
            (r?.field === 'all' && r?.action === 'replace' && r?.data && typeof r?.data === 'object')
          ));

        if (patchTargets.length === 0) return;

        patchTargets.forEach(({ r, i }) => {
          const isAllImport = r.field === 'all';
          const nextData = isAllImport ? { ...r.data } : null;
          const nextItems = isAllImport
            ? (Array.isArray(nextData?.equipment?.items) ? nextData.equipment.items.map(it => (it && typeof it === 'object' ? { ...it } : it)) : [])
            : (Array.isArray(r.data) ? r.data.map(it => (it && typeof it === 'object' ? { ...it } : it)) : []);

          const appendStandalone = (riderLine) => {
            nextItems.push({ name: riderLine, type: 'item', description: riderLine });
          };

          riderEntries.forEach((entry) => {
            const assoc = riderAssociations[entry.key];
            if (!assoc || assoc.status !== 'applied') return;

            const riderLine = String(entry?.rider?.name || '').trim();
            if (!riderLine) return;

            if (assoc.mode === 'standalone') {
              appendStandalone(riderLine);
              return;
            }

            const parentName = assoc.parentName;
            const parentFingerprint = assoc.parentFingerprint || '';
            if (!parentName && !parentFingerprint) return;
            const parentIdx = nextItems.findIndex((it) => {
              if (!it || typeof it !== 'object') return false;
              if (parentFingerprint && buildAssociationParentFingerprint(it) === parentFingerprint) return true;
              return (it?.name || '') === parentName;
            });
            if (parentIdx === -1) return;
            const parent = nextItems[parentIdx];

            const existingDesc = typeof parent.description === 'string' ? parent.description : '';
            const appendedDesc = existingDesc
              ? (existingDesc + (existingDesc.endsWith('\n') ? '' : '\n') + riderLine)
              : riderLine;
            nextItems[parentIdx] = { ...parent, description: appendedDesc };
          });

          if (isAllImport) {
            nextData.equipment = { ...(nextData.equipment || {}), items: nextItems };
            records[i] = { ...r, data: nextData };
          } else {
            records[i] = { ...r, data: nextItems };
          }
        });
      };

      attachRidersIntoEquipmentRecords();

      (parsedContent?.data?.weaponRiders || []).forEach((rider, idx) => {
        const assoc = riderAssociations[`weapon_rider_${idx}`];
        if (!assoc || assoc.status !== 'applied') return;
        associationDecisionsToRemember.push({
          associationKind: 'weapon_rider',
          rider,
          mode: assoc.mode || 'attach',
          parentName: assoc.parentName || '',
          parentFingerprint: assoc.parentFingerprint || ''
        });
      });
      (parsedContent?.data?.equipmentRiders || []).forEach((rider, idx) => {
        const assoc = riderAssociations[`equip_rider_${idx}`];
        if (!assoc || assoc.status !== 'applied') return;
        associationDecisionsToRemember.push({
          associationKind: 'equipment_rider',
          rider,
          mode: assoc.mode || 'attach',
          parentName: assoc.parentName || '',
          parentFingerprint: assoc.parentFingerprint || ''
        });
      });
    }

    if (includeReviewItems) {
      const allUncategorizedItems = getAllUncategorizedItems();
      allUncategorizedItems.forEach((item) => {
        if (reviewItemKeySet && !reviewItemKeySet.has(item.key)) return;

        const decision = uncategorizedDecisions[item.key];
        const selectedCategories = normalizeReviewCategories(decision);
        if (selectedCategories.length === 0) return;
        const insight = buildReviewItemInsight(item);
        const applyableItem = insight?.knowledgeConfirmedInSession
          ? {
              ...item,
              parserMeta: {
                ...(item?.parserMeta || {}),
                knowledgeConfirmed: true,
                knowledgeConfirmationSource: 'session',
                knowledgeConfirmedAt: baseTimestamp
              }
            }
          : item;

        reviewDecisionsToRemember.push({
          item,
          categories: selectedCategories
        });
        appliedReviewItemKeys.push(item.key);

        selectedCategories.forEach((category) => {
          const parsedReviewItem = parseAsCategory(applyableItem, category);
          const reviewConflictResolution = reviewConflictResolutions?.[item.key]?.[category] || null;
          const reviewConflictTarget = reviewConflictTargets?.[item.key]?.[category] || null;
          records.push({
            timestamp: baseTimestamp,
            action: 'append',
            field: category,
            data: applyConflictResolutionToItem(
              parsedReviewItem,
              category,
              reviewConflictResolution,
              reviewConflictTarget
            ),
            originalText: `${item.name}: ${item.description}`,
            parsedContent: { type: category, data: item },
            fromUncategorized: true
          });
        });
      });
    }

    return {
      records: records.map((record) => ({
        ...record,
        contentUsageMeta
      })),
      conflictDecisionsToRemember,
      reviewDecisionsToRemember,
      associationDecisionsToRemember,
      appliedReviewItemKeys
    };
  };

  const resolveApplyOutcome = (outcome) => {
    if (outcome === false || outcome?.ok === false) {
      setError(outcome?.message || 'Those details could not be added to the character sheet. Your review is still here so you can try again.');
      return false;
    }
    return true;
  };

  const handleApplyError = (applyError) => {
    console.error('[SmartPasteInterface] Apply error:', applyError);
    setError(`Those details could not be added: ${applyError.message || 'something unexpected happened'}. Your review is still here.`);
    return false;
  };

  const runApplyCallback = (records, options = null) => {
    if (!onApplyChanges) return true;

    setIsApplying(true);
    try {
      const callbackPayload = records.length === 1 ? records[0] : records;
      if (options && callbackPayload && (typeof callbackPayload === 'object' || typeof callbackPayload === 'function')) {
        Object.defineProperty(callbackPayload, SMART_PASTE_APPLY_OPTIONS, {
          value: options,
          configurable: true
        });
      }
      const outcome = onApplyChanges(callbackPayload);
      if (outcome && typeof outcome.then === 'function') {
        return outcome
          .then(resolveApplyOutcome)
          .catch(handleApplyError)
          .finally(() => setIsApplying(false));
      }
      setIsApplying(false);
      return resolveApplyOutcome(outcome);
    } catch (applyError) {
      setIsApplying(false);
      return handleApplyError(applyError);
    }
  };

  const handleApplySelectedAndReady = () => {
    if (isApplying) return;
    if (!ensureContentUseAcknowledged()) {
      return;
    }

    const readyReviewItems = getReadyReviewItems();
    const readyReviewKeys = readyReviewItems.map((item) => item.key).filter(Boolean);
    const selectedStandalonePlanIndexes = selectedSuggestionIndexes.filter((idx) => (
      suggestions[idx] && isStandaloneImportSuggestion(suggestions[idx]) && !appliedSuggestionIndexes.includes(idx)
    ));
    const suggestionIndexesToApply = selectedSuggestionIndexes.filter((idx) => (
      suggestions[idx] &&
      !isStandaloneImportSuggestion(suggestions[idx]) &&
      !appliedSuggestionIndexes.includes(idx)
    ));

    if (!suggestionIndexesToApply.length && !readyReviewKeys.length) {
      if (selectedStandalonePlanIndexes.length > 0) {
        setError('Use Add to Character Sheet when replacing the whole sheet or saving a new character. Add Ready Details is only for the individual sections you selected.');
        return;
      }
      setError('Choose at least one detail to add, or finish an item that still needs your attention.');
      return;
    }

    const payload = buildApplyPayload({
      includeSuggestions: suggestionIndexesToApply.length > 0,
      includeReviewItems: readyReviewKeys.length > 0,
      reviewItemKeys: new Set(readyReviewKeys),
      includeRiders: false,
      suggestionIndexes: suggestionIndexesToApply
    });

    if (!payload.records.length) {
      setError('Choose at least one detail, or finish an item that still needs your attention.');
      return;
    }

    const commitStagedApply = (didApply) => {
      if (!didApply) return;
      setHistory(prev => [...prev, ...payload.records]);
      persistApplyPreferenceUpdates(payload);
      setError(null);
      commitAppliedReviewItems(payload);
      commitAppliedSuggestionIndexes(suggestionIndexesToApply);
    };
    const applyOutcome = runApplyCallback(payload.records, { closeModal: false, mode: 'staged' });
    if (applyOutcome && typeof applyOutcome.then === 'function') {
      return applyOutcome.then(commitStagedApply);
    }
    return commitStagedApply(applyOutcome);
  };

  const handleApplyVisibleReviewItems = (items) => {
    if (isApplying) return;
    if (!ensureContentUseAcknowledged()) {
      return;
    }

    const visibleKeys = Array.from(new Set((Array.isArray(items) ? items : []).map((item) => item?.key).filter(Boolean)));
    if (!visibleKeys.length) {
      setError('No visible review items are ready to apply.');
      return;
    }

    const payload = buildApplyPayload({
      includeSuggestions: false,
      includeReviewItems: true,
      reviewItemKeys: new Set(visibleKeys),
      includeRiders: false
    });

    if (!payload.records.length) {
      setError('Choose at least one category for the visible review items before applying.');
      return;
    }

    const commitVisibleApply = (didApply) => {
      if (!didApply) return;
      setHistory(prev => [...prev, ...payload.records]);
      persistApplyPreferenceUpdates(payload);
      setError(null);
      commitAppliedReviewItems(payload);
    };
    const applyOutcome = runApplyCallback(payload.records, { closeModal: false, mode: 'staged' });
    if (applyOutcome && typeof applyOutcome.then === 'function') {
      return applyOutcome.then(commitVisibleApply);
    }
    return commitVisibleApply(applyOutcome);
  };

  const handleApplyChanges = () => {
    if (isApplying) return;
    if (!ensureContentUseAcknowledged()) {
      return;
    }

    const payload = buildApplyPayload({
      includeSuggestions: true,
      includeReviewItems: true,
      includeRiders: true
    });

    const finishLine = buildSimulatorFinishLine();
    if (finishLine?.finalApplyBlocked && payload.records.some((record) => record?.field === 'all')) {
      setError(finishLine.finalApplyGuidance);
      return;
    }

    if (payload.records.some(record => standaloneImportFieldSet.has(record?.field)) && payload.records.length > 1) {
      setError('Replacing the whole sheet or saving a new character must be done by itself. Clear the other selections or finish them first.');
      return;
    }

    if (!payload.records.length) {
      setError('Choose at least one detail, or choose where an unplaced item belongs.');
      return;
    }

    const commitFinalApply = (didApply) => {
      if (!didApply) return;
      setHistory(prev => [...prev, ...payload.records]);
      persistApplyPreferenceUpdates(payload);
      resetSmartPasteSession();
    };
    const applyOutcome = runApplyCallback(payload.records);
    if (applyOutcome && typeof applyOutcome.then === 'function') {
      return applyOutcome.then(commitFinalApply);
    }
    return commitFinalApply(applyOutcome);
  };

  const handleClear = () => {
    setPastedText('');
    setParsedContent(null);
    setSuggestions([]);
    setSelectedSuggestionIndex(-1);
    setSelectedSuggestion(null);
    setSelectedSuggestionIndexes([]);
    setAppliedSuggestionIndexes([]);
    setConfirmedSuggestionIndexes({});
    setShowPreview(false);
    setSuggestionFieldOverrides({});
    setSuggestionDataOverrides({});
    setIsSourceExpanded(true);
    hasCollapsedCharacterSourceRef.current = false;
    setError(null);
    setExtrasModel(null);
    setRememberedReviewSelections({});
    setConfirmedReviewItems({});
    setReviewMergeSelection({});
    setReviewFilterMode('all');
    setReviewBulkCategory('features');
    setReviewBulkConflictResolution('merge');
    setSuggestionReassignments({});
    setSuggestionConflictResolutions({});
    setSuggestionConflictTargets({});
    setReviewConflictResolutions({});
    setReviewConflictTargets({});
    setRiderAssociations({});
    setCharacterAdvancedPanels({
      reviewTools: false,
      simulator: false,
      previewHelp: false
    });
  };

  const getConfidenceClass = (confidence) => {
    if (confidence >= 0.85) return 'confidence-excellent';
    if (confidence >= 0.7) return 'confidence-high';
    if (confidence >= 0.5) return 'confidence-medium';
    return 'confidence-low';
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 0.85) return 'Looks good';
    if (confidence >= 0.65) return 'Check a few details';
    return 'Needs your review';
  };

  const renderTypeIcon = (type) => {
    const icons = {
      spell: '✨',
      action: '⚔️',
      item: '🎒',
      feature: '🌟',
      character: '👤',
      monster: '🐲',
      ability: '💪',
      abilities: '💪',
      list: '📝'
    };
    return icons[type] || '❓';
  };

  const getConfidenceExplanation = (confidence) => {
    if (confidence >= 0.9) return 'SmartPaste found the key fields cleanly and this looks ready for a quick review.';
    if (confidence >= 0.8) return 'SmartPaste found most of the important details and may need your help with a few smaller decisions.';
    if (confidence >= 0.7) return 'This looks like a usable match, but check a few details before adding them to the sheet.';
    if (confidence >= 0.6) return 'SmartPaste found part of the pasted text and may need your help placing the rest.';
    if (confidence >= 0.5) return 'SmartPaste found only part of the pasted text. Check each detail before adding it.';
    return 'SmartPaste found some usable details, but the paste still needs a closer review before everything can be placed safely.';
  };

  const syncLegacySuggestionSelection = (nextIndexes) => {
    if (!nextIndexes || nextIndexes.length === 0) {
      setSelectedSuggestion(null);
      setSelectedSuggestionIndex(-1);
      return;
    }
    const first = nextIndexes[0];
    if (suggestions[first]) {
      setSelectedSuggestion(suggestions[first]);
      setSelectedSuggestionIndex(first);
    } else {
      setSelectedSuggestion(null);
      setSelectedSuggestionIndex(-1);
    }
  };

  const confirmSuggestionSelection = (indexes) => {
    const normalizedIndexes = Array.from(new Set(
      (Array.isArray(indexes) ? indexes : [indexes])
        .filter((idx) => Number.isInteger(idx) && suggestions[idx] && suggestionRequiresKnowledgeConfirmation(suggestions[idx]))
    ));
    if (!normalizedIndexes.length) return;

    setConfirmedSuggestionIndexes((prev) => {
      const next = { ...prev };
      normalizedIndexes.forEach((idx) => {
        next[idx] = true;
      });
      return next;
    });
  };

  const handleSuggestionToggle = (index) => {
    if (!suggestions || !suggestions[index] || appliedSuggestionIndexes.includes(index)) return;
    const suggestion = suggestions[index];
    const isSelected = selectedSuggestionIndexes.includes(index);

    const standaloneIndexes = suggestions
      .map((entry, suggestionIndex) => (isStandaloneImportSuggestion(entry) ? suggestionIndex : -1))
      .filter((suggestionIndex) => suggestionIndex !== -1);
    const isStandaloneImport = isStandaloneImportSuggestion(suggestion);

    if (isStandaloneImport) {
      if (!isSelected) {
        confirmSuggestionSelection(index);
      }
      setSelectedSuggestionIndexes((prev) => {
        const next = prev.includes(index) ? [] : [index];
        syncLegacySuggestionSelection(next);
        return next;
      });
      return;
    }

    if (!isSelected) {
      confirmSuggestionSelection(index);
    }
    setSelectedSuggestionIndexes((prev) => {
      const nextIsSelected = prev.includes(index);
      const next = nextIsSelected
        ? prev.filter(i => i !== index)
        : [...prev.filter(i => !standaloneIndexes.includes(i)), index];

      syncLegacySuggestionSelection(next);
      return next;
    });
  };

  const getSuggestionIndexesForField = (fieldKey) => {
    if (!fieldKey || !Array.isArray(suggestions)) return [];

    return suggestions
      .map((suggestion, suggestionIndex) => (suggestion?.field === fieldKey ? suggestionIndex : -1))
      .filter((suggestionIndex) => suggestionIndex !== -1 && !appliedSuggestionIndexes.includes(suggestionIndex));
  };

  const handleDetectedFieldClick = (fieldKey) => {
    if (!fieldKey || !suggestions || !suggestions.length) return;

    const targetIndexes = getSuggestionIndexesForField(fieldKey);
    if (!targetIndexes.length) return;
    const standaloneIndexes = suggestions
      .map((suggestion, suggestionIndex) => (isStandaloneImportSuggestion(suggestion) ? suggestionIndex : -1))
      .filter((suggestionIndex) => suggestionIndex !== -1);
    const allSelected = targetIndexes.every(idx => selectedSuggestionIndexes.includes(idx));

    if (!allSelected) {
      confirmSuggestionSelection(targetIndexes);
    }

    setSelectedSuggestionIndexes((prev) => {
      const nextAllSelected = targetIndexes.every(idx => prev.includes(idx));
      const next = nextAllSelected
        ? prev.filter(idx => !targetIndexes.includes(idx))
        : Array.from(new Set([...prev.filter(idx => !standaloneIndexes.includes(idx)), ...targetIndexes]));

      syncLegacySuggestionSelection(next);
      return next;
    });
  };

  const buildCombatActionEconomyPreview = () => {
    if (String(context?.entityType || '').toLowerCase() !== 'character' || !parsedContent?.data) {
      return null;
    }

    const previewCharacter = projectSmartPasteCharacterRecords(
      context?.currentData || {},
      {
        field: 'all',
        action: 'replace',
        data: parsedContent.data
      }
    );
    const previewEconomy = buildCombatActionEconomy(previewCharacter);
    const previewCatalog = buildCombatActionCatalog(previewCharacter);
    const previewCounts = {
      actions: Array.isArray(previewEconomy?.actions) ? previewEconomy.actions.length : 0,
      bonusActions: Array.isArray(previewEconomy?.bonusActions) ? previewEconomy.bonusActions.length : 0,
      reactions: Array.isArray(previewEconomy?.reactions) ? previewEconomy.reactions.length : 0
    };
    const movementSummary = formatMovementSummary(previewCharacter.speed);
    const sourceLabelMap = {
      actions: 'Actions',
      bonusActions: 'Bonus Actions',
      reactions: 'Reactions',
      features: 'Features',
      classFeatures: 'Class Features',
      feats: 'Feats',
      speciesTraits: 'Species Traits',
      equipment: 'Weapons & Equipment',
      spells: 'Spells',
      spellcasting: 'Spellcasting Stats',
      speed: 'Movement'
    };
    const sourceDescriptionMap = {
      actions: 'Explicit authored actions from the paste.',
      bonusActions: 'Explicit authored bonus actions from the paste.',
      reactions: 'Explicit authored reactions from the paste.',
      features: 'Feature entries that can create simulator combat options.',
      classFeatures: 'Class features that can add combat actions or resources.',
      feats: 'Feats that can add combat options or modify action timing.',
      speciesTraits: 'Species traits that can become combat actions or reactions.',
      equipment: 'Only explicitly equipped weapons become attacks; spare weapons and ammunition remain inventory.',
      spells: 'Spell entries that can become castable combat options.',
      spellcasting: 'Spell attack bonus, save DC, and casting ability support for spells.',
      speed: movementSummary ? `Movement speed: ${movementSummary}` : 'Movement speed detected from the paste.'
    };
    // @date_of_change: 2026-04-10
    // rule_reference: Smart Paste must surface combat-facing options using the same derived action economy the simulator consumes.
    // This preview stays read-only and routes user decisions back through the existing section suggestion checklist.
    const sourceEntries = Array.from(combatActionEconomyFieldSet)
      .map((field) => {
        const suggestionIndexes = getSuggestionIndexesForField(field);
        if (!suggestionIndexes.length) return null;

        const itemCount = suggestionIndexes.reduce((sum, suggestionIndex) => {
          const suggestionData = suggestions[suggestionIndex]?.data;
          if (Array.isArray(suggestionData)) {
            return sum + suggestionData.length;
          }
          return suggestionData !== null && suggestionData !== undefined ? sum + 1 : sum;
        }, 0);
        const selectedCount = suggestionIndexes.filter((suggestionIndex) => selectedSuggestionIndexes.includes(suggestionIndex)).length;

        return {
          field,
          label: sourceLabelMap[field] || formatCatalogKindCountLabel(field, itemCount || 1),
          description: sourceDescriptionMap[field] || '',
          itemCount,
          suggestionIndexes,
          isSelected: selectedCount > 0 && selectedCount === suggestionIndexes.length,
          isPartiallySelected: selectedCount > 0 && selectedCount < suggestionIndexes.length
        };
      })
      .filter(Boolean);

    if (!previewCatalog.length && !movementSummary && !sourceEntries.length) {
      return null;
    }

    return {
      counts: previewCounts,
      movementSummary,
      catalog: previewCatalog,
      sourceEntries
    };
  };

  const renderWhatWeFound = () => {
    if (!parsedContent || !parsedContent.data) return null;

    const { type } = parsedContent;
    const data = (type === 'character' || type === 'monster')
      ? IntelligentContentParser.deriveCharacterImportData(parsedContent.data)
      : parsedContent.data;
    const detectedFields = [];
    const addField = (label, value, icon, fieldKey) => {
      if (value === undefined || value === null || value === '') return;
      detectedFields.push({ label, value, icon, fieldKey });
    };

    if (type === 'character' || type === 'monster') {
      addField('Name', data.name, '', 'name');
      const classSummary = Array.isArray(data.classes) && data.classes.length > 0
        ? data.classes
          .map((entry) => `${entry.name || entry.class || 'Unknown Class'}${entry.subclass ? ` (${entry.subclass})` : ''} ${entry.level ?? ''}`.trim())
          .join(' / ')
        : data.class;
      addField(
        Array.isArray(data.classes) && data.classes.length > 1 ? 'Classes' : 'Class',
        classSummary,
        '',
        Array.isArray(data.classes) && data.classes.length > 1 ? 'classes' : 'class'
      );
      addField('Subclass', data.subclass, '', 'subclass');
      addField('Level', data.level, '', 'level');
      addField('Species', data.race || data.species, '', 'race');
      addField('Background', data.background, '', 'background');
      if (data.abilities) {
        const abilStr = Object.entries(data.abilities)
          .map(([k, v]) => `${k.slice(0, 3).toUpperCase()} ${v}`)
          .join(', ');
        addField('Ability Scores', abilStr, '', 'abilities');
      }
      if (data.ac) {
        const acVal = typeof data.ac === 'object' ? `${data.ac.value}${data.ac.notes ? ` (${data.ac.notes})` : ''}` : data.ac;
        addField('AC', acVal, '', 'ac');
      }
      if (data.maxHp || data.hp) {
        const hpVal = data.maxHp || (typeof data.hp === 'object' ? data.hp.average : data.hp);
        addField('HP', hpVal, '', 'hp');
      }
      if (data.speed) {
        const speedStr = typeof data.speed === 'object'
          ? Object.entries(data.speed).map(([k, v]) => `${k} ${v} ft.`).join(', ')
          : `${data.speed} ft.`;
        addField('Speed', speedStr, '', 'speed');
      }
      if (data.proficiencyBonus) addField('Proficiency Bonus', `+${data.proficiencyBonus}`, '', 'proficiencyBonus');
      if (data.savingThrows && Object.keys(data.savingThrows).length) {
        const stStr = Object.entries(data.savingThrows)
          .map(([k, v]) => `${k.slice(0, 3).toUpperCase()} +${v}`)
          .join(', ');
        addField('Saving Throws', stStr, '', 'savingThrows');
      }
      if (data.skills && data.skills.length) {
        addField('Skills', data.skills.slice(0, 5).join(', ') + (data.skills.length > 5 ? '...' : ''), '', 'skills');
      }
      if (data.spells && (data.spells.byLevel || data.spells.prepared)) {
        const spellCount = data.spells.prepared?.length ||
          Object.values(data.spells.byLevel || {}).flat().length || 0;
        addField('Spells', `${spellCount} spell(s) found`, '', 'spells');
      }
      if (data.spellcasting && (data.spellcasting.saveDC || data.spellcasting.saveDc)) {
        const dcVal = data.spellcasting.saveDC ?? data.spellcasting.saveDc;
        addField('Spell Save DC', dcVal, '', 'spellcasting');
      }
      if (data.equipment && data.equipment.items?.length) {
        addField('Equipment', `${data.equipment.items.length} item(s)`, '', 'equipment');
      }
      // date_of_change: 2026-02-25
      // rule_reference: Show weapon riders that need user association with a parent weapon.
      if (data.weaponRiders && data.weaponRiders.length) {
        addField('Weapon Properties', `${data.weaponRiders.length} need review`, '', null);
      }
      // date_of_change: 2026-02-27
      // rule_reference: Some items have effect lines that need association to a parent item (e.g., Root Boots).
      if (data.equipmentRiders && data.equipmentRiders.length) {
        addField('Item Effects', `${data.equipmentRiders.length} need review`, '', null);
      }
      if (data.proficiencies) {
        if (data.proficiencies.armor?.length) addField('Armor Training', data.proficiencies.armor.join(', '), '', 'proficiencies');
        if (data.proficiencies.weapons?.length) addField('Weapon Prof.', data.proficiencies.weapons.join(', '), '', 'proficiencies');
      }
      if (data.toolProficiencies?.length) addField('Tool Prof.', data.toolProficiencies.join(', '), '', 'toolProficiencies');
      if (data.classFeatures && data.classFeatures.length) {
        addField('Class Features', `${data.classFeatures.length} class feature(s)`, '', 'classFeatures');
      }
      if (data.features && data.features.length) {
        addField('Features', `${data.features.length} feature(s)`, '', 'features');
      }
      if (data.actions && data.actions.length) {
        addField('Actions', `${data.actions.length} action(s)`, '', 'actions');
      }
      // date_of_change: 2026-02-27
      // rule_reference: D&D 5e (2024) action economy — show parsed bonus actions and reactions in "What We Found"
      if (data.bonusActions && data.bonusActions.length) {
        addField('Bonus Actions', `${data.bonusActions.length} bonus action(s)`, '', 'bonusActions');
      }
      if (data.reactions && data.reactions.length) {
        addField('Reactions', `${data.reactions.length} reaction(s)`, '', 'reactions');
      }
      // date_of_change: 2026-02-27
      // rule_reference: D&D 5e (2024) — show structured feats and species traits arrays in "What We Found"
      if (data.feats && data.feats.length) {
        addField('Feats', `${data.feats.length} feat(s)`, '', 'feats');
      }
      if (data.speciesTraits && data.speciesTraits.length) {
        addField('Species Traits', `${data.speciesTraits.length} trait(s)`, '', 'speciesTraits');
      }
      if (data.alignment) addField('Alignment', data.alignment, '', 'alignment');
      if (data.languages && data.languages.length) {
        addField('Languages', data.languages.join(', '), '', 'languages');
      }
      if (data.resistances && data.resistances.length) {
        addField('Resistances', data.resistances.join(', '), '', 'resistances');
      }
      if (data.immunities && data.immunities.length) {
        addField('Immunities', data.immunities.join(', '), '', 'immunities');
      }
      if (data.conditionImmunities && data.conditionImmunities.length) {
        addField('Condition Immunities', data.conditionImmunities.join(', '), '', 'conditionImmunities');
      }
      if (data.cr) addField('Challenge Rating', data.cr, '', null);
      if (data.featuresList && data.featuresList.length && !(data.classFeatures && data.classFeatures.length)) {
        addField('Class Features (text)', `${data.featuresList.length} feature(s)`, '', 'classFeatures');
      }
      if (data.speciesTraitsList && data.speciesTraitsList.length && !(data.speciesTraits && data.speciesTraits.length)) {
        addField('Species Traits (text)', `${data.speciesTraitsList.length} trait(s)`, '', 'speciesTraitsText');
      }
      if (data.featsList && data.featsList.length && !(data.feats && data.feats.length)) {
        addField('Feats (text)', `${data.featsList.length} feat(s)`, '', 'feats');
      }
      if (data.biography && typeof data.biography === 'object') {
        const bio = data.biography;
        if (bio.personality) addField('Personality', bio.personality.length > 40 ? bio.personality.substring(0, 40) + '...' : bio.personality, '', 'personality');
        if (bio.ideals) addField('Ideals', bio.ideals.length > 40 ? bio.ideals.substring(0, 40) + '...' : bio.ideals, '', 'ideals');
        if (bio.bonds) addField('Bonds', bio.bonds.length > 40 ? bio.bonds.substring(0, 40) + '...' : bio.bonds, '', 'bonds');
        if (bio.flaws) addField('Flaws', bio.flaws.length > 40 ? bio.flaws.substring(0, 40) + '...' : bio.flaws, '', 'flaws');
        if (bio.backstory) addField('Backstory', bio.backstory.length > 40 ? bio.backstory.substring(0, 40) + '...' : bio.backstory, '', 'backstory');
        if (bio.appearance) addField('Appearance', bio.appearance.length > 40 ? bio.appearance.substring(0, 40) + '...' : bio.appearance, '', 'appearance');
        if (bio.notes) addField('Notes', bio.notes.length > 40 ? bio.notes.substring(0, 40) + '...' : bio.notes, '', 'notes');
        if (bio.age) addField('Age', bio.age, '', 'age');
        if (bio.height) addField('Height', bio.height, '', 'height');
        if (bio.weight) addField('Weight', bio.weight, '', 'weight');
        if (bio.eyes) addField('Eyes', bio.eyes, '', 'eyes');
        if (bio.hair) addField('Hair', bio.hair, '', 'hair');
        if (bio.skin) addField('Skin', bio.skin, '', 'skin');
      }
    } else if (type === 'spell') {
      addField('Name', data.name, '', 'name');
      if (data.level !== undefined) addField('Level', data.level === 0 ? 'Cantrip' : data.level, '', null);
      addField('School', data.school, '', null);
      addField('Casting Time', data.castingTime, '', null);
      addField('Range', data.range, '', null);
      addField('Duration', data.duration, '', null);
    } else if (type === 'action') {
      addField('Name', data.name, '', 'name');
      addField('Attack Type', data.attackType, '', null);
      addField('To Hit', data.attackBonus ? `+${data.attackBonus}` : null, '', null);
      if (data.attack?.damage) {
        const dmgStr = data.attack.damage.map(d => `${d.dice} ${d.type}`).join(', ');
        addField('Damage', dmgStr, '', null);
      }
    } else if (type === 'item') {
      addField('Name', data.name, '', 'name');
      addField('Type', data.type, '', null);
      addField('Rarity', data.rarity, '', null);
      if (data.attunement) addField('Attunement', 'Required', '', null);
    } else if (type === 'abilities') {
      Object.entries(data).forEach(([k, v]) => {
        addField(k.charAt(0).toUpperCase() + k.slice(1), v, '', 'abilities');
      });
    }

    if (detectedFields.length === 0) return null;

    return (
      <div className="what-we-found">
        <div className="what-we-found-header" onClick={() => setShowWhatWeFound(!showWhatWeFound)}>
          <h4>
            <span className="header-icon">🔍</span>
            What We Found
            <span className="field-count">{detectedFields.length} fields</span>
          </h4>
          <button className="toggle-btn" aria-label={showWhatWeFound ? 'Collapse' : 'Expand'}>
            {showWhatWeFound ? '▼' : '▶'}
          </button>
        </div>
        {showWhatWeFound && (
          <div className="detected-fields-grid">
            {detectedFields.map((field, idx) => {
              const isActive = Boolean(
                field.fieldKey &&
                suggestions &&
                suggestions.some((s, sIdx) => s.field === field.fieldKey && selectedSuggestionIndexes.includes(sIdx))
              );
              const clickable = Boolean(
                field.fieldKey
                && suggestions
                && suggestions.some((suggestion) => suggestion?.field === field.fieldKey)
              );
              const className = `detected-field${clickable ? ' clickable' : ''}${isActive ? ' selected' : ''}`;
              return (
                <div
                  key={idx}
                  className={className}
                  onClick={clickable ? () => handleDetectedFieldClick(field.fieldKey) : undefined}
                >
                  <span className="field-icon">{field.icon}</span>
                  <span className="field-label">{field.label}</span>
                  <span className="field-value" title={String(field.value)}>{String(field.value)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const toggleExpandReviewItem = (itemKey, item) => {
    setExpandedReviewItems(prev => ({ ...prev, [itemKey]: !prev[itemKey] }));

    // @date_of_change 2025-12-09 - When an item is first expanded, seed categories from aggregator tags
    if (item) {
      setUncategorizedDecisions(prev => {
        if (prev[itemKey] !== undefined) return prev;
        const autoCategories = normalizeReviewCategories(deriveInitialCategoriesFromItem(item));
        if (!autoCategories.length) return prev;
        return { ...prev, [itemKey]: autoCategories };
      });
    }
  };

  // @date_of_change 2025-12-09 - Derive initial category suggestions from aggregator tags
  const deriveInitialCategoriesFromItem = (item) => {
    if (!item) return [];

    const categories = new Set();
    const mapTagToCategory = (tag) => {
      switch (tag) {
        case 'abilities':
          return 'abilities';
        case 'savingThrows':
          return 'savingThrows';
        case 'skills':
          return 'skills';
        case 'spells':
          return 'spells';
        case 'equipment':
          return 'equipment';
        case 'features':
          return 'features';
        case 'classFeatures':
          return 'classFeatures';
        case 'actions':
          return 'actions';
        case 'bonusActions':
          return 'bonusActions';
        case 'reactions':
          return 'reactions';
        case 'traits':
          return 'speciesTraits';
        case 'speciesTraits':
          return 'speciesTraits';
        case 'notes':
          return 'notes';
        default:
          return null;
      }
    };

    if (item.suggestedType) {
      const mapped = mapTagToCategory(item.suggestedType);
      if (mapped) categories.add(mapped);
    }

    const tags = Array.isArray(item.tags) ? item.tags : [];
    tags.forEach((tag) => {
      const mapped = mapTagToCategory(tag);
      if (mapped) categories.add(mapped);
    });

    return normalizeReviewCategories(Array.from(categories));
  };

  const getReviewItemSourceText = (item) => [
    item?.parserMeta?.rawText,
    item?.description,
    item?.name
  ].filter(Boolean).join('\n');

  const isGenericReviewItemName = (value) => {
    const normalized = IntelligentContentParser.normalizeParsedEntryName(value || '');
    return !normalized || ['unplaced details', 'untitled block', 'notes leftovers', 'biography details'].includes(normalized);
  };

  const escapeReviewRegex = (value) => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const extractStructuredScalarFromText = (text, fieldNames = []) => {
    const sourceText = String(text || '');
    if (!sourceText.trim()) return '';

    for (const fieldName of fieldNames) {
      const escaped = escapeReviewRegex(fieldName);
      const quotedDouble = sourceText.match(new RegExp(`["']?${escaped}["']?\\s*:\\s*"([^"]+)"`, 'i'));
      if (quotedDouble?.[1]) return quotedDouble[1].trim();

      const quotedSingle = sourceText.match(new RegExp(`["']?${escaped}["']?\\s*:\\s*'([^']+)'`, 'i'));
      if (quotedSingle?.[1]) return quotedSingle[1].trim();

      const bare = sourceText.match(new RegExp(`["']?${escaped}["']?\\s*:\\s*([^,\\n\\r}]+)`, 'i'));
      if (bare?.[1]) {
        const candidate = bare[1].trim().replace(/^["']|["']$/g, '');
        if (candidate) return candidate;
      }
    }

    return '';
  };

  const extractStructuredBooleanFromText = (text, fieldNames = []) => {
    const scalar = extractStructuredScalarFromText(text, fieldNames).toLowerCase();
    if (!scalar) return null;
    if (scalar === 'true') return true;
    if (scalar === 'false') return false;
    return null;
  };

  const shortenReviewPreviewText = (value, maxLength = 180) => {
    const normalized = String(value || '').replace(/\s+/g, ' ').trim();
    if (!normalized) return '';
    return normalized.length > maxLength
      ? `${normalized.slice(0, maxLength - 1).trim()}...`
      : normalized;
  };

  const extractAbilityScoresFromReviewItem = (item) => {
    const text = getReviewItemSourceText(item);
    const abilityConfigs = [
      { key: 'strength', patterns: ['strength', 'str'] },
      { key: 'dexterity', patterns: ['dexterity', 'dex'] },
      { key: 'constitution', patterns: ['constitution', 'con'] },
      { key: 'intelligence', patterns: ['intelligence', 'int'] },
      { key: 'wisdom', patterns: ['wisdom', 'wis'] },
      { key: 'charisma', patterns: ['charisma', 'cha'] }
    ];
    const values = {};

    abilityConfigs.forEach(({ key, patterns }) => {
      for (const pattern of patterns) {
        const match = text.match(new RegExp(`\\b${pattern}\\b[^\\n\\r]{0,18}?\\b(\\d{1,2})\\b`, 'i'));
        if (match) {
          values[key] = parseInt(match[1], 10);
          break;
        }
      }
    });

    return Object.keys(values).length > 0 ? values : null;
  };

  const extractSavingThrowsFromReviewItem = (item) => {
    const text = getReviewItemSourceText(item);
    const abilityMap = {
      strength: 'strength',
      dexterity: 'dexterity',
      constitution: 'constitution',
      intelligence: 'intelligence',
      wisdom: 'wisdom',
      charisma: 'charisma',
      str: 'strength',
      dex: 'dexterity',
      con: 'constitution',
      int: 'intelligence',
      wis: 'wisdom',
      cha: 'charisma'
    };
    const values = {};
    const patterns = [
      /\b(Strength|Dexterity|Constitution|Intelligence|Wisdom|Charisma|STR|DEX|CON|INT|WIS|CHA)\b[^\n\r]{0,20}?saving throws?\s*([+-]\d+)/gi,
      /\b(Strength|Dexterity|Constitution|Intelligence|Wisdom|Charisma|STR|DEX|CON|INT|WIS|CHA)\b\s+saves?\s*([+-]\d+)/gi
    ];

    patterns.forEach((pattern) => {
      let match;
      while ((match = pattern.exec(text)) !== null) {
        const key = abilityMap[String(match[1] || '').toLowerCase()];
        const bonus = parseInt(match[2], 10);
        if (key && Number.isFinite(bonus)) {
          values[key] = bonus;
        }
      }
    });

    return Object.keys(values).length > 0 ? values : null;
  };

  const extractSkillsFromReviewItem = (item) => {
    const text = getReviewItemSourceText(item);
    const skillConfigs = [
      'Athletics',
      'Acrobatics',
      'Sleight of Hand',
      'Stealth',
      'Arcana',
      'History',
      'Investigation',
      'Nature',
      'Religion',
      'Animal Handling',
      'Insight',
      'Medicine',
      'Perception',
      'Survival',
      'Deception',
      'Intimidation',
      'Performance',
      'Persuasion'
    ];

    const values = [];
    skillConfigs.forEach((skill) => {
      const escaped = skill.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const match = text.match(new RegExp(`\\b${escaped}\\b(?:\\s+skill)?\\s*([+-]\\d+)`, 'i'));
      if (match) {
        values.push({
          name: skill,
          proficient: true,
          expertise: /expertise/i.test(match[0]),
          bonus: parseInt(match[1], 10)
        });
      }
    });

    return values.length > 0 ? values : null;
  };

  const extractSpellsFromReviewItem = (item) => {
    const sourceText = String(item?.parserMeta?.rawText || item?.description || '').trim();
    if (!sourceText) return null;
    const structuredName = extractStructuredScalarFromText(sourceText, ['name', 'title']);
    const structuredLevel = extractStructuredScalarFromText(sourceText, ['level', 'spellLevel']);
    const structuredSchool = extractStructuredScalarFromText(sourceText, ['school']);
    const structuredCastingTime = extractStructuredScalarFromText(sourceText, ['castingTime', 'casting time']);
    const structuredRange = extractStructuredScalarFromText(sourceText, ['range']);
    const structuredComponents = extractStructuredScalarFromText(sourceText, ['components']);
    const structuredDuration = extractStructuredScalarFromText(sourceText, ['duration']);
    const structuredDescription = extractStructuredScalarFromText(sourceText, ['description', 'text']);
    const structuredDamage = extractStructuredScalarFromText(sourceText, ['damage']);
    const structuredDamageType = extractStructuredScalarFromText(sourceText, ['damageType']);
    const structuredSaveType = extractStructuredScalarFromText(sourceText, ['saveType']);
    const structuredSource = extractStructuredScalarFromText(sourceText, ['source']);
    const structuredAttackType = extractStructuredScalarFromText(sourceText, ['attackType']);
    const structuredConcentration = extractStructuredBooleanFromText(sourceText, ['concentration']);
    const levelNumber = structuredLevel !== '' && Number.isFinite(Number(structuredLevel))
      ? parseInt(structuredLevel, 10)
      : null;

    if (
      (structuredName || !isGenericReviewItemName(item?.name)) &&
      (structuredSchool || structuredCastingTime || structuredDuration || structuredRange || /\bspell\b/i.test(sourceText))
    ) {
      const resolvedName = structuredName || item?.name || 'Imported spell';
      return [{
        name: resolvedName,
        ...(levelNumber !== null ? { level: levelNumber } : {}),
        ...(structuredSchool ? { school: structuredSchool } : {}),
        ...(structuredCastingTime ? { castingTime: structuredCastingTime } : {}),
        ...(structuredRange ? { range: structuredRange } : {}),
        ...(structuredComponents ? { components: structuredComponents } : {}),
        ...(structuredDuration ? { duration: structuredDuration } : {}),
        ...(structuredDescription ? { description: structuredDescription } : {}),
        ...(structuredDamage ? { damage: structuredDamage } : {}),
        ...(structuredDamageType ? { damageType: structuredDamageType } : {}),
        ...(structuredSaveType ? { saveType: structuredSaveType } : {}),
        ...(structuredAttackType ? { attackType: structuredAttackType } : {}),
        ...(structuredSource ? { source: structuredSource } : {}),
        ...(structuredConcentration !== null ? { concentration: structuredConcentration } : {}),
        importMeta: buildSmartPasteImportMeta(item, 'spells')
      }];
    }

    const cleanedText = sourceText
      .replace(/^[^:]+:\s*/i, '')
      .replace(/\b(cantrips|spells?)\s+known\b[:\s-]*/ig, '')
      .replace(/\bprepared\s+spells?\b[:\s-]*/ig, '')
      .trim();
    if (!cleanedText) return null;

    const spellNames = cleanedText
      .split(/[\n,;]+/)
      .map((entry) => entry.replace(/^[•\-*]\s*/, '').trim())
      .filter(Boolean)
      .map((entry) => entry.replace(/\.$/, '').trim())
      .filter(Boolean);

    if (!spellNames.length) return null;

    const isCantripList = /cantrips? known/i.test(String(item?.name || ''));
    return spellNames.map((name) => ({
      name,
      ...(isCantripList ? { level: 0 } : {}),
      importMeta: buildSmartPasteImportMeta(item, 'spells')
    }));
  };

  const extractEquipmentFromReviewItem = (item) => {
    const sourceText = getReviewItemSourceText(item);
    const extractedName = extractStructuredScalarFromText(sourceText, ['name', 'title']);
    const quantityValue = parseInt(extractStructuredScalarFromText(sourceText, ['quantity', 'qty']), 10);
    const typeValue = extractStructuredScalarFromText(sourceText, ['type']);
    const rarityValue = extractStructuredScalarFromText(sourceText, ['rarity'])
      || sourceText.match(/\b(very rare|legendary|uncommon|common|rare|artifact)\b/i)?.[1]
      || '';
    const attackBonusValue = parseInt(extractStructuredScalarFromText(sourceText, ['attackBonus', 'attack bonus']), 10);
    const damage = extractStructuredScalarFromText(sourceText, ['damage', 'damageFormula']);
    const damageType = extractStructuredScalarFromText(sourceText, ['damageType', 'damage type']);
    const armorClassValue = parseInt(extractStructuredScalarFromText(sourceText, ['armorClass', 'armor class']), 10);
    const range = extractStructuredScalarFromText(sourceText, ['range']);
    const properties = extractStructuredScalarFromText(sourceText, ['properties', 'property']);
    const description = extractStructuredScalarFromText(sourceText, ['description', 'text']) || item?.description || '';
    const structuredRequiresAttunement = extractStructuredBooleanFromText(
      sourceText,
      ['requiresAttunement', 'requires_attunement', 'requires attunement', 'attunement']
    );
    const requiresAttunement = structuredRequiresAttunement !== null
      ? structuredRequiresAttunement
      : /\brequires?\s+attunement\b/i.test(sourceText);
    const attuned = extractStructuredBooleanFromText(sourceText, ['attuned', 'isAttuned', 'is_attuned']);
    const equipped = extractStructuredBooleanFromText(sourceText, ['equipped', 'isEquipped', 'is_equipped']);
    const explicitMagic = extractStructuredBooleanFromText(sourceText, ['magical', 'isMagic', 'is_magic']);
    const weight = extractStructuredScalarFromText(sourceText, ['weight']);
    const inferredType = (() => {
      if (typeValue) return typeValue;
      if (/\bshield\b/i.test(sourceText)) return 'shield';
      if (Number.isFinite(armorClassValue) || /\b(light armor|medium armor|heavy armor)\b/i.test(sourceText)) return 'armor';
      if (damage || Number.isFinite(attackBonusValue) || /\b(weapon|dagger|sword|staff|crossbow|bow|trident|axe|mace)\b/i.test(sourceText)) return 'weapon';
      return 'item';
    })();
    const inferredMagic = explicitMagic !== null
      ? explicitMagic
      : Boolean(
          rarityValue
          || requiresAttunement
          || /\+\d|spell scroll|potion|wand|staff|rod|pearl of power/i.test(sourceText)
        );

    // date_of_change: 2026-07-29
    // rule_reference: Manually reviewed SmartPaste equipment must populate the same equipped,
    // rarity, description, and attunement fields as a full structured character import.
    return normalizeEquipmentInventoryEntry({
      name: extractedName || item?.name || 'Imported item',
      description,
      quantity: Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1,
      type: inferredType,
      rarity: rarityValue,
      magical: inferredMagic,
      ...(Number.isFinite(attackBonusValue) ? { attackBonus: attackBonusValue } : {}),
      ...(damage ? { damage } : {}),
      ...(damageType ? { damageType } : {}),
      ...(Number.isFinite(armorClassValue) ? { armorClass: armorClassValue } : {}),
      ...(range ? { range } : {}),
      ...(properties ? { properties: properties.split(/\s*[;,]\s*/).filter(Boolean) } : {}),
      ...(weight ? { weight } : {}),
      requiresAttunement,
      ...(attuned !== null ? { attuned } : {}),
      ...(equipped !== null ? { equipped } : {}),
      importMeta: buildSmartPasteImportMeta(item, 'equipment')
    });
  };

  const extractFeatureLikeEntryFromReviewItem = (item, defaultSource = 'Imported') => {
    const sourceText = getReviewItemSourceText(item);
    const extractedName = extractStructuredScalarFromText(sourceText, ['name', 'title']);
    const description = extractStructuredScalarFromText(sourceText, ['description', 'text']) || item?.description || '';
    const source = extractStructuredScalarFromText(sourceText, ['source']) || defaultSource;
    const usage = extractStructuredScalarFromText(sourceText, ['uses', 'maxUses', 'resetOn'])
      || sourceText.match(/(\d+\/(?:short|long)\s*rest|\d+\/day|at will|recharge \d+-?\d*)/i)?.[1]
      || null;

    return {
      name: extractedName || item?.name || 'Imported feature',
      description,
      source,
      usage,
      importMeta: buildSmartPasteImportMeta(item, defaultSource === 'Class' ? 'classFeatures' : 'features')
    };
  };

  const toggleCategorySelection = (itemKey, category) => {
    const normalizedCategory = normalizeImportField(category);
    setUncategorizedDecisions(prev => {
      const current = normalizeReviewCategories(prev[itemKey]);
      if (Array.isArray(current)) {
        return {
          ...prev,
          [itemKey]: current.includes(normalizedCategory)
            ? current.filter(c => c !== normalizedCategory)
            : [...current, normalizedCategory]
        };
      }
      return prev;
    });
    setKnowledgeConfirmationForReviewItems(itemKey, true);
  };

  const parseAsCategory = (item, category) => {
    const normalizedCategory = normalizeImportField(category);
    const text = `${item.name}: ${item.description}`;
    const importMeta = buildSmartPasteImportMeta(item, normalizedCategory);
    
    switch (normalizedCategory) {
      case 'abilities': {
        return extractAbilityScoresFromReviewItem(item) || {};
      }
      case 'savingThrows': {
        return extractSavingThrowsFromReviewItem(item) || {};
      }
      case 'skills': {
        return extractSkillsFromReviewItem(item) || [];
      }
      case 'spells': {
        return extractSpellsFromReviewItem(item) || [];
      }
      case 'equipment': {
        return extractEquipmentFromReviewItem(item);
      }
      case 'features': {
        return {
          ...extractFeatureLikeEntryFromReviewItem(item, 'Imported'),
          importMeta
        };
      }
      case 'classFeatures': {
        return {
          ...extractFeatureLikeEntryFromReviewItem(item, 'Class'),
          importMeta: buildSmartPasteImportMeta(item, 'classFeatures')
        };
      }
      case 'actions': {
        const attackMatch = text.match(/(\+\d+)\s*to\s*hit/i);
        const damageMatch = text.match(/(\d+d\d+(?:\s*\+\s*\d+)?)\s*(piercing|slashing|bludgeoning|fire|cold|lightning|thunder|acid|poison|necrotic|radiant|force|psychic)?/i);
        const reachMatch = text.match(/reach\s*(\d+)\s*ft/i);
        const rangeMatch = text.match(/range\s*(\d+)(?:\/(\d+))?\s*ft/i);
        
        return {
          name: item.name,
          description: item.description,
          attackBonus: attackMatch ? parseInt(attackMatch[1], 10) : null,
          damage: damageMatch ? damageMatch[1] : null,
          damageType: damageMatch ? damageMatch[2] : null,
          reach: reachMatch ? parseInt(reachMatch[1], 10) : null,
          range: rangeMatch ? { normal: parseInt(rangeMatch[1], 10), long: rangeMatch[2] ? parseInt(rangeMatch[2], 10) : null } : null,
          importMeta
        };
      }
      case 'bonusActions':
      case 'reactions': {
        return {
          name: item.name,
          description: item.description,
          importMeta
        };
      }
      case 'speciesTraits': {
        return {
          ...extractFeatureLikeEntryFromReviewItem(item, 'Species'),
          source: 'Species',
          importMeta
        };
      }
      case 'feats': {
        return {
          ...extractFeatureLikeEntryFromReviewItem(item, 'Imported'),
          source: 'Imported',
          importMeta
        };
      }
      case 'notes':
      default:
        return {
          content: `${item.name}: ${item.description}`,
          importMeta
        };
    }
  };

  const getPrimaryReviewCategory = (item, selectedCategories = []) => {
    const normalizedSelected = normalizeReviewCategories(selectedCategories);
    if (normalizedSelected.length > 0) return normalizedSelected[0];

    const suggestedCategories = normalizeReviewCategories(deriveInitialCategoriesFromItem(item));
    if (suggestedCategories.length > 0) return suggestedCategories[0];

    return normalizeImportField(item?.suggestedType || 'notes');
  };

  const buildReviewOutcomePreview = (item, selectedCategories = []) => {
    const category = getPrimaryReviewCategory(item, selectedCategories);
    const parsedValue = parseAsCategory(item, category);
    const rawSourceText = String(item?.parserMeta?.rawText || '').trim();
    const fallbackSummary = shortenReviewPreviewText(item?.description || rawSourceText, 180);
    const buildPreview = (title, summary, detailChips = [], targetCategory = category) => ({
      title: title || item?.name || 'Imported detail',
      summary: summary || fallbackSummary,
      detailChips: detailChips.filter(Boolean),
      destinationLabel: formatCatalogKindLabel(targetCategory),
      rawSourceText
    });

    switch (category) {
      case 'abilities': {
        const values = parsedValue && typeof parsedValue === 'object' ? parsedValue : {};
        const ordered = [
          ['STR', values.strength],
          ['DEX', values.dexterity],
          ['CON', values.constitution],
          ['INT', values.intelligence],
          ['WIS', values.wisdom],
          ['CHA', values.charisma]
        ].filter(([, value]) => Number.isFinite(value));
        return buildPreview(
          ordered.length > 0 ? 'Ability score updates' : (item?.name || 'Ability scores'),
          ordered.length > 0 ? `SmartPaste will update ${ordered.length} ability score${ordered.length === 1 ? '' : 's'}.` : fallbackSummary,
          ordered.map(([label, value]) => `${label} ${value}`)
        );
      }
      case 'savingThrows': {
        const values = parsedValue && typeof parsedValue === 'object' ? parsedValue : {};
        const entries = Object.entries(values)
          .filter(([, value]) => Number.isFinite(value))
          .map(([key, value]) => `${key.slice(0, 3).toUpperCase()} ${value >= 0 ? '+' : ''}${value}`);
        return buildPreview(
          entries.length > 0 ? 'Saving throw updates' : (item?.name || 'Saving throws'),
          entries.length > 0 ? `SmartPaste will update ${entries.length} saving throw${entries.length === 1 ? '' : 's'}.` : fallbackSummary,
          entries
        );
      }
      case 'skills': {
        const skills = Array.isArray(parsedValue) ? parsedValue : [];
        return buildPreview(
          skills.length > 0 ? 'Skill updates' : (item?.name || 'Skills'),
          skills.length > 0 ? `SmartPaste will add or update ${skills.length} skill entr${skills.length === 1 ? 'y' : 'ies'}.` : fallbackSummary,
          skills.slice(0, 4).map((skill) => `${skill.name}${Number.isFinite(skill.bonus) ? ` ${skill.bonus >= 0 ? '+' : ''}${skill.bonus}` : ''}`)
        );
      }
      case 'spells': {
        const spells = Array.isArray(parsedValue) ? parsedValue : [];
        if (spells.length === 1) {
          const spell = spells[0];
          const levelLabel = spell.level === 0 ? 'Cantrip' : (Number.isFinite(spell.level) ? `Level ${spell.level}` : null);
          return buildPreview(
            spell.name || item?.name || 'Spell',
            shortenReviewPreviewText(spell.description || item?.description || rawSourceText, 180),
            [
              levelLabel,
              spell.school,
              spell.castingTime,
              spell.range,
              spell.duration,
              spell.damage ? `Damage ${spell.damage}` : null,
              spell.saveType ? `Save ${String(spell.saveType).toUpperCase()}` : null
            ],
            'spells'
          );
        }
        return buildPreview(
          spells.length > 0 ? `${spells.length} spells` : (item?.name || 'Spell details'),
          spells.length > 0 ? `SmartPaste will add ${spells.length} spell${spells.length === 1 ? '' : 's'} from this block.` : fallbackSummary,
          spells.slice(0, 4).map((spell) => spell.name).filter(Boolean),
          'spells'
        );
      }
      case 'equipment': {
        const equipment = parsedValue && typeof parsedValue === 'object' ? parsedValue : {};
        return buildPreview(
          equipment.name || item?.name || 'Equipment item',
          shortenReviewPreviewText(equipment.description || item?.description || rawSourceText, 180),
          [
            equipment.type ? formatCatalogKindLabel(equipment.type) : null,
            equipment.quantity ? `Qty ${equipment.quantity}` : null,
            equipment.damage ? `${equipment.damage}${equipment.damageType ? ` ${equipment.damageType}` : ''}` : null,
            equipment.armorClass ? `AC ${equipment.armorClass}` : null,
            equipment.range ? `Range ${equipment.range}` : null,
            equipment.attunement ? 'Attunement' : null
          ],
          'equipment'
        );
      }
      case 'features':
      case 'classFeatures':
      case 'speciesTraits':
      case 'feats':
      case 'actions':
      case 'bonusActions':
      case 'reactions': {
        const entry = parsedValue && typeof parsedValue === 'object' ? parsedValue : {};
        return buildPreview(
          entry.name || item?.name || 'Imported entry',
          shortenReviewPreviewText(entry.description || item?.description || rawSourceText, 180),
          [
            entry.source,
            entry.usage,
            entry.attackBonus !== null && entry.attackBonus !== undefined ? `Atk ${entry.attackBonus >= 0 ? '+' : ''}${entry.attackBonus}` : null,
            entry.damage ? `${entry.damage}${entry.damageType ? ` ${entry.damageType}` : ''}` : null,
            entry.range?.normal ? `Range ${entry.range.normal}${entry.range.long ? `/${entry.range.long}` : ''}` : null,
            entry.reach ? `Reach ${entry.reach}` : null
          ],
          category
        );
      }
      case 'notes':
      default:
        return buildPreview(item?.name || 'Imported note', fallbackSummary, [], 'notes');
    }
  };

  const getAllUncategorizedItems = () => {
    const baseItems = Array.isArray(extrasModel?.items) ? extrasModel.items : [];
    if (!baseItems.length && !splitItems.length) return [];

    // @date_of_change 2025-12-09 - Hide items that have been explicitly separated or merged
    const filteredBase = baseItems.filter(item => {
      const decision = uncategorizedDecisions[item.key];
      return decision !== 'separated' && decision !== 'merged';
    });

    const filteredSplit = splitItems.filter(item => {
      const decision = uncategorizedDecisions[item.key];
      return decision !== 'merged';
    });

    return [...filteredBase, ...filteredSplit];
  };

  const canApplyChanges = () => {
    if (!hasConfirmedContentUse) return false;

    if (selectedSuggestionIndexes.some((idx) => (
      suggestions[idx] &&
      !appliedSuggestionIndexes.includes(idx) &&
      !getSuggestionKnowledgeInsight(suggestions[idx], idx).needsKnowledgeConfirmation
    ))) {
      return true;
    }

    const hasReviewSelections = getAllUncategorizedItems().some((item) => {
      const decision = uncategorizedDecisions[item.key];
      return Array.isArray(decision) && decision.length > 0;
    });
    if (hasReviewSelections) return true;

    return Object.values(riderAssociations || {}).some((assoc) => assoc?.status === 'applied');
  };

  const getReviewItemKeys = (itemsOrKeys) => Array.from(new Set(
    (Array.isArray(itemsOrKeys) ? itemsOrKeys : [itemsOrKeys])
      .map((entry) => (typeof entry === 'string' ? entry : entry?.key))
      .filter(Boolean)
  ));

  const setKnowledgeConfirmationForReviewItems = (itemsOrKeys, confirmed = true) => {
    const keys = getReviewItemKeys(itemsOrKeys);
    if (!keys.length) return;

    setConfirmedReviewItems((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        if (confirmed) {
          next[key] = true;
        } else {
          delete next[key];
        }
      });
      return next;
    });
  };

  const separateTextBlock = (itemKey, item) => {
    const text = item.description || '';
    
    const bulletMatch = text.split(/(?:^|\n)\s*(?:[-•●◦▪▸►]|\d+[.)]\s*|\*\s+)/);
    if (bulletMatch.length > 1) {
      const newItems = [];
      bulletMatch.filter(s => s.trim()).forEach((segment, idx) => {
        const trimmed = segment.trim();
        if (!trimmed) return;
        const colonIdx = trimmed.indexOf(':');
        const periodIdx = trimmed.indexOf('.');
        let name, desc;
        if (colonIdx > 0 && colonIdx < 50) {
          name = trimmed.substring(0, colonIdx).trim();
          desc = trimmed.substring(colonIdx + 1).trim();
        } else if (periodIdx > 0 && periodIdx < 60) {
          name = trimmed.substring(0, periodIdx).trim();
          desc = trimmed.substring(periodIdx + 1).trim();
        } else {
          name = trimmed.substring(0, 40).trim() + (trimmed.length > 40 ? '...' : '');
          desc = trimmed;
        }
        const separatedItem = {
          name,
          description: desc || trimmed,
          key: `${itemKey}-split-${idx}`,
          suggestedType: item.suggestedType,
          parentKey: itemKey,
          source: item.source,
          subType: item.subType,
          tags: Array.isArray(item.tags) ? [...item.tags] : [],
          parserMeta: item?.parserMeta
            ? {
                ...item.parserMeta,
                rawText: trimmed,
                reviewReason: item.parserMeta.reviewReason || 'Separated from a larger review block'
              }
            : null
        };
        newItems.push(separatedItem);
      });

      if (newItems.length > 0) {
        // @date_of_change 2025-12-09 - Track separated chunks as independent review items and hide the original
        setSplitItems(prev => [...prev, ...newItems]);
        setUncategorizedDecisions(prev => ({ ...prev, [itemKey]: 'separated' }));
        setKnowledgeConfirmationForReviewItems(itemKey, false);
      }
    }
  };

  // eslint-disable-next-line no-unused-vars
  const _batchApplySelectedCategories = (itemKeys, item) => {
    if (!onApplyChanges) return;
    
    const selectedCategories = uncategorizedDecisions[itemKeys[0]] || [];
    if (!Array.isArray(selectedCategories) || selectedCategories.length === 0) return;
    
    const records = selectedCategories.map(category => ({
      timestamp: new Date().toISOString(),
      action: 'append',
      field: category,
      data: parseAsCategory(item, category),
      originalText: `${item.name}: ${item.description}`,
      parsedContent: { type: category, data: item }
    }));
    
    records.forEach(record => onApplyChanges(record));
    
    itemKeys.forEach(key => setUncategorizedDecisions(prev => ({ ...prev, [key]: 'applied' })));
  };

  const skipUncategorizedItem = (itemKey) => {
    setUncategorizedDecisions(prev => ({ ...prev, [itemKey]: 'skipped' }));
    setKnowledgeConfirmationForReviewItems(itemKey, false);
  };

  const looksLikeMultipleItems = (text) => {
    if (!text) return false;
    const bulletPattern = /(?:^|\n)\s*(?:[-•●◦▪▸►]|\d+[.)]\s*|\*\s+)/;
    const semicolonCount = (text.match(/;/g) || []).length;
    const doubleNewlines = (text.match(/\n\n+/g) || []).length;
    return bulletPattern.test(text) || semicolonCount >= 2 || doubleNewlines >= 2;
  };

  const formatReviewSource = (item) => {
    if (item?.sourceLabel) return item.sourceLabel;
    const sourceSection = item?.parserMeta?.sourceSection;
    if (!sourceSection) return null;
    const normalized = String(sourceSection)
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_/]+/g, ' ')
      .trim();
    if (!normalized) return null;
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const formatReviewLocation = (item) => {
    const lineStart = item?.parserMeta?.lineStart;
    const lineEnd = item?.parserMeta?.lineEnd;
    if (!Number.isInteger(lineStart)) return null;
    if (Number.isInteger(lineEnd) && lineEnd > lineStart) return `Lines ${lineStart}-${lineEnd}`;
    return `Line ${lineStart}`;
  };

  const formatReviewConfidence = (item) => {
    const confidence = typeof item?.parseConfidence === 'number'
      ? item.parseConfidence
      : item?.parserMeta?.parseConfidence;
    if (typeof confidence !== 'number') return null;
    return getConfidenceLabel(confidence);
  };

  const getItemKnowledgeSource = (item) => getKnowledgeSourceFromValue(item);

  const getKnowledgeSourceDisplay = (knowledgeSource) => {
    switch (String(knowledgeSource || '').toLowerCase()) {
      case 'srd':
        return {
          label: 'Rules reference',
          className: 'knowledge-srd',
          title: 'Filled from the public game rules included with VCS.'
        };
      case 'provided':
        return {
          label: 'From paste',
          className: 'knowledge-provided',
          title: 'Read directly from the pasted content or an explicitly labeled section.'
        };
      case 'inferred':
        return {
          label: 'SmartPaste guess',
          className: 'knowledge-inferred',
          title: 'SmartPaste made its best guess. Check this before adding it.'
        };
      case 'mixed':
        return {
          label: 'Paste + rules',
          className: 'knowledge-mixed',
          title: 'This combines pasted details with the public game rules included with VCS.'
        };
      case 'user':
        return {
          label: 'Confirmed',
          className: 'knowledge-user',
          title: 'You confirmed this choice.'
        };
      default:
        return null;
    }
  };

  const getItemKnowledgeMeta = (item) => {
    const parserMeta = item?.parserMeta || item?.proficienciesMeta || item?.data?.parserMeta;
    if (!parserMeta?.knowledgeSource) return null;
    const display = getKnowledgeSourceDisplay(parserMeta.knowledgeSource);
    if (!display) return null;
    return {
      ...display,
      note: null
    };
  };

  const getSuggestionKnowledgeMeta = (suggestion) => {
    if (!suggestion) return null;

    const sources = getSuggestionKnowledgeSources(suggestion);
    if (sources.length === 1) {
      return getKnowledgeSourceDisplay(sources[0]);
    }
    if (sources.length > 1) {
      return getKnowledgeSourceDisplay('mixed');
    }
    return null;
  };

  const getSuggestionKnowledgeInsight = (suggestion, index) => {
    const knowledgeSources = getSuggestionKnowledgeSources(suggestion);
    const requiresKnowledgeConfirmation = knowledgeSources.some((source) => doesKnowledgeSourceRequireConfirmation(source));
    const knowledgeConfirmedInSession = requiresKnowledgeConfirmation && Boolean(confirmedSuggestionIndexes[index]);
    return {
      knowledgeSources,
      requiresKnowledgeConfirmation,
      knowledgeConfirmedInSession,
      needsKnowledgeConfirmation: requiresKnowledgeConfirmation && !knowledgeConfirmedInSession
    };
  };

  const getSuggestionKnowledgeStatusMeta = (insight) => {
    if (!insight) return null;
    if (insight.needsKnowledgeConfirmation) {
      return {
        label: 'Needs confirmation',
        className: 'knowledge-inferred',
        title: 'SmartPaste chose where this probably belongs. Select it to confirm.'
      };
    }
    if (insight.knowledgeConfirmedInSession) {
      return getKnowledgeSourceDisplay('user');
    }
    return null;
  };

  const getReviewKnowledgeStatusMeta = (insight) => {
    if (!insight) return null;
    if (insight.needsKnowledgeConfirmation) {
      return {
        label: 'Needs confirmation',
        className: 'knowledge-inferred',
        title: 'SmartPaste chose where this probably belongs. Confirm the choice before adding it.'
      };
    }
    if (insight.knowledgeConfirmedInSession) {
      return getKnowledgeSourceDisplay('user');
    }
    if (insight.knowledgeConfirmedFromMemory) {
      return {
        label: 'Remembered review',
        className: 'remembered-hint',
        title: 'SmartPaste reused a prior review decision for this character.'
      };
    }
    return null;
  };

  const formatCatalogKindLabel = (kind) => ({
    abilities: 'Ability Scores',
    savingThrows: 'Saving Throws',
    skills: 'Skills',
    spells: 'Spells',
    features: 'Feature',
    classFeatures: 'Class Feature',
    feats: 'Feat',
    speciesTraits: 'Species Trait',
    actions: 'Action',
    bonusActions: 'Bonus Action',
    reactions: 'Reaction',
    equipment: 'Equipment',
    notes: 'Note'
  }[kind] || kind);

  const formatCatalogKindCountLabel = (kind, count) => ({
    abilities: 'Ability Scores',
    savingThrows: 'Saving Throws',
    skills: 'Skills',
    spells: count === 1 ? 'Spell' : 'Spells',
    features: count === 1 ? 'Feature' : 'Features',
    classFeatures: count === 1 ? 'Class Feature' : 'Class Features',
    feats: count === 1 ? 'Feat' : 'Feats',
    speciesTraits: count === 1 ? 'Species Trait' : 'Species Traits',
    actions: count === 1 ? 'Action' : 'Actions',
    bonusActions: count === 1 ? 'Bonus Action' : 'Bonus Actions',
    reactions: count === 1 ? 'Reaction' : 'Reactions',
    equipment: 'Equipment',
    notes: count === 1 ? 'Note' : 'Notes'
  }[kind] || `${formatCatalogKindLabel(kind)}${count === 1 ? '' : 's'}`);

  const buildReviewItemInsight = (item) => {
    const selectedCats = normalizeReviewCategories(uncategorizedDecisions[item.key]);
    const rememberedCats = normalizeReviewCategories(rememberedReviewSelections[item.key]);
    const knowledgeSource = getItemKnowledgeSource(item);
    const knowledgeRequiresConfirmation = ['inferred', 'mixed'].includes(knowledgeSource);
    const knowledgeConfirmedInSession = knowledgeRequiresConfirmation && Boolean(confirmedReviewItems[item.key]);
    const knowledgeConfirmedFromMemory = !knowledgeConfirmedInSession &&
      knowledgeRequiresConfirmation &&
      rememberedCats.length > 0;
    const needsKnowledgeConfirmation = selectedCats.length > 0 &&
      knowledgeRequiresConfirmation &&
      !knowledgeConfirmedInSession &&
      !knowledgeConfirmedFromMemory;
    const reviewConflictEntries = selectedCats
      .map((category) => {
        const parsedReviewItem = parseAsCategory(item, category);
        const conflicts = getImportItemConflicts(parsedReviewItem, category);
        if (!conflicts.length) return null;

        const rememberedConflictResolution = getRememberedConflictResolutionForField(parsedReviewItem, category, conflicts);
        const rememberedConflictTarget = getRememberedConflictTargetForField(parsedReviewItem, category, conflicts);

        return {
          category,
          parsedReviewItem,
          conflicts,
          conflictResolution: reviewConflictResolutions?.[item.key]?.[category] ||
            rememberedConflictResolution ||
            getDefaultConflictResolution(conflicts),
          conflictTargetValue: (() => {
            const explicitTarget = reviewConflictTargets?.[item.key]?.[category];
            if (explicitTarget && conflicts.some(conflict => conflict.fingerprint === explicitTarget)) {
              return explicitTarget;
            }

            if (rememberedConflictTarget && conflicts.some(conflict => conflict.fingerprint === rememberedConflictTarget)) {
              return rememberedConflictTarget;
            }

            return conflicts[0]?.fingerprint || '';
          })(),
          hasRememberedConflictChoice: !reviewConflictResolutions?.[item.key]?.[category] && Boolean(rememberedConflictResolution),
          hasRememberedConflictTarget: !reviewConflictTargets?.[item.key]?.[category] && Boolean(rememberedConflictTarget)
        };
      })
      .filter(Boolean);

    const reviewConflictHints = reviewConflictEntries.map((entry) => {
      const categoryLabel = formatCatalogKindLabel(entry.category);
      const primaryConflict = entry.conflicts[0];
      const summary = primaryConflict?.matchType === 'exact'
        ? `May update existing ${categoryLabel}`
        : `May match existing ${categoryLabel}`;
      const title = entry.conflicts
        .map((conflict) => `${conflict.name}: ${conflict.description || 'Existing entry'}`)
        .join('\n');

      return {
        key: `${item.key}-${entry.category}`,
        summary,
        title,
        isExact: primaryConflict?.matchType === 'exact'
      };
    });

    const hasRememberedReviewConflictChoice = reviewConflictEntries.some((entry) => entry.hasRememberedConflictChoice);
    const hasRememberedReviewConflictTarget = !hasRememberedReviewConflictChoice &&
      reviewConflictEntries.some((entry) => entry.hasRememberedConflictTarget);
    const needsConflictAttention = reviewConflictEntries.some((entry) => {
      const hasExplicitResolution = Boolean(reviewConflictResolutions?.[item.key]?.[entry.category]);
      const hasExplicitTarget = Boolean(reviewConflictTargets?.[item.key]?.[entry.category]);
      return !entry.hasRememberedConflictChoice && !entry.hasRememberedConflictTarget && !hasExplicitResolution && !hasExplicitTarget;
    });
    const primaryMatchType = reviewConflictEntries.some((entry) => entry.conflicts[0]?.matchType === 'exact')
      ? 'exact'
      : (reviewConflictEntries.length > 0 ? 'sameName' : null);
    const needsCategorySelection = selectedCats.length === 0;
    const sortPriority = (
      (primaryMatchType === 'exact' ? 40 : 0) +
      (primaryMatchType === 'sameName' ? 20 : 0) +
      (needsKnowledgeConfirmation ? 12 : 0) +
      (needsCategorySelection ? 5 : 0) -
      (knowledgeConfirmedInSession ? 3 : 0) -
      (knowledgeConfirmedFromMemory ? 2 : 0) -
      (hasRememberedReviewConflictChoice ? 4 : 0) -
      (hasRememberedReviewConflictTarget ? 2 : 0)
    );

    return {
      selectedCats,
      rememberedCats,
      knowledgeSource,
      knowledgeRequiresConfirmation,
      knowledgeConfirmedInSession,
      knowledgeConfirmedFromMemory,
      needsKnowledgeConfirmation,
      reviewConflictEntries,
      reviewConflictHints,
      hasRememberedReviewConflictChoice,
      hasRememberedReviewConflictTarget,
      needsConflictAttention,
      needsCategorySelection,
      primaryMatchType,
      sortPriority
    };
  };

  const getReadyReviewItems = () => getAllUncategorizedItems().filter((item) => {
    const decision = uncategorizedDecisions[item.key];
    if (decision === 'applied' || decision === 'skipped') return false;
    const insight = buildReviewItemInsight(item);
    return insight.selectedCats.length > 0 &&
      !insight.needsConflictAttention &&
      !insight.needsKnowledgeConfirmation;
  });

  const buildSimulatorFinishLine = () => {
    if (String(context?.entityType || '').toLowerCase() !== 'character' || !parsedContent) {
      return null;
    }

    const noStructuredActionsWarning = 'No usable combat actions were found. Add an action, bonus action, reaction, weapon, or spell before starting combat.';

    const payload = buildApplyPayload({
      includeSuggestions: true,
      includeReviewItems: true,
      includeRiders: true
    });
    const unresolvedReviewItems = getAllUncategorizedItems().filter((item) => {
      const decision = uncategorizedDecisions[item.key];
      if (decision === 'applied' || decision === 'skipped') return false;
      const insight = buildReviewItemInsight(item);
      return (
        insight.needsCategorySelection ||
        insight.needsConflictAttention ||
        insight.needsKnowledgeConfirmation
      );
    });
    const unresolvedRiderCount = [
      ...(parsedContent?.data?.weaponRiders || []).map((_, idx) => riderAssociations[`weapon_rider_${idx}`]),
      ...(parsedContent?.data?.equipmentRiders || []).map((_, idx) => riderAssociations[`equip_rider_${idx}`])
    ].filter((association) => association?.status !== 'applied' && association?.status !== 'skipped').length;
    const selectedSuggestionCount = (Array.isArray(selectedSuggestionIndexes) ? selectedSuggestionIndexes : [])
      .filter((idx) => suggestions[idx] && !appliedSuggestionIndexes.includes(idx))
      .length;
    const selectedFullImportCount = (Array.isArray(selectedSuggestionIndexes) ? selectedSuggestionIndexes : [])
      .filter((idx) => suggestions[idx]?.field === 'all' && !appliedSuggestionIndexes.includes(idx))
      .length;
    const readyReviewCount = getReadyReviewItems().length;
    const appliedAssociationCount = Object.values(riderAssociations || {}).filter((assoc) => assoc?.status === 'applied').length;

    if (!payload.records.length) {
      return {
        status: 'idle',
        title: 'Choose details to add',
        summary: 'Choose the character details you want, then finish anything that still needs your attention.',
        selectedSuggestionCount,
        selectedFullImportCount,
        readyReviewCount,
        appliedAssociationCount,
        unresolvedReviewCount: unresolvedReviewItems.length,
        unresolvedRiderCount,
        projectedCharacter: null,
        readinessAssessment: null,
        readinessStatus: null,
        finalApplyBlocked: false,
        finalApplyGuidance: ''
      };
    }

    const projectedCharacter = projectSmartPasteCharacterRecords(context?.currentData || {}, payload.records);
    const readinessAssessment = assessCharacterCombatReadiness(projectedCharacter);
    const readinessStatus = classifyCombatReadiness(readinessAssessment);
    const simulatorActionEconomy = buildCombatActionEconomy(projectedCharacter);
    const simulatorActionCounts = {
      actions: Array.isArray(simulatorActionEconomy?.actions) ? simulatorActionEconomy.actions.length : 0,
      bonusActions: Array.isArray(simulatorActionEconomy?.bonusActions) ? simulatorActionEconomy.bonusActions.length : 0,
      reactions: Array.isArray(simulatorActionEconomy?.reactions) ? simulatorActionEconomy.reactions.length : 0
    };
    // @date_of_change: 2026-04-09
    // rule_reference: Smart Paste simulator readiness must honor the same combat option derivation the battle room uses.
    // Weapons, spells, feats, class features, and species traits can all become usable combat actions after hydration.
    const hasSimulatorActionEconomy = (
      simulatorActionCounts.actions + simulatorActionCounts.bonusActions + simulatorActionCounts.reactions
    ) > 0;
    const effectiveReadinessStatus = hasSimulatorActionEconomy
      ? {
          ...readinessStatus,
          warnings: (readinessStatus?.warnings || []).filter((warning) => warning !== noStructuredActionsWarning),
          blockingWarnings: (readinessStatus?.blockingWarnings || []).filter((warning) => warning !== noStructuredActionsWarning),
          informationalWarnings: [...(readinessStatus?.informationalWarnings || [])],
          simulatorReady: (readinessStatus?.errors || []).length === 0
            && (readinessStatus?.blockingWarnings || []).filter((warning) => warning !== noStructuredActionsWarning).length === 0
        }
      : readinessStatus;
    const resolvedDerivedCombatEconomy = hasSimulatorActionEconomy
      && (readinessStatus?.blockingWarnings || []).includes(noStructuredActionsWarning)
      && effectiveReadinessStatus.simulatorReady;

    let status = 'ready';
    let title = 'Ready for combat';
    let summary = 'This character has the core stats and options needed for combat.';

    if (unresolvedReviewItems.length > 0 || unresolvedRiderCount > 0) {
      status = 'needs-review';
      title = 'Needs your review';
      summary = 'Finish the remaining choices before adding the complete character.';
    } else if (!effectiveReadinessStatus.simulatorReady) {
      status = 'combat-review';
      title = 'Needs combat details';
      summary = 'A few combat details are still missing before this can replace the whole sheet.';
    } else if (resolvedDerivedCombatEconomy) {
      status = 'ready-with-notes';
      title = 'Ready with combat options';
      summary = 'SmartPaste turned the pasted weapons, spells, feats, class features, and species traits into usable combat options.';
    } else if (effectiveReadinessStatus.informationalWarnings.length > 0) {
      status = 'ready-with-notes';
      title = 'Ready with a few assumptions';
      summary = 'This character can enter combat, but SmartPaste filled in a few missing defaults for you.';
    }

    let finalApplyBlocked = false;
    let finalApplyGuidance = '';

    if (selectedFullImportCount > 0) {
      if (unresolvedReviewItems.length > 0 || unresolvedRiderCount > 0) {
        finalApplyBlocked = true;
        finalApplyGuidance = 'Finish the remaining choices before replacing the whole sheet, or use Add Ready Details to add only the sections that are ready.';
      } else if (!effectiveReadinessStatus.simulatorReady) {
        const firstBlockingIssue = effectiveReadinessStatus.errors[0] || effectiveReadinessStatus.blockingWarnings[0];
        finalApplyBlocked = true;
        finalApplyGuidance = firstBlockingIssue
          ? `This character still needs a combat detail: ${firstBlockingIssue}`
          : 'This character still needs a few combat details before it can replace the whole sheet.';
      }
    }

    return {
      status,
      title,
      summary,
      selectedSuggestionCount,
      selectedFullImportCount,
      readyReviewCount,
      appliedAssociationCount,
      unresolvedReviewCount: unresolvedReviewItems.length,
      unresolvedRiderCount,
      projectedCharacter,
      readinessAssessment,
      readinessStatus: effectiveReadinessStatus,
      simulatorActionCounts,
      finalApplyBlocked,
      finalApplyGuidance
    };
  };

  const renderCombatActionEconomySection = (preview) => {
    if (!preview) return null;

    const groupedCatalogEntries = [
      {
        key: 'actions',
        label: 'Actions',
        entries: preview.catalog.filter((entry) => (entry?.activationType || entry?.actionType || 'action') === 'action')
      },
      {
        key: 'bonusActions',
        label: 'Bonus Actions',
        entries: preview.catalog.filter((entry) => (entry?.activationType || entry?.actionType || '') === 'bonus action')
      },
      {
        key: 'reactions',
        label: 'Reactions',
        entries: preview.catalog.filter((entry) => (entry?.activationType || entry?.actionType || '') === 'reaction')
      }
    ].filter((group) => group.entries.length > 0);

    return (
      <div className="combat-action-economy-section">
        <div className="combat-action-economy-header">
          <h4>
            <span className="header-icon">⚔️</span>
            Combat options
          </h4>
          <p className="combat-action-economy-intro">
            SmartPaste can build these actions from the pasted character. Choose which parts you want to add to the sheet.
          </p>
        </div>

        <div className="combat-action-economy-metrics">
          <span>{formatCountLabel(preview.counts.actions, 'action')}</span>
          <span>{formatCountLabel(preview.counts.bonusActions, 'bonus action')}</span>
          <span>{formatCountLabel(preview.counts.reactions, 'reaction')}</span>
          {preview.movementSummary && <span>Movement {preview.movementSummary}</span>}
        </div>

        {preview.sourceEntries.length > 0 && (
          <div className="combat-action-economy-sources">
            <div className="combat-action-economy-subtitle">Choose which pasted sections add these combat options</div>
            <div className="combat-action-economy-source-grid">
              {preview.sourceEntries.map((source) => (
                <button
                  key={source.field}
                  type="button"
                  className={`combat-action-source-btn${source.isSelected ? ' selected' : ''}${source.isPartiallySelected ? ' partial' : ''}`}
                  onClick={() => handleDetectedFieldClick(source.field)}
                  title={source.description}
                >
                  <span className="combat-action-source-label">{source.label}</span>
                  <span className="combat-action-source-count">{formatCountLabel(source.itemCount, 'entry')}</span>
                  <span className="combat-action-source-state">
                    {source.isSelected ? 'Selected' : source.isPartiallySelected ? 'Partially selected' : 'Available'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {groupedCatalogEntries.length > 0 ? (
          <div className="combat-action-economy-groups">
            {groupedCatalogEntries.map((group) => (
              <div key={group.key} className="combat-action-economy-group">
                <div className="combat-action-economy-group-title">{group.label}</div>
                <div className="combat-action-economy-entry-list">
                  {group.entries.map((entry, entryIndex) => {
                    const entryMeta = [
                      formatCombatEntryTypeLabel(entry?.type),
                      formatCombatRangeSummary(entry?.range),
                      Number.isFinite(entry?.bonus) && entry.bonus !== 0 ? `${entry.bonus >= 0 ? '+' : ''}${entry.bonus} to hit` : '',
                      entry?.areaType && entry?.areaSize ? `${entry.areaSize}-ft ${entry.areaType}` : ''
                    ].filter(Boolean);
                    const entryDescription = String(entry?.desc || '').trim();
                    return (
                      <div key={`${group.key}-${entry.name}-${entryIndex}`} className="combat-action-economy-entry">
                        <div className="combat-action-economy-entry-name">{entry.name}</div>
                        {entryMeta.length > 0 && (
                          <div className="combat-action-economy-entry-meta">
                            {entryMeta.map((metaBit) => (
                              <span key={`${entry.name}-${metaBit}`} className="combat-action-economy-entry-chip">{metaBit}</span>
                            ))}
                          </div>
                        )}
                        {entryDescription && (
                          <div className="combat-action-economy-entry-desc" title={entryDescription}>
                            {entryDescription.length > 160 ? `${entryDescription.slice(0, 157)}...` : entryDescription}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="combat-action-economy-empty">
            SmartPaste did not find any combat actions yet. Check the weapons, spells, feats, and features below.
          </div>
        )}
      </div>
    );
  };

  const commitAppliedReviewItems = (payload) => {
    const appliedKeySet = new Set(payload?.appliedReviewItemKeys || []);
    if (appliedKeySet.size === 0) return;

    setUncategorizedDecisions((prev) => {
      const next = { ...prev };
      appliedKeySet.forEach((key) => {
        next[key] = 'applied';
      });
      return next;
    });
    setExpandedReviewItems((prev) => {
      const next = { ...prev };
      appliedKeySet.forEach((key) => {
        delete next[key];
      });
      return next;
    });
    setReviewMergeSelection((prev) => {
      const next = { ...prev };
      appliedKeySet.forEach((key) => {
        delete next[key];
      });
      return next;
    });
    setReviewConflictResolutions((prev) => {
      const next = { ...prev };
      appliedKeySet.forEach((key) => {
        delete next[key];
      });
      return next;
    });
    setReviewConflictTargets((prev) => {
      const next = { ...prev };
      appliedKeySet.forEach((key) => {
        delete next[key];
      });
      return next;
    });
    setConfirmedReviewItems((prev) => {
      const next = { ...prev };
      appliedKeySet.forEach((key) => {
        delete next[key];
      });
      return next;
    });
    setRememberedReviewSelections((prev) => {
      const next = { ...prev };
      (payload?.reviewDecisionsToRemember || []).forEach(({ item, categories }) => {
        if (item?.key) {
          next[item.key] = categories;
        }
      });
      return next;
    });
  };

  const commitAppliedSuggestionIndexes = (indexes) => {
    const appliedIndexes = Array.from(new Set(
      (Array.isArray(indexes) ? indexes : [])
        .filter((idx) => Number.isInteger(idx) && suggestions[idx])
    ));
    if (appliedIndexes.length === 0) return;

    const appliedIndexSet = new Set(appliedIndexes);
    setAppliedSuggestionIndexes((prev) => Array.from(new Set([...prev, ...appliedIndexes])));
    setSelectedSuggestionIndexes((prev) => {
      const next = prev.filter((idx) => !appliedIndexSet.has(idx));
      syncLegacySuggestionSelection(next);
      return next;
    });
    setConfirmedSuggestionIndexes((prev) => {
      const next = { ...prev };
      appliedIndexes.forEach((idx) => {
        delete next[idx];
      });
      return next;
    });
  };

  const canApplySelectedAndReady = () => (
    hasConfirmedContentUse && (
      selectedSuggestionIndexes.some((idx) => (
        suggestions[idx] &&
        !isStandaloneImportSuggestion(suggestions[idx]) &&
        !appliedSuggestionIndexes.includes(idx) &&
        !getSuggestionKnowledgeInsight(suggestions[idx], idx).needsKnowledgeConfirmation
      ))
      || getReadyReviewItems().length > 0
    )
  );

  // @date_of_change 2025-12-09 - Toggle merge selection for extras review items
  const toggleMergeSelection = (itemKey) => {
    setReviewMergeSelection(prev => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }));
  };

  // @date_of_change 2025-12-09 - Merge multiple selected extras into a single combined review item
  const mergeSelectedItems = (itemKeys, allItems) => {
    const uniqueKeys = Array.from(new Set(itemKeys || []));
    if (uniqueKeys.length < 2) return;

    const itemsToMerge = allItems.filter(item => uniqueKeys.includes(item.key));
    if (itemsToMerge.length < 2) return;

    const mergedName = itemsToMerge
      .map(item => item.name)
      .filter(Boolean)
      .join(' / ') || 'Merged Extra';

    const mergedDescription = itemsToMerge
      .map(item => {
        const label = item.name ? `${item.name}: ` : '';
        return `${label}${item.description || ''}`.trim();
      })
      .filter(Boolean)
      .join('\n\n');

    const primaryType = itemsToMerge[0].suggestedType || 'notes';
    const allTags = new Set();
    itemsToMerge.forEach(item => {
      (Array.isArray(item.tags) ? item.tags : []).forEach(t => allTags.add(t));
    });
    const mergedLineStart = itemsToMerge.reduce((min, item) => {
      const value = item?.parserMeta?.lineStart;
      return Number.isInteger(value) ? Math.min(min, value) : min;
    }, Number.POSITIVE_INFINITY);
    const mergedLineEnd = itemsToMerge.reduce((max, item) => {
      const value = item?.parserMeta?.lineEnd;
      return Number.isInteger(value) ? Math.max(max, value) : max;
    }, 0);
    const confidenceValues = itemsToMerge
      .map(item => (typeof item?.parseConfidence === 'number' ? item.parseConfidence : null))
      .filter(value => typeof value === 'number');
    const knowledgeSources = Array.from(new Set(
      itemsToMerge
        .map(item => item?.parserMeta?.knowledgeSource)
        .filter(Boolean)
    ));
    const mergedKnowledgeSource = knowledgeSources.length === 1
      ? knowledgeSources[0]
      : (knowledgeSources.length > 1 ? 'mixed' : undefined);
    const mergedKnowledgeNotes = Array.from(new Set(
      itemsToMerge
        .map(item => item?.parserMeta?.knowledgeNote)
        .filter(Boolean)
    ));

    const mergedItem = {
      key: `merge-${Date.now()}-${itemsToMerge[0].key}`,
      name: mergedName,
      description: mergedDescription,
      suggestedType: primaryType,
      tags: Array.from(allTags),
      isMerged: true,
      parserMeta: {
        sourceParser: 'SmartPasteInterface.mergeSelectedItems',
        sourceSection: itemsToMerge[0]?.parserMeta?.sourceSection || itemsToMerge[0]?.suggestedType || 'review',
        lineStart: Number.isFinite(mergedLineStart) ? mergedLineStart : undefined,
        lineEnd: mergedLineEnd > 0 ? mergedLineEnd : undefined,
        parseConfidence: confidenceValues.length ? confidenceValues.reduce((sum, value) => sum + value, 0) / confidenceValues.length : undefined,
        rawText: itemsToMerge.map(item => item?.parserMeta?.rawText || `${item.name}: ${item.description}`).filter(Boolean).join('\n\n'),
        reviewReason: `Merged by user from ${itemsToMerge.length} review items`,
        knowledgeSource: mergedKnowledgeSource,
        knowledgeNote: mergedKnowledgeNotes.length > 0 ? mergedKnowledgeNotes.join(' | ') : undefined
      }
    };

    setSplitItems(prev => [...prev, mergedItem]);
    setUncategorizedDecisions(prev => {
      const next = { ...prev };
      uniqueKeys.forEach(k => {
        next[k] = 'merged';
      });

      const autoCategories = deriveInitialCategoriesFromItem(mergedItem);
      if (autoCategories.length) {
        next[mergedItem.key] = autoCategories;
      }

      return next;
    });

    setReviewMergeSelection(prev => {
      const next = { ...prev };
      uniqueKeys.forEach(k => {
        delete next[k];
      });
      return next;
    });
    setConfirmedReviewItems((prev) => {
      const next = { ...prev };
      uniqueKeys.forEach((key) => {
        delete next[key];
      });
      next[mergedItem.key] = true;
      return next;
    });
  };

  const setExpandedStateForItems = (items, expanded) => {
    const keys = Array.from(new Set((Array.isArray(items) ? items : []).map((item) => item?.key).filter(Boolean)));
    if (!keys.length) return;

    setExpandedReviewItems((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        next[key] = expanded;
      });
      return next;
    });
  };

  const skipVisibleReviewItems = (items) => {
    const keys = Array.from(new Set((Array.isArray(items) ? items : []).map((item) => item?.key).filter(Boolean)));
    if (!keys.length) return;

    setUncategorizedDecisions((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        next[key] = 'skipped';
      });
      return next;
    });
    setReviewMergeSelection((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        delete next[key];
      });
      return next;
    });
    setKnowledgeConfirmationForReviewItems(keys, false);
  };

  const setCategoriesForReviewItems = (items, categories, mode = 'replace') => {
    const keys = Array.from(new Set((Array.isArray(items) ? items : []).map((item) => item?.key).filter(Boolean)));
    const normalizedCategories = normalizeReviewCategories(categories);
    if (!keys.length) return;

    setUncategorizedDecisions((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        const current = normalizeReviewCategories(next[key]);
        next[key] = mode === 'append'
          ? Array.from(new Set([...current, ...normalizedCategories]))
          : normalizedCategories;
      });
        return next;
      });
      if (normalizedCategories.length > 0) {
        setKnowledgeConfirmationForReviewItems(keys, true);
      }
  };

  const restoreSuggestedCategoriesForReviewItems = (items) => {
    const visibleItems = Array.isArray(items) ? items : [];
    if (!visibleItems.length) return;

    setUncategorizedDecisions((prev) => {
      const next = { ...prev };
      visibleItems.forEach((item) => {
        const rememberedCategories = normalizeReviewCategories(rememberedReviewSelections[item.key]);
        const fallbackCategories = deriveInitialCategoriesFromItem(item);
        const categories = rememberedCategories.length > 0 ? rememberedCategories : fallbackCategories;
        next[item.key] = categories;
      });
      return next;
    });
  };

  const setConflictResolutionForReviewItems = (items, resolution) => {
    const visibleItems = Array.isArray(items) ? items : [];
    if (!visibleItems.length || !resolution) return;

    setReviewConflictResolutions((prev) => {
      const next = { ...prev };

      visibleItems.forEach((item) => {
        const categories = normalizeReviewCategories(uncategorizedDecisions[item.key]);
        if (!categories.length) return;

        categories.forEach((category) => {
          const parsedReviewItem = parseAsCategory(item, category);
          const conflicts = getImportItemConflicts(parsedReviewItem, category);
          if (!conflicts.length) return;

          next[item.key] = {
            ...(next[item.key] || {}),
            [category]: resolution
          };
        });
      });

      return next;
    });
    setKnowledgeConfirmationForReviewItems(visibleItems, true);
  };

  const renderNeedsReview = () => {
    if (!parsedContent) return null;

    const allItems = getAllUncategorizedItems();
    if (!allItems.length) return null;
    const actionableItems = allItems.filter(item => 
      uncategorizedDecisions[item.key] !== 'applied' && 
      uncategorizedDecisions[item.key] !== 'skipped'
    );
    const appliedCount = allItems.length - actionableItems.length;

    // date_of_change: 2026-02-25
    // rule_reference: Group extras by their suggested D&D category for clearer user review.
    const groupKeyForItem = (item) => {
      const type = item.suggestedType;
      const source = item.source;
      const subType = item.subType;

      if (type === 'abilities' || type === 'savingThrows' || type === 'skills') return 'abilities';
      if (type === 'spells') return 'spells';
      if (type === 'equipment') {
        if (source === 'spellStoringItem') return 'equipment_spellStoringItem';
        if (source === 'scrolls') return 'equipment_scrolls';
        if (source === 'bloodforged') return 'equipment_bloodforged';
        return 'equipment_other';
      }

      if (type === 'actions') return 'actions';
      if (subType === 'speciesTrait') return 'traits_species';
      if (subType === 'monsterAction') return 'actions';

      if (type === 'features' || type === 'feats') return 'features';
      if (type === 'traits') return 'traits';
      if (type === 'notes' || type === 'biography') return 'notes';

      return 'other';
    };

    const groupLabels = {
      abilities: '💪 Core stats, saves, & skills',
      spells: '✨ Spells & spell lists',
      features: '⚔️ Features & Abilities',
      actions: '🎯 Combat Actions',
      equipment_spellStoringItem: '🎒 Equipment — Spell-Storing Item',
      equipment_scrolls: '🎒 Equipment — Scrolls',
      equipment_bloodforged: '🎒 Equipment — Bloodforged',
      equipment_other: '🎒 Equipment & Gear',
      traits_species: '🧬 Species Traits',
      traits: '🧬 Traits',
      notes: '📝 Notes & Descriptions',
      other: '❓ More unplaced details'
    };

    const itemInsights = allItems.reduce((acc, item) => {
      acc[item.key] = buildReviewItemInsight(item);
      return acc;
    }, {});
    const actionableConflicts = actionableItems.filter((item) => Boolean(itemInsights[item.key]?.primaryMatchType));
    const actionableExactConflicts = actionableItems.filter((item) => itemInsights[item.key]?.primaryMatchType === 'exact');
    const actionableNeedsCategory = actionableItems.filter((item) => itemInsights[item.key]?.needsCategorySelection);
    const actionableNeedsConflictReview = actionableItems.filter((item) => itemInsights[item.key]?.needsConflictAttention);
    const actionableNeedsConfirmation = actionableItems.filter((item) => itemInsights[item.key]?.needsKnowledgeConfirmation);
    const actionableResolved = actionableItems.filter((item) => {
      const insight = itemInsights[item.key];
      const categories = insight?.selectedCats || [];
      return categories.length > 0 &&
        !insight?.needsConflictAttention &&
        !insight?.needsKnowledgeConfirmation;
    });
    const actionableNeedsAttention = actionableItems.filter((item) => (
      itemInsights[item.key]?.needsCategorySelection ||
      itemInsights[item.key]?.needsConflictAttention ||
      itemInsights[item.key]?.needsKnowledgeConfirmation
    ));
    const reviewFilterOptions = [
      { value: 'all', label: 'All unplaced details', count: actionableItems.length },
      { value: 'attention', label: 'Needs attention', count: actionableNeedsAttention.length },
      { value: 'ready', label: 'Ready to add', count: actionableResolved.length },
      { value: 'needsCategory', label: 'Needs a section', count: actionableNeedsCategory.length },
      { value: 'needsConfirmation', label: 'Needs confirmation', count: actionableNeedsConfirmation.length },
      { value: 'needsConflict', label: 'Needs match choice', count: actionableNeedsConflictReview.length },
      { value: 'conflicts', label: 'Possible matches', count: actionableConflicts.length },
      { value: 'updates', label: 'Likely updates', count: actionableExactConflicts.length }
    ];
    const bulkCategoryOptions = [
      { value: 'abilities', label: 'Ability Scores' },
      { value: 'savingThrows', label: 'Saving Throws' },
      { value: 'skills', label: 'Skills' },
      { value: 'spells', label: 'Spells' },
      { value: 'features', label: 'Feature' },
      { value: 'actions', label: 'Action' },
      { value: 'bonusActions', label: 'Bonus Action' },
      { value: 'reactions', label: 'Reaction' },
      { value: 'feats', label: 'Feat' },
      { value: 'equipment', label: 'Equipment' },
      { value: 'speciesTraits', label: 'Species Trait' },
      { value: 'notes', label: 'Notes' }
    ];
    const matchesReviewFilter = (item) => {
      const decision = uncategorizedDecisions[item.key];
      const isResolved = decision === 'applied' || decision === 'skipped';
      if (isResolved) return reviewFilterMode === 'all';

      const insight = itemInsights[item.key];
      switch (reviewFilterMode) {
        case 'attention':
          return Boolean(
            insight?.needsCategorySelection ||
            insight?.needsConflictAttention ||
            insight?.needsKnowledgeConfirmation
          );
        case 'ready':
          return Boolean(
            (insight?.selectedCats || []).length > 0 &&
            !insight?.needsConflictAttention &&
            !insight?.needsKnowledgeConfirmation
          );
        case 'needsCategory':
          return Boolean(insight?.needsCategorySelection);
        case 'needsConfirmation':
          return Boolean(insight?.needsKnowledgeConfirmation);
        case 'needsConflict':
          return Boolean(insight?.needsConflictAttention);
        case 'conflicts':
          return Boolean(insight?.primaryMatchType);
        case 'updates':
          return insight?.primaryMatchType === 'exact';
        case 'all':
        default:
          return true;
      }
    };

    const groups = allItems.reduce((acc, item, index) => {
      if (!matchesReviewFilter(item)) return acc;
      const key = groupKeyForItem(item);
      if (!acc[key]) {
        acc[key] = { label: groupLabels[key] || 'Other', items: [], order: index };
      }
      acc[key].items.push(item);
      return acc;
    }, {});

    const groupEntries = Object.entries(groups)
      .filter(([, group]) => group.items.length > 0)
      .sort((left, right) => {
        const leftSeverity = Math.max(...left[1].items.map((item) => itemInsights[item.key]?.sortPriority || 0), 0);
        const rightSeverity = Math.max(...right[1].items.map((item) => itemInsights[item.key]?.sortPriority || 0), 0);
        if (rightSeverity !== leftSeverity) return rightSeverity - leftSeverity;
        return left[1].order - right[1].order;
      });
    const filteredActionableCount = Object.values(groups).reduce((sum, group) => (
      sum + group.items.filter((item) => {
        const decision = uncategorizedDecisions[item.key];
        return decision !== 'applied' && decision !== 'skipped';
      }).length
    ), 0);
    const visibleActionableItems = allItems.filter((item) => {
      if (!matchesReviewFilter(item)) return false;
      const decision = uncategorizedDecisions[item.key];
      return decision !== 'applied' && decision !== 'skipped';
    });
    const visibleResolvedItems = visibleActionableItems.filter((item) => {
      const insight = itemInsights[item.key];
      const categories = insight?.selectedCats || [];
      return categories.length > 0 &&
        !insight?.needsConflictAttention &&
        !insight?.needsKnowledgeConfirmation;
    });
    const visibleConflictItems = visibleActionableItems.filter((item) => {
      const insight = itemInsights[item.key];
      return Array.isArray(insight?.reviewConflictEntries) && insight.reviewConflictEntries.length > 0;
    });
    const visibleKnowledgeConfirmationItems = visibleActionableItems.filter((item) => itemInsights[item.key]?.needsKnowledgeConfirmation);
    const visibleExpandedCount = visibleActionableItems.filter((item) => expandedReviewItems[item.key]).length;
    const categoryOrder = ['features', 'classFeatures', 'feats', 'speciesTraits', 'actions', 'bonusActions', 'reactions', 'equipment', 'notes'];
    const visibleCategoryCounts = visibleActionableItems.reduce((acc, item) => {
      const categories = itemInsights[item.key]?.selectedCats || [];
      categories.forEach((category) => {
        acc[category] = (acc[category] || 0) + 1;
      });
      return acc;
    }, {});
    const visibleCategorySummary = Object.entries(visibleCategoryCounts)
      .sort((left, right) => {
        const leftIndex = categoryOrder.indexOf(left[0]);
        const rightIndex = categoryOrder.indexOf(right[0]);
        const safeLeft = leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex;
        const safeRight = rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex;
        return safeLeft - safeRight;
      });
    const visibleNeedsCategoryCount = visibleActionableItems.filter((item) => itemInsights[item.key]?.needsCategorySelection).length;
    const visibleNeedsKnowledgeConfirmationCount = visibleActionableItems.filter((item) => itemInsights[item.key]?.needsKnowledgeConfirmation).length;
    const visibleNeedsConflictAttentionCount = visibleActionableItems.filter((item) => itemInsights[item.key]?.needsConflictAttention).length;
    const visibleBlockedApplyCount = visibleActionableItems.filter((item) => {
      const insight = itemInsights[item.key];
      const categories = insight?.selectedCats || [];
      return categories.length > 0 && (insight?.needsConflictAttention || insight?.needsKnowledgeConfirmation);
    }).length;
    const selectedMergeKeys = Object.keys(reviewMergeSelection).filter(key => reviewMergeSelection[key]);
    
    return (
      <div className="needs-review-section">
        <div className="needs-review-header">
          <h4>
            <span className="header-icon">💡</span>
            Review the remaining details
            <span className="field-count">
              {reviewFilterMode === 'all'
                ? `${actionableItems.length} remaining`
                : `${filteredActionableCount} shown`}
            </span>
            {appliedCount > 0 && <span className="applied-count">✓ {appliedCount} added</span>}
          </h4>
          <p className="review-intro">
            SmartPaste could not place these details with enough confidence. Pick where each one belongs, or leave it as notes for now.
          </p>
          <div className="review-filter-row">
            {reviewFilterOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`review-filter-btn ${reviewFilterMode === option.value ? 'active' : ''}`}
                onClick={() => setReviewFilterMode(option.value)}
                disabled={option.count === 0 && option.value !== 'all'}
              >
                {option.label}
                <span className="review-filter-count">{option.count}</span>
              </button>
            ))}
          </div>
          {visibleActionableItems.length > 0 && (
            <div className="review-bulk-actions">
              <label className="review-bulk-assign">
                <span className="review-bulk-label">Set a destination for the visible items</span>
                <select
                  value={reviewBulkCategory}
                  onChange={(e) => setReviewBulkCategory(e.target.value)}
                  className="review-bulk-select"
                  aria-label="Set visible review items to category"
                >
                  {bulkCategoryOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="review-bulk-btn"
                onClick={() => setCategoriesForReviewItems(visibleActionableItems, [reviewBulkCategory])}
              >
                Apply destination
              </button>
              <button
                type="button"
                className="review-bulk-btn"
                onClick={() => restoreSuggestedCategoriesForReviewItems(visibleActionableItems)}
              >
                Restore suggested destinations
              </button>
              {visibleKnowledgeConfirmationItems.length > 0 && (
                <button
                  type="button"
                  className="review-bulk-btn"
                  onClick={() => setKnowledgeConfirmationForReviewItems(visibleKnowledgeConfirmationItems, true)}
                >
                  Confirm visible inferred items
                </button>
              )}
              <button
                type="button"
                className="review-bulk-btn"
                onClick={() => setCategoriesForReviewItems(visibleActionableItems, [])}
              >
                Clear visible destinations
              </button>
              {visibleConflictItems.length > 0 && (
                <>
                  <label className="review-bulk-assign">
                    <span className="review-bulk-label">Choose how visible matches should be handled</span>
                    <select
                      value={reviewBulkConflictResolution}
                      onChange={(e) => setReviewBulkConflictResolution(e.target.value)}
                      className="review-bulk-select"
                      aria-label="Set visible review conflicts to resolution"
                    >
                      {conflictResolutionTargets.map((target) => (
                        <option key={target.value} value={target.value}>{target.label}</option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="review-bulk-btn"
                    onClick={() => setConflictResolutionForReviewItems(visibleConflictItems, reviewBulkConflictResolution)}
                  >
                    Apply match handling
                  </button>
                </>
              )}
              <button
                type="button"
                className="review-bulk-btn"
                onClick={() => setExpandedStateForItems(visibleActionableItems, true)}
                disabled={visibleExpandedCount === visibleActionableItems.length}
              >
                Expand visible
              </button>
              <button
                type="button"
                className="review-bulk-btn"
                onClick={() => setExpandedStateForItems(visibleActionableItems, false)}
                disabled={visibleExpandedCount === 0}
              >
                Collapse visible
              </button>
              <button
                type="button"
                className="review-bulk-btn danger"
                onClick={() => skipVisibleReviewItems(visibleActionableItems)}
              >
                Skip visible for now
              </button>
              <button
                type="button"
                className="review-bulk-btn primary"
                onClick={() => handleApplyVisibleReviewItems(actionableResolved)}
                disabled={actionableResolved.length === 0 || !hasConfirmedContentUse || isApplying}
              >
                Add all ready items
              </button>
              <button
                type="button"
                className="review-bulk-btn primary"
                onClick={() => handleApplyVisibleReviewItems(visibleResolvedItems)}
                disabled={visibleResolvedItems.length === 0 || !hasConfirmedContentUse || isApplying}
              >
                Add visible ready items
              </button>
            </div>
          )}
          {visibleActionableItems.length > 0 && (
            <div className="review-plan-summary">
              <span className="review-plan-label">Ready details</span>
              {visibleCategorySummary.map(([category, count]) => (
                <span key={category} className="review-meta-chip">
                  {count} {formatCatalogKindCountLabel(category, count)}
                </span>
              ))}
              {visibleResolvedItems.length > 0 && (
                <span className="review-meta-chip remembered-hint">
                  {visibleResolvedItems.length} ready to add
                </span>
              )}
              {visibleNeedsCategoryCount > 0 && (
                <span className="review-meta-chip conflict-hint same-name">
                  {visibleNeedsCategoryCount} need a section
                </span>
              )}
              {visibleNeedsKnowledgeConfirmationCount > 0 && (
                <span className="review-meta-chip knowledge-inferred">
                  {visibleNeedsKnowledgeConfirmationCount} need confirmation
                </span>
              )}
              {visibleConflictItems.length > 0 && (
                <span className="review-meta-chip conflict-hint">
                  {visibleConflictItems.length} with possible matches
                </span>
              )}
              {visibleNeedsConflictAttentionCount > 0 && (
                <span className="review-meta-chip remembered-hint">
                  {visibleNeedsConflictAttentionCount} need match review
                </span>
              )}
              {visibleBlockedApplyCount > 0 && (
                <span className="review-meta-chip conflict-hint exact">
                  {visibleBlockedApplyCount} still need your input before they can be added
                </span>
              )}
            </div>
          )}
        </div>
        
        <div className="review-items-list">
          {groupEntries.length === 0 && (
            <div className="review-empty-state">
              {reviewFilterMode === 'attention'
                ? 'Everything in this view is already resolved.'
                : reviewFilterMode === 'ready'
                  ? 'Nothing in this view is ready to add yet.'
                : reviewFilterMode === 'needsCategory'
                  ? 'Everything in this view already has a destination.'
                  : reviewFilterMode === 'needsConfirmation'
                    ? 'Nothing in this view still needs confirmation.'
                    : reviewFilterMode === 'needsConflict'
                    ? 'Nothing in this view still needs a match choice.'
                : reviewFilterMode === 'conflicts'
                  ? 'No possible matches are visible right now.'
                  : 'No likely updates are visible right now.'}
            </div>
          )}
          {groupEntries.map(([groupKey, group]) => {
            const groupItems = [...group.items].sort((left, right) => {
              const leftDecision = uncategorizedDecisions[left.key];
              const rightDecision = uncategorizedDecisions[right.key];
              const leftApplied = leftDecision === 'applied' || leftDecision === 'skipped';
              const rightApplied = rightDecision === 'applied' || rightDecision === 'skipped';
              if (leftApplied !== rightApplied) return leftApplied ? 1 : -1;

              const leftInsight = itemInsights[left.key] || { sortPriority: 0 };
              const rightInsight = itemInsights[right.key] || { sortPriority: 0 };
              if (rightInsight.sortPriority !== leftInsight.sortPriority) {
                return rightInsight.sortPriority - leftInsight.sortPriority;
              }

              return (left.name || '').localeCompare(right.name || '');
            });
            const groupActionable = groupItems.filter(item => 
              uncategorizedDecisions[item.key] !== 'applied' && 
              uncategorizedDecisions[item.key] !== 'skipped'
            );
            const exactConflictCount = groupActionable.filter((item) => itemInsights[item.key]?.primaryMatchType === 'exact').length;
            const sameNameConflictCount = groupActionable.filter((item) => itemInsights[item.key]?.primaryMatchType === 'sameName').length;
            const knowledgeConfirmationCount = groupActionable.filter((item) => itemInsights[item.key]?.needsKnowledgeConfirmation).length;
            const groupSelectedKeys = groupItems
              .filter(item => selectedMergeKeys.includes(item.key) && uncategorizedDecisions[item.key] !== 'applied' && uncategorizedDecisions[item.key] !== 'skipped')
              .map(item => item.key);
            
            return (
              <div key={groupKey} className="review-group">
                <div className="review-group-header">
                  <h5>
                    {group.label}
                    <span className="group-count">{groupActionable.length} to consider</span>
                  </h5>
                  {(exactConflictCount > 0 || sameNameConflictCount > 0 || knowledgeConfirmationCount > 0) && (
                    <div className="review-group-summary">
                      {knowledgeConfirmationCount > 0 && (
                        <span className="review-meta-chip knowledge-inferred">
                          {knowledgeConfirmationCount} need confirmation
                        </span>
                      )}
                      {exactConflictCount > 0 && (
                        <span className="review-meta-chip conflict-hint exact">
                          {exactConflictCount} likely update an existing entry
                        </span>
                      )}
                      {sameNameConflictCount > 0 && (
                        <span className="review-meta-chip conflict-hint same-name">
                          {sameNameConflictCount} may match something you already have
                        </span>
                      )}
                    </div>
                  )}
                  {groupSelectedKeys.length >= 2 && (
                    <button
                      className="merge-group-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        mergeSelectedItems(groupSelectedKeys, groupItems);
                      }}
                    >
                      Combine {groupSelectedKeys.length} details
                    </button>
                  )}
                </div>
                {groupItems.map((item) => {
                  const decision = uncategorizedDecisions[item.key];
                  const isExpanded = expandedReviewItems[item.key];
                  const isApplied = decision === 'applied';
                  const isSkipped = decision === 'skipped';
                  const sourceLabel = formatReviewSource(item);
                  const locationLabel = formatReviewLocation(item);
                  const confidenceLabel = formatReviewConfidence(item);
                  const knowledgeMeta = getItemKnowledgeMeta(item);
                  const insight = itemInsights[item.key] || buildReviewItemInsight(item);
                  const {
                    selectedCats,
                    rememberedCats,
                    needsKnowledgeConfirmation,
                    knowledgeConfirmedInSession,
                    knowledgeConfirmedFromMemory,
                    reviewConflictEntries,
                    reviewConflictHints,
                    hasRememberedReviewConflictChoice,
                    hasRememberedReviewConflictTarget
                  } = insight;
                  const knowledgeStatusMeta = getReviewKnowledgeStatusMeta(insight);
                  const reviewPreview = buildReviewOutcomePreview(item, selectedCats);

                  if (isApplied) {
                    return (
                      <div key={item.key} className="review-item applied">
                        <div className="review-item-content">
                          <span className="applied-check">✓</span>
                          <span className="review-item-name">{item.name}</span>
                          <span className="applied-label">Added!</span>
                        </div>
                      </div>
                    );
                  }

                  if (isSkipped) {
                    return (
                      <div key={item.key} className="review-item applied skipped">
                        <div className="review-item-content">
                          <span className="applied-check">↷</span>
                          <span className="review-item-name">{item.name}</span>
                          <span className="applied-label">Skipped</span>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={item.key} className={`review-item actionable ${isExpanded ? 'expanded' : ''}`}>
                      <div className="review-item-header" onClick={() => toggleExpandReviewItem(item.key, item)}>
                        <div
                          className="merge-select"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={!!reviewMergeSelection[item.key]}
                            onChange={() => toggleMergeSelection(item.key)}
                            aria-label="Select extra for merge"
                          />
                        </div>
                        <div className="review-item-content">
                          <div className="review-item-name">{reviewPreview.title}</div>
                          <div className="review-item-outcome">
                            SmartPaste thinks this becomes: <strong>{reviewPreview.destinationLabel}</strong>
                          </div>
                          {reviewPreview.summary && (
                            <div className="review-item-desc" title={reviewPreview.summary}>
                              {reviewPreview.summary}
                            </div>
                          )}
                          {reviewPreview.detailChips.length > 0 && (
                            <div className="review-item-preview-chips">
                              {reviewPreview.detailChips.map((chip) => (
                                <span key={`${item.key}-${chip}`} className="review-meta-chip preview-chip">
                                  {chip}
                                </span>
                              ))}
                            </div>
                          )}
                          {(knowledgeMeta || sourceLabel || locationLabel || confidenceLabel) && (
                            <div className="review-item-meta">
                              {knowledgeMeta && (
                                <span
                                  className={`review-meta-chip ${knowledgeMeta.className}`}
                                  title={knowledgeMeta.note || knowledgeMeta.title}
                                >
                                  {knowledgeMeta.label}
                                </span>
                              )}
                              {sourceLabel && <span className="review-meta-chip">{sourceLabel}</span>}
                              {locationLabel && <span className="review-meta-chip">{locationLabel}</span>}
                              {confidenceLabel && <span className="review-meta-chip">{confidenceLabel}</span>}
                            </div>
                          )}
                          {(knowledgeStatusMeta || reviewConflictHints.length > 0 || hasRememberedReviewConflictChoice || hasRememberedReviewConflictTarget) && (
                            <div className="review-item-hints">
                              {knowledgeStatusMeta && (
                                <span
                                  className={`review-meta-chip ${knowledgeStatusMeta.className}`}
                                  title={knowledgeStatusMeta.title}
                                >
                                  {knowledgeStatusMeta.label}
                                </span>
                              )}
                              {reviewConflictHints.map((hint) => (
                                <span
                                  key={hint.key}
                                  className={`review-meta-chip conflict-hint ${hint.isExact ? 'exact' : 'same-name'}`}
                                  title={hint.title}
                                >
                                  {hint.summary}
                                </span>
                              ))}
                              {hasRememberedReviewConflictChoice && (
                                <span
                                  className="review-meta-chip remembered-hint"
                                  title="SmartPaste remembered how you handled this match earlier."
                                >
                                  Remembered choice
                                </span>
                              )}
                              {!hasRememberedReviewConflictChoice && hasRememberedReviewConflictTarget && (
                                <span
                                  className="review-meta-chip remembered-hint"
                                  title="SmartPaste remembered which existing entry you targeted before."
                                >
                                  Remembered target
                                </span>
                              )}
                            </div>
                          )}
                          {item.isSectionFragment && (
                            <div className="review-item-hints">
                              <span
                                className="review-meta-chip remembered-hint"
                                title="This item represents leftover lines from a section that SmartPaste partially parsed."
                              >
                                Unplaced detail
                              </span>
                            </div>
                          )}
                        </div>
                        <button className="expand-toggle" aria-label={isExpanded ? 'Collapse' : 'Expand'}>
                          {isExpanded ? '▼' : '▶'}
                        </button>
                      </div>
                      
                      {isExpanded && (
                        <div className="review-item-actions">
                          {item.reviewReason && (
                            <div className="review-item-context">
                              <span className="context-label">Why it needs review:</span>
                              <span className="context-value">{item.reviewReason}</span>
                            </div>
                          )}
                          {knowledgeMeta && (
                            <div className="review-item-context">
                              <span className="context-label">Knowledge source:</span>
                              <span className="context-value">
                                {knowledgeMeta.label}
                                {knowledgeMeta.note ? ` - ${knowledgeMeta.note}` : ''}
                              </span>
                            </div>
                          )}
                          {(needsKnowledgeConfirmation || knowledgeConfirmedInSession || knowledgeConfirmedFromMemory) && (
                            <div className="review-item-context">
                              <span className="context-label">Review status:</span>
                              <span className="context-value">
                                {needsKnowledgeConfirmation
                                  ? 'This inferred placement needs a human confirmation before it becomes ready to apply.'
                                  : knowledgeConfirmedInSession
                                    ? 'Confirmed in this SmartPaste session.'
                                    : 'Using a remembered review decision for this character.'}
                              </span>
                            </div>
                          )}
                          {item.isSectionFragment && (
                            <div className="review-item-context">
                              <span className="context-label">Unplaced detail from:</span>
                              <span className="context-value">
                                {item.fragmentCategory ? formatCatalogKindLabel(item.fragmentCategory) : 'Unknown section'}
                                {item.fragmentHeader ? ` (${item.fragmentHeader})` : ''}
                              </span>
                            </div>
                          )}
                          {rememberedCats.length > 0 && (
                            <div className="review-item-context">
                              <span className="context-label">Remembered categories:</span>
                              <span className="context-value">{rememberedCats.map(formatCatalogKindLabel).join(', ')}</span>
                            </div>
                          )}
                          {Array.isArray(item.relatedCatalogEntries) && item.relatedCatalogEntries.length > 0 && (
                            <div className="review-item-context">
                              <span className="context-label">Related parsed entries:</span>
                              <span className="context-value">
                                {item.relatedCatalogEntries.map((entry, index) => (
                                  <span key={entry.id || `${entry.kind}-${entry.name}-${index}`}>
                                    {index > 0 ? ', ' : ''}
                                    {entry.name} ({formatCatalogKindLabel(entry.kind)})
                                  </span>
                                ))}
                              </span>
                            </div>
                          )}
                          {item.suggestedType && item.suggestedType !== 'notes' && (
                            <div className="suggested-category-badge">
                              <span className="badge-label">Suggested destination:</span>
                              <span className="badge-value">{{
                                abilities: '💪 Ability Scores',
                                savingThrows: '🛡️ Saving Throws',
                                skills: '🎯 Skills',
                                spells: '✨ Spells',
                                equipment: '🎒 Equipment',
                                features: '⚔️ Feature / Ability',
                                feats: '⚔️ Feat',
                                actions: '🎯 Action',
                                traits: '🧬 Trait',
                                notes: '📝 Notes'
                              }[item.suggestedType] || item.suggestedType}</span>
                              {!selectedCats.length && (
                                <button
                                  className="accept-suggestion-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCategorySelection(item.key, item.suggestedType);
                                  }}
                                >
                                  Yes, looks right
                                </button>
                              )}
                            </div>
                          )}
                          <p className="action-prompt">
                            {item.suggestedType && item.suggestedType !== 'notes'
                              ? 'If not, choose a different destination:'
                              : 'Choose where this belongs:'}
                          </p>
                          <div className="category-checkboxes">
                            <label className={`category-checkbox ${selectedCats.includes('abilities') ? 'selected' : ''}`}>
                              <input 
                                type="checkbox" 
                                checked={selectedCats.includes('abilities')}
                                onChange={() => toggleCategorySelection(item.key, 'abilities')}
                              />
                              <span>💪 Ability Scores</span>
                            </label>
                            <label className={`category-checkbox ${selectedCats.includes('savingThrows') ? 'selected' : ''}`}>
                              <input 
                                type="checkbox" 
                                checked={selectedCats.includes('savingThrows')}
                                onChange={() => toggleCategorySelection(item.key, 'savingThrows')}
                              />
                              <span>🛡️ Saving Throws</span>
                            </label>
                            <label className={`category-checkbox ${selectedCats.includes('skills') ? 'selected' : ''}`}>
                              <input 
                                type="checkbox" 
                                checked={selectedCats.includes('skills')}
                                onChange={() => toggleCategorySelection(item.key, 'skills')}
                              />
                              <span>🎯 Skills</span>
                            </label>
                            <label className={`category-checkbox ${selectedCats.includes('spells') ? 'selected' : ''}`}>
                              <input 
                                type="checkbox" 
                                checked={selectedCats.includes('spells')}
                                onChange={() => toggleCategorySelection(item.key, 'spells')}
                              />
                              <span>✨ Spells</span>
                            </label>
                            <label className={`category-checkbox ${selectedCats.includes('features') ? 'selected' : ''}`}>
                              <input 
                                type="checkbox" 
                                checked={selectedCats.includes('features')}
                                onChange={() => toggleCategorySelection(item.key, 'features')}
                              />
                              <span>⚔️ Feature / Ability</span>
                            </label>
                            <label className={`category-checkbox ${selectedCats.includes('actions') ? 'selected' : ''}`}>
                              <input 
                                type="checkbox" 
                                checked={selectedCats.includes('actions')}
                                onChange={() => toggleCategorySelection(item.key, 'actions')}
                              />
                              <span>🎯 Action</span>
                            </label>
                            <label className={`category-checkbox ${selectedCats.includes('bonusActions') ? 'selected' : ''}`}>
                              <input 
                                type="checkbox" 
                                checked={selectedCats.includes('bonusActions')}
                                onChange={() => toggleCategorySelection(item.key, 'bonusActions')}
                              />
                              <span>⚡ Bonus Action</span>
                            </label>
                            <label className={`category-checkbox ${selectedCats.includes('reactions') ? 'selected' : ''}`}>
                              <input 
                                type="checkbox" 
                                checked={selectedCats.includes('reactions')}
                                onChange={() => toggleCategorySelection(item.key, 'reactions')}
                              />
                              <span>🛡️ Reaction</span>
                            </label>
                            <label className={`category-checkbox ${selectedCats.includes('feats') ? 'selected' : ''}`}>
                              <input 
                                type="checkbox" 
                                checked={selectedCats.includes('feats')}
                                onChange={() => toggleCategorySelection(item.key, 'feats')}
                              />
                              <span>⭐ Feat</span>
                            </label>
                            <label className={`category-checkbox ${selectedCats.includes('equipment') ? 'selected' : ''}`}>
                              <input 
                                type="checkbox" 
                                checked={selectedCats.includes('equipment')}
                                onChange={() => toggleCategorySelection(item.key, 'equipment')}
                              />
                              <span>🎒 Equipment</span>
                            </label>
                            <label className={`category-checkbox ${selectedCats.includes('speciesTraits') ? 'selected' : ''}`}>
                              <input 
                                type="checkbox" 
                                checked={selectedCats.includes('speciesTraits')}
                                onChange={() => toggleCategorySelection(item.key, 'speciesTraits')}
                              />
                              <span>🧬 Species Trait</span>
                            </label>
                            <label className={`category-checkbox ${selectedCats.includes('notes') ? 'selected' : ''}`}>
                              <input 
                                type="checkbox" 
                                checked={selectedCats.includes('notes')}
                                onChange={() => toggleCategorySelection(item.key, 'notes')}
                              />
                              <span>📝 Notes</span>
                            </label>
                          </div>
                          {reviewPreview.rawSourceText && (
                            <div className="review-item-context">
                              <span className="context-label">Pasted text:</span>
                              <pre className="review-raw-text">{reviewPreview.rawSourceText}</pre>
                            </div>
                          )}
                          {reviewConflictEntries.length > 0 && (
                            <div className="review-item-context">
                              <span className="context-label">Existing matches:</span>
                              <div className="context-value" style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                                {reviewConflictEntries.map((entry) => (
                                  <div key={`${item.key}-${entry.category}`} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
                                    <span style={{ fontWeight: 600 }}>{formatCatalogKindLabel(entry.category)}:</span>
                                    <span style={{ color: '#8a6116' }} title={entry.conflicts.map(conflict => `${conflict.name}: ${conflict.description || 'Existing entry'}`).join('\n')}>
                                      {entry.conflicts[0].matchType === 'exact' ? 'Exact existing match' : 'Same name already exists'}:
                                      {' '}
                                      {entry.conflicts[0].name}
                                      {entry.conflicts.length > 1 ? ` (+${entry.conflicts.length - 1} more)` : ''}
                                    </span>
                                    {entry.conflicts.length > 1 && (
                                      <select
                                        value={entry.conflictTargetValue}
                                        onChange={(e) => handleReviewConflictTargetChange(item.key, entry.category, e.target.value)}
                                        style={{ fontSize: '0.85em', padding: '1px 4px', maxWidth: '240px' }}
                                        title="Choose which existing entry SmartPaste should target"
                                      >
                                        {entry.conflicts.map((conflict) => {
                                          const descSnippet = conflict.description
                                            ? ` - ${conflict.description.substring(0, 36)}${conflict.description.length > 36 ? '...' : ''}`
                                            : '';
                                          return (
                                            <option key={conflict.fingerprint || `${conflict.name}-${conflict.index}`} value={conflict.fingerprint || ''}>
                                              {`${conflict.name}${descSnippet}`}
                                            </option>
                                          );
                                        })}
                                      </select>
                                    )}
                                    <select
                                      value={entry.conflictResolution}
                                      onChange={(e) => handleReviewConflictResolutionChange(item.key, entry.category, e.target.value)}
                                      style={{ fontSize: '0.85em', padding: '1px 4px', maxWidth: '170px' }}
                                      title={conflictResolutionTargets.find(target => target.value === entry.conflictResolution)?.description || 'Choose what to do with this match'}
                                    >
                                      {conflictResolutionTargets.map(target => (
                                        <option key={target.value} value={target.value}>{target.label}</option>
                                      ))}
                                    </select>
                                    {entry.hasRememberedConflictChoice && (
                                      <span style={{ color: '#8a6116' }} title="SmartPaste remembered this choice from earlier.">
                                        remembered
                                      </span>
                                    )}
                                    {!entry.hasRememberedConflictChoice && entry.hasRememberedConflictTarget && (
                                      <span style={{ color: '#8a6116' }} title="SmartPaste remembered which existing entry you targeted before.">
                                        match remembered
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="review-action-buttons">
                            {looksLikeMultipleItems(item.description) && (
                              <button 
                                className="separate-btn" 
                                onClick={() => separateTextBlock(item.key, item)}
                              >
                                Split into smaller items
                              </button>
                            )}
                            {needsKnowledgeConfirmation && (
                              <button
                                className="accept-suggestion-btn"
                                onClick={() => setKnowledgeConfirmationForReviewItems(item.key, true)}
                              >
                                Yes, use this
                              </button>
                            )}
                            <button className="skip-btn" onClick={() => skipUncategorizedItem(item.key)}>
                              Ignore for now
                            </button>
                          </div>
                          {selectedCats.length > 0 && (
                            <div className="category-status">
                              ✓ Will be added to: <strong>{selectedCats.map(formatCatalogKindLabel).join(', ')}</strong>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderExamplesPanel = (forceVisible = false) => {
    if (!showExamples && !forceVisible) return null;

    return (
      <div className="examples-panel">
        <div className="examples-header">
          <h4>📚 Example Formats</h4>
          <button className="close-btn" onClick={() => setShowExamples(false)}>×</button>
        </div>
        <p className="examples-intro">Click an example to try it out:</p>
        <div className="examples-list">
          {exampleFormats.map((ex, idx) => (
            <div key={idx} className="example-card">
              <div className="example-header">
                <span className="example-icon">{ex.icon}</span>
                <span className="example-label">{ex.label}</span>
                <button className="use-btn" onClick={() => applyExampleTextFromPanel(ex.example)}>Use This</button>
              </div>
              <pre className="example-code">{ex.example}</pre>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderContentPreview = () => {
    if (!parsedContent) return null;

    const { type, data, confidence, warnings = [], validation } = parsedContent;
    const validationErrors = Array.from(new Set(Array.isArray(validation?.errors) ? validation.errors.filter(Boolean) : []));
    const validationWarnings = Array.from(new Set(
      (Array.isArray(warnings) ? warnings : [])
        .filter(Boolean)
        .filter((warning) => !validationErrors.includes(warning))
    ));
    const validationSuggestions = Array.from(new Set(Array.isArray(validation?.suggestions) ? validation.suggestions.filter(Boolean) : []));
    const summaryBits = [];

    if (type === 'character' || type === 'monster') {
      if (data?.name) summaryBits.push('name');
      if (data?.level) summaryBits.push('level');
      if (data?.class) summaryBits.push('class');
      if (data?.ac) summaryBits.push('armor class');
      if (data?.abilities && Object.keys(data.abilities).length > 0) summaryBits.push('ability scores');
      if (data?.savingThrows && Object.keys(data.savingThrows).length > 0) summaryBits.push('saving throws');
      if (Array.isArray(data?.skills) && data.skills.length > 0) summaryBits.push('skills');
      if (data?.speed) summaryBits.push('speed');
      if (data?.hp || data?.maxHp) summaryBits.push('hit points');
      if (data?.spells && (Object.keys(data.spells?.byLevel || {}).length > 0 || (data.spells?.prepared || []).length > 0 || (data.spells?.known || []).length > 0)) summaryBits.push('spells');
      if ((data?.classFeatures || []).length > 0 || (data?.features || []).length > 0 || (data?.feats || []).length > 0 || (data?.speciesTraits || []).length > 0) summaryBits.push('features');
    }
    const summaryTitle = type === 'character'
      ? 'SmartPaste found your character'
      : type === 'monster'
        ? 'SmartPaste found this stat block'
        : `SmartPaste found this ${type}`;
    const summaryBody = summaryBits.length > 0
      ? `SmartPaste found ${summaryBits.join(', ')}${validationErrors.length > 0 ? '. A couple of details still need your help before the character is complete.' : '.'}`
      : 'SmartPaste found details to review. Choose where they belong on the character sheet.';

    return (
      <div className="content-preview">
        <div className="content-header">
          <div className="content-type">
            <span className="type-icon">{renderTypeIcon(type)}</span>
            <span className="type-name">
              {type === 'character'
                ? 'Character Details Ready'
                : type === 'monster'
                  ? 'Stat Block Ready For Review'
                  : `${type.charAt(0).toUpperCase() + type.slice(1)} Ready For Review`}
            </span>
          </div>
          <div className="confidence-section" title={getConfidenceExplanation(confidence)}>
            <span className={`confidence-badge ${getConfidenceClass(confidence)}`}>
              {getConfidenceLabel(confidence)}
            </span>
            <span className="confidence-hint">{getConfidenceExplanation(confidence)}</span>
          </div>
        </div>

        <div className="validation-section">
          <div className="validation-suggestions">
            <h5>✨ {summaryTitle}</h5>
            <ul>
              <li className="suggestion-item">{summaryBody}</li>
            </ul>
          </div>
        </div>

        {(validationWarnings.length > 0 || validationErrors.length > 0 || validationSuggestions.length > 0) && (
          <div className="validation-section">
            {validationErrors.length > 0 && (
              <div className="validation-errors">
                <h5>⚠️ Still needed for the full character</h5>
                <ul>
                  {validationErrors.map((error, index) => (
                    <li key={index} className="error-item">{error}</li>
                  ))}
                </ul>
              </div>
            )}
            {validationWarnings.length > 0 && (
              <div className="validation-warnings">
                <h5>💡 Worth reviewing</h5>
                <ul>
                  {validationWarnings.map((warning, index) => (
                    <li key={index} className="warning-item">{warning}</li>
                  ))}
                </ul>
              </div>
            )}
            {validationSuggestions.length > 0 && (
              <div className="validation-suggestions">
                <h5>🛠️ Good next steps</h5>
                <ul>
                  {validationSuggestions.map((suggestion, index) => (
                    <li key={index} className="suggestion-item">{suggestion}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="content-details">
          {type === 'spell' && (
            <div className="spell-preview">
              <h4>{data.name}</h4>
              <div className="spell-meta">
                <span>Level {data.level}</span>
                {data.school && <span>{data.school}</span>}
                {data.castingTime && <span>{data.castingTime}</span>}
              </div>
              <p className="description">{data.description}</p>
            </div>
          )}

          {type === 'action' && (
            <div className="action-preview">
              <h4>{data.name}</h4>
              <div className="action-meta">
                <span>{data.attackType} attack</span>
                {data.attackBonus && <span>+{data.attackBonus} to hit</span>}
                {data.reach && <span>Reach {data.reach} ft.</span>}
                {data.damage && <span>{data.damage} damage</span>}
              </div>
              <p className="description">{data.description}</p>
            </div>
          )}

          {type === 'item' && (
            <div className="item-preview">
              <h4>{data.name}</h4>
              <div className="item-meta">
                <span>{data.type}</span>
                {data.rarity && <span>{data.rarity}</span>}
                {data.requiresAttunement && <span>Requires Attunement</span>}
                {data.acBonus && <span>AC +{data.acBonus}</span>}
                {data.damage && <span>{data.damage} {data.damageType}</span>}
              </div>
              <p className="description">{data.description}</p>
            </div>
          )}

          {type === 'feature' && (
            <div className="feature-preview">
              <h4>{data.name}</h4>
              <div className="feature-meta">
                {data.usage && <span>{data.usage}</span>}
                {data.recharge && <span>{data.recharge}</span>}
              </div>
              <p className="description">{data.description}</p>
            </div>
          )}

          {(type === 'character' || type === 'monster') && (
            <div className="statblock-preview">
              <h4>{data.name}</h4>
              <div className="statblock-meta">
                {data.level && <span>Level {data.level}</span>}
                {data.cr && <span>CR {data.cr}</span>}
                {data.ac && (
                  <span>
                    AC {typeof data.ac === 'object' ? ((data.ac.value ?? '') + (data.ac.notes ? ` (${data.ac.notes})` : '')) : data.ac}
                  </span>
                )}
                {data.hp && (
                  <span>
                    HP {typeof data.hp === 'object' ? (data.maxHp ?? data.hp.average ?? data.hp.current ?? data.hp.value ?? '') : data.hp}
                  </span>
                )}
              </div>
              {data.abilities && (
                <div className="abilities-grid">
                  {Object.entries(data.abilities).map(([ability, score]) => (
                    <div key={ability} className="ability-score">
                      <span className="ability-name">{ability.slice(0, 3).toUpperCase()}</span>
                      <span className="ability-value">{score}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {(type === 'ability' || type === 'abilities') && (
            <div className="abilities-preview">
              <h4>Ability Scores</h4>
              <div className="abilities-grid">
                {Object.entries(data).map(([ability, score]) => (
                  <div key={ability} className="ability-score">
                    <span className="ability-name">{ability.toUpperCase()}</span>
                    <span className="ability-value">{score}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {type === 'list' && (
            <div className="list-preview">
              <h4>Items ({(data.items || []).length})</h4>
              <div className="items-list">
                {(data.items || []).map((item, index) => (
                  <span key={index} className="list-item">{item}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const reassignableFieldSet = new Set(['equipment', 'features', 'classFeatures', 'feats', 'speciesTraits', 'actions', 'bonusActions', 'reactions']);
  const reassignmentTargets = [
    { value: '', label: '(keep as-is)' },
    { value: SMART_PASTE_SKIP_REASSIGNMENT, label: 'Skip this item' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'classFeatures', label: 'Class Feature' },
    { value: 'features', label: 'Feature' },
    { value: 'feats', label: 'Feat' },
    { value: 'speciesTraits', label: 'Species Trait' },
    { value: 'actions', label: 'Action' },
    { value: 'bonusActions', label: 'Bonus Action' },
    { value: 'reactions', label: 'Reaction' },
  ];

  const handleReassignItem = (suggestionIdx, itemIdx, newField) => {
    setSuggestionReassignments(prev => {
      const current = { ...(prev[suggestionIdx] || {}) };
      if (!newField) {
        delete current[itemIdx];
      } else {
        current[itemIdx] = newField;
      }
      return { ...prev, [suggestionIdx]: current };
    });
    confirmSuggestionSelection(suggestionIdx);
  };

  const getScalarDestinationOptions = (field) => {
    const groupKey = getCharacterGroupKeyForField(field);
    const group = characterPrimaryGroups.find((entry) => entry.key === groupKey);
    if (!group) return [{ value: field, label: formatCharacterFieldLabel(field) }];

    const seenFields = new Set();
    return group.fields
      .map((candidate) => normalizeCharacterReviewField(candidate))
      .filter((candidate) => editableScalarFieldSet.has(candidate) || candidate === 'abilities')
      .filter((candidate) => {
        if (seenFields.has(candidate)) return false;
        seenFields.add(candidate);
        return true;
      })
      .map((candidate) => ({ value: candidate, label: formatCharacterFieldLabel(candidate) }));
  };

  const selectSuggestionAfterEdit = (suggestionIdx) => {
    if (appliedSuggestionIndexes.includes(suggestionIdx)) return;
    setSelectedSuggestionIndexes((prev) => {
      if (prev.includes(suggestionIdx)) return prev;
      const next = [...prev.filter((idx) => !isStandaloneImportSuggestion(suggestions[idx])), suggestionIdx];
      syncLegacySuggestionSelection(next);
      return next;
    });
    confirmSuggestionSelection(suggestionIdx);
  };

  const handleSuggestionFieldOverride = (suggestionIdx, field) => {
    const originalField = normalizeCharacterReviewField(suggestions[suggestionIdx]?.field);
    setSuggestionFieldOverrides((prev) => {
      const next = { ...prev };
      if (!field || field === originalField) {
        delete next[suggestionIdx];
      } else {
        next[suggestionIdx] = field;
      }
      return next;
    });
    selectSuggestionAfterEdit(suggestionIdx);
  };

  const handleSuggestionDataOverride = (suggestionIdx, value) => {
    setSuggestionDataOverrides((prev) => ({ ...prev, [suggestionIdx]: value }));
    selectSuggestionAfterEdit(suggestionIdx);
  };

  const handleScalarSuggestionValueChange = (suggestionIdx, field, value) => {
    const nextValue = numericCharacterFieldSet.has(field) && value !== '' ? Number(value) : value;
    handleSuggestionDataOverride(suggestionIdx, nextValue);
  };

  const handleAbilityScoreChange = (suggestionIdx, ability, value, currentData) => {
    handleSuggestionDataOverride(suggestionIdx, {
      ...(currentData && typeof currentData === 'object' ? currentData : {}),
      [ability]: value === '' ? '' : Number(value)
    });
  };

  const conflictResolutionTargets = [
    { value: 'merge', label: 'Combine details', description: 'Update the existing entry with these details.' },
    { value: 'replace', label: 'Replace existing', description: 'Use the pasted version instead of the existing one.' },
    { value: 'keepBoth', label: 'Keep both', description: 'Add this as a separate entry even if a match exists.' }
  ];

  const getRememberedSuggestionConflictResolution = (item, field, conflicts) => (
    getRememberedConflictResolutionForField(item, field, conflicts)
  );

  const getRememberedSuggestionConflictTarget = (item, field, conflicts) => (
    getRememberedConflictTargetForField(item, field, conflicts)
  );

  const getConflictResolutionValue = (suggestionIdx, itemIdx, item, field, conflicts) => (
    suggestionConflictResolutions?.[suggestionIdx]?.[itemIdx] ||
    getRememberedSuggestionConflictResolution(item, field, conflicts) ||
    getDefaultConflictResolution(conflicts)
  );

  const getConflictTargetValue = (suggestionIdx, itemIdx, item, field, conflicts) => {
    if (!Array.isArray(conflicts) || conflicts.length === 0) return '';

    const explicitTarget = suggestionConflictTargets?.[suggestionIdx]?.[itemIdx];
    if (explicitTarget && conflicts.some(conflict => conflict.fingerprint === explicitTarget)) {
      return explicitTarget;
    }

    const rememberedTarget = getRememberedSuggestionConflictTarget(item, field, conflicts);
    if (rememberedTarget && conflicts.some(conflict => conflict.fingerprint === rememberedTarget)) {
      return rememberedTarget;
    }

    return conflicts[0]?.fingerprint || '';
  };

  const handleConflictResolutionChange = (suggestionIdx, itemIdx, value) => {
    setSuggestionConflictResolutions(prev => ({
      ...prev,
      [suggestionIdx]: {
        ...(prev[suggestionIdx] || {}),
        [itemIdx]: value
      }
    }));
    confirmSuggestionSelection(suggestionIdx);
  };

  const handleConflictTargetChange = (suggestionIdx, itemIdx, value) => {
    setSuggestionConflictTargets(prev => ({
      ...prev,
      [suggestionIdx]: {
        ...(prev[suggestionIdx] || {}),
        [itemIdx]: value
      }
    }));
    confirmSuggestionSelection(suggestionIdx);
  };

  const handleReviewConflictResolutionChange = (itemKey, category, value) => {
    const normalizedCategory = normalizeImportField(category);
    setReviewConflictResolutions(prev => ({
      ...prev,
      [itemKey]: {
        ...(prev[itemKey] || {}),
        [normalizedCategory]: value
      }
    }));
    setKnowledgeConfirmationForReviewItems(itemKey, true);
  };

  const handleReviewConflictTargetChange = (itemKey, category, value) => {
    const normalizedCategory = normalizeImportField(category);
    setReviewConflictTargets(prev => ({
      ...prev,
      [itemKey]: {
        ...(prev[itemKey] || {}),
        [normalizedCategory]: value
      }
    }));
    setKnowledgeConfirmationForReviewItems(itemKey, true);
  };

  // date_of_change: 2026-05-03
  // rule_reference: D&D Character Data Governance Standards - deduplicate identical suggestions
  const deduplicateSuggestions = (suggestions) => {
    const seen = new Set();
    return suggestions.filter(s => {
      // Create unique key from field + data fingerprint
      const dataKey = typeof s.data === 'object' 
        ? JSON.stringify(s.data).slice(0, 200) 
        : String(s.data);
      const key = `${s.field}-${dataKey}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // date_of_change: 2026-05-03
  // rule_reference: Category icon mapping for visual grouping
  const getSuggestionCategoryIcon = (field) => {
    const iconMap = {
      name: '👤',
      class: '👤',
      subclass: '👤',
      level: '👤',
      race: '👤',
      species: '👤',
      background: '👤',
      alignment: '👤',
      equipment: '🎒',
      weapons: '⚔️',
      armor: '🛡️',
      items: '🎒',
      actions: '⚔️',
      bonusActions: '⚡',
      reactions: '🔄',
      spells: '✨',
      features: '📜',
      feats: '📜',
      traits: '📜',
      languages: '🗣️',
      hp: '❤️',
      maxHp: '❤️',
      hitDice: '🎲',
      ac: '🛡️',
      speed: '👟',
      abilities: '💪',
      proficiencyBonus: '⭐',
      savingThrows: '🎲'
    };
    return iconMap[field] || '📋';
  };

  const renderSuggestions = () => {
    if (!parsedContent) return null;
    if (!suggestions || suggestions.length === 0) return null;

    // Deduplicate suggestions to prevent "Set spells" appearing twice
    const uniqueSuggestions = deduplicateSuggestions(suggestions);

    return (
      <div className="placement-suggestions">
        <h4>
          <span className="suggestion-category-icon">📋</span>
          Choose how much to add
          {uniqueSuggestions.length < suggestions.length && (
            <span className="dedupe-notice">
              <span>🗑️</span>
              {suggestions.length - uniqueSuggestions.length} repeated detail(s) ignored
            </span>
          )}
        </h4>
        <div className="suggestions-list">
          {uniqueSuggestions.map((suggestion, index) => {
            const checked = selectedSuggestionIndexes.includes(index);
            const isApplied = appliedSuggestionIndexes.includes(index);
            const isStandaloneImport = isStandaloneImportSuggestion(suggestion);
            const standaloneDescription = getStandaloneImportDescription(suggestion);
            const isReassignable = reassignableFieldSet.has(suggestion.field) && Array.isArray(suggestion.data) && suggestion.data.length > 0;
            const reassignMap = suggestionReassignments[index] || {};
            const hasReassignments = Object.keys(reassignMap).length > 0;
            const suggestionKnowledgeMeta = getSuggestionKnowledgeMeta(suggestion);
            const suggestionKnowledgeInsight = getSuggestionKnowledgeInsight(suggestion, index);
            const suggestionKnowledgeStatusMeta = getSuggestionKnowledgeStatusMeta(suggestionKnowledgeInsight);
            const conflictMap = Array.isArray(suggestion.data)
              ? suggestion.data.map((item, itemIdx) => {
                  const targetField = reassignMap[itemIdx] || suggestion.field;
                  return getImportItemConflicts(item, targetField);
                })
              : [];
            const conflictCount = conflictMap.filter(conflicts => conflicts.length > 0).length;
            const hasConflictEdits = Object.keys(suggestionConflictResolutions[index] || {}).length > 0;
            const showItemControls = checked && Array.isArray(suggestion.data) && (isReassignable || conflictCount > 0);

            return (
              <div key={index} className="suggestion-option-block">
                <label className="suggestion-option">
                  <input
                    type={isStandaloneImport ? 'radio' : 'checkbox'}
                    name={isStandaloneImport ? 'smart-paste-import-plan' : `placement-${index}`}
                    checked={checked || isApplied}
                    onChange={() => handleSuggestionToggle(index)}
                    disabled={isApplied}
                  />
                  <span className="suggestion-text">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <span className="suggestion-category-icon">
                        {getSuggestionCategoryIcon(suggestion.field)}
                      </span>
                      {suggestion.preview}
                      {isApplied && <span className="reassign-badge"> Added!</span>}
                    </span>
                    {isStandaloneImport && <span className="reassign-badge"> (whole character)</span>}
                    {suggestionKnowledgeMeta && (
                      <span
                        className={`review-meta-chip suggestion-knowledge-chip ${suggestionKnowledgeMeta.className}`}
                        title={suggestionKnowledgeMeta.note || suggestionKnowledgeMeta.title}
                      >
                        {suggestionKnowledgeMeta.label}
                      </span>
                    )}
                    {suggestionKnowledgeStatusMeta && (
                      <span
                        className={`review-meta-chip suggestion-knowledge-chip ${suggestionKnowledgeStatusMeta.className}`}
                        title={suggestionKnowledgeStatusMeta.title}
                      >
                        {suggestionKnowledgeStatusMeta.label}
                      </span>
                    )}
                    {hasReassignments && <span className="reassign-badge"> (edited)</span>}
                    {!hasReassignments && hasConflictEdits && <span className="reassign-badge"> (conflict choices set)</span>}
                    {conflictCount > 0 && <span className="reassign-badge"> ({conflictCount} possible match{conflictCount === 1 ? '' : 'es'})</span>}
                    {standaloneDescription && (
                      <span style={{ display: 'block', marginTop: '0.25rem', color: '#6f654c', fontSize: '0.85em' }}>
                        {standaloneDescription}
                      </span>
                    )}
                  </span>
                </label>
                {showItemControls && (
                  <div className="reassignment-items" style={{ marginLeft: '1.5rem', marginTop: '0.25rem', fontSize: '0.85em' }}>
                    {suggestion.data.map((item, itemIdx) => {
                      const itemName = item?.name || item?.label || `Item ${itemIdx + 1}`;
                      const currentTarget = reassignMap[itemIdx] || '';
                      const targetField = currentTarget || suggestion.field;
                      const itemKnowledgeMeta = getItemKnowledgeMeta(item);
                      const conflicts = conflictMap[itemIdx] || [];
                      const rememberedConflictResolution = getRememberedSuggestionConflictResolution(item, targetField, conflicts);
                      const conflictResolution = getConflictResolutionValue(index, itemIdx, item, targetField, conflicts);
                      const conflictTargetValue = getConflictTargetValue(index, itemIdx, item, targetField, conflicts);
                      const rememberedConflictTarget = getRememberedSuggestionConflictTarget(item, targetField, conflicts);
                      const hasRememberedConflictChoice = !suggestionConflictResolutions?.[index]?.[itemIdx] && Boolean(rememberedConflictResolution);
                      const hasRememberedConflictTarget = !suggestionConflictTargets?.[index]?.[itemIdx] && Boolean(rememberedConflictTarget);
                      return (
                        <div key={itemIdx} className="reassignment-item" style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', padding: '4px 0' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ flex: '1 1 auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={itemName}>{itemName}</span>
                            {itemKnowledgeMeta && (
                              <span
                                className={`review-meta-chip suggestion-knowledge-chip ${itemKnowledgeMeta.className}`}
                                title={itemKnowledgeMeta.note || itemKnowledgeMeta.title}
                              >
                                {itemKnowledgeMeta.label}
                              </span>
                            )}
                            {isReassignable && (
                              <select
                                value={currentTarget}
                                onChange={(e) => handleReassignItem(index, itemIdx, e.target.value)}
                                style={{ fontSize: '0.85em', padding: '1px 4px', maxWidth: '140px' }}
                                title="Choose where this item belongs"
                              >
                                {reassignmentTargets
                                  .filter(t => t.value !== suggestion.field || t.value === '')
                                  .map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                  ))}
                              </select>
                            )}
                          </div>
                          {conflicts.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', marginLeft: '0.1rem' }}>
                              <span style={{ color: '#8a6116' }} title={conflicts.map(conflict => `${conflict.name}: ${conflict.description || 'Existing entry'}`).join('\n')}>
                                {conflicts[0].matchType === 'exact' ? 'Exact existing match' : 'Same name already exists'}:
                                {' '}
                                {conflicts[0].name}
                                {conflicts.length > 1 ? ` (+${conflicts.length - 1} more)` : ''}
                              </span>
                              {conflicts.length > 1 && (
                                <select
                                  value={conflictTargetValue}
                                  onChange={(e) => handleConflictTargetChange(index, itemIdx, e.target.value)}
                                  style={{ fontSize: '0.85em', padding: '1px 4px', maxWidth: '240px' }}
                                  title="Choose which existing entry SmartPaste should target"
                                >
                                  {conflicts.map((conflict) => {
                                    const descSnippet = conflict.description
                                      ? ` - ${conflict.description.substring(0, 36)}${conflict.description.length > 36 ? '...' : ''}`
                                      : '';
                                    return (
                                      <option key={conflict.fingerprint || `${conflict.name}-${conflict.index}`} value={conflict.fingerprint || ''}>
                                        {`${conflict.name}${descSnippet}`}
                                      </option>
                                    );
                                  })}
                                </select>
                              )}
                              <select
                                value={conflictResolution}
                                onChange={(e) => handleConflictResolutionChange(index, itemIdx, e.target.value)}
                                style={{ fontSize: '0.85em', padding: '1px 4px', maxWidth: '170px' }}
                                title={conflictResolutionTargets.find(target => target.value === conflictResolution)?.description || 'Choose what to do with this match'}
                              >
                                {conflictResolutionTargets.map(target => (
                                  <option key={target.value} value={target.value}>{target.label}</option>
                                ))}
                              </select>
                              {hasRememberedConflictChoice && (
                                <span style={{ color: '#8a6116' }} title="SmartPaste remembered this choice from earlier.">
                                  remembered
                                </span>
                              )}
                              {!hasRememberedConflictChoice && hasRememberedConflictTarget && (
                                <span style={{ color: '#8a6116' }} title="SmartPaste remembered which existing entry you targeted before.">
                                  match remembered
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSimulatorFinishLine = (finishLine) => {
    if (!finishLine) return null;

    const projected = finishLine.projectedCharacter;
    const readinessStatus = finishLine.readinessStatus;
    const simulatorActionCounts = finishLine.simulatorActionCounts || {
      actions: Array.isArray(projected?.actions) ? projected.actions.length : 0,
      bonusActions: Array.isArray(projected?.bonusActions) ? projected.bonusActions.length : 0,
      reactions: Array.isArray(projected?.reactions) ? projected.reactions.length : 0
    };
    const issueList = [
      ...(readinessStatus?.errors || []),
      ...(readinessStatus?.blockingWarnings || []),
      ...(readinessStatus?.informationalWarnings || [])
    ].filter(Boolean);
    const visibleIssues = issueList.slice(0, 3);

    return (
      <div className={`simulator-finish-line ${finishLine.status}`}>
        <div className="simulator-finish-line-header">
          <div>
            <h4>Combat Check</h4>
            <p>{finishLine.summary}</p>
          </div>
          <span className={`finish-line-pill ${finishLine.status}`}>
            {finishLine.title}
          </span>
        </div>

        <div className="finish-line-plan">
          <span>{formatCountLabel(finishLine.selectedSuggestionCount, 'selected detail')}</span>
          <span>{formatCountLabel(finishLine.readyReviewCount, 'extra detail ready', 'extra details ready')}</span>
          <span>{formatCountLabel(finishLine.appliedAssociationCount, 'linked item effect')}</span>
          {finishLine.unresolvedReviewCount > 0 && (
            <span>{formatCountLabel(finishLine.unresolvedReviewCount, 'detail')} still need attention</span>
          )}
          {finishLine.unresolvedRiderCount > 0 && (
            <span>{formatCountLabel(finishLine.unresolvedRiderCount, 'item effect')} still need a home</span>
          )}
        </div>

        {projected && (
          <div className="finish-line-metrics">
            <span>HP {projected.maxHp ?? projected.hp ?? '—'}</span>
            <span>AC {projected.ac ?? '—'}</span>
            <span>Speed {formatMovementSummary(projected.speed) || '—'}</span>
            <span>PB {projected.proficiencyBonus != null ? `+${projected.proficiencyBonus}` : '—'}</span>
            <span>{formatCountLabel(simulatorActionCounts.actions, 'action')}</span>
            <span>{formatCountLabel(simulatorActionCounts.bonusActions, 'bonus action')}</span>
            <span>{formatCountLabel(simulatorActionCounts.reactions, 'reaction')}</span>
            <span>{formatCountLabel(Array.isArray(projected?.equipment?.weapons) ? projected.equipment.weapons.length : 0, 'weapon')}</span>
          </div>
        )}

        {visibleIssues.length > 0 && (
          <ul className="finish-line-issues">
            {visibleIssues.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
            {issueList.length > visibleIssues.length && (
              <li>{`+${issueList.length - visibleIssues.length} more issue${issueList.length - visibleIssues.length === 1 ? '' : 's'}`}</li>
            )}
          </ul>
        )}

        {finishLine.finalApplyBlocked && finishLine.finalApplyGuidance && (
          <div className="finish-line-guidance">
            {finishLine.finalApplyGuidance}
          </div>
        )}
      </div>
    );
  };

  const isCharacterPrimaryReview = showPreview && parsedContent?.type === 'character';

  const toggleCharacterAdvancedPanel = (panelKey, forceOpen = null) => {
    setCharacterAdvancedPanels((prev) => ({
      ...prev,
      [panelKey]: typeof forceOpen === 'boolean' ? forceOpen : !prev[panelKey]
    }));
  };

  const getCharacterGroupKeyForField = (field) => characterFieldGroupMap[normalizeImportField(field)] || 'extras';

  const getCharacterSuggestionValueSummary = (suggestion) => {
    if (!suggestion) return '';
    const { field, data } = suggestion;

    if (field === 'abilities' && data && typeof data === 'object' && !Array.isArray(data)) {
      return ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma']
        .filter((ability) => Number.isFinite(data[ability]))
        .map((ability) => `${ability.slice(0, 3).toUpperCase()} ${data[ability]}`)
        .join(', ');
    }

    if (field === 'ac') {
      if (data && typeof data === 'object') {
        return `${data.value ?? ''}${data.notes ? ` (${data.notes})` : ''}`.trim();
      }
      return String(data ?? '');
    }

    if (field === 'hp') {
      if (data && typeof data === 'object') {
        return String(data.average ?? data.value ?? '');
      }
      return String(data ?? '');
    }

    if (field === 'speed') {
      return formatMovementSummary(data);
    }

    if (Array.isArray(data)) {
      const namedItems = data.map((item) => item?.name || item?.label).filter(Boolean);
      if (namedItems.length === 1) return namedItems[0];
      if (namedItems.length > 1) {
        return `${namedItems.slice(0, 3).join(', ')}${namedItems.length > 3 ? ` +${namedItems.length - 3} more` : ''}`;
      }
      return suggestion.preview || `${data.length} detected item${data.length === 1 ? '' : 's'}`;
    }

    if (typeof data === 'string' || typeof data === 'number') {
      return String(data);
    }

    if (data && typeof data === 'object') {
      if (data.name) return data.name;
      if (field === 'spellcasting') {
        return [
          data.ability ? `Ability ${String(data.ability).slice(0, 3).toUpperCase()}` : '',
          Number.isFinite(data.saveDC ?? data.saveDc) ? `Save DC ${data.saveDC ?? data.saveDc}` : '',
          Number.isFinite(data.attackBonus ?? data.attack) ? `Atk ${data.attackBonus ?? data.attack}` : ''
        ].filter(Boolean).join(', ');
      }
    }

    return suggestion.preview || '';
  };

  const renderCompactContentUseNotice = () => (
    <div className="character-content-use-footer">
      <div className="character-content-use-actions">
        <label className={`content-use-acknowledgement compact ${hasConfirmedContentUse ? 'checked' : ''}`}>
          <input
            type="checkbox"
            checked={hasConfirmedContentUse}
            onChange={handleContentUseAcknowledgementChange}
            disabled={!normalizedPastedText}
          />
          <span>{contentUsageNotice.smartPasteAcknowledgement}</span>
        </label>
        <a className="content-use-notice-link compact" href="/legal/sources">
          {contentUsageNotice.footerLinkLabel}
        </a>
      </div>
      {contentUseNeedsRefresh && (
        <p className="content-use-notice-refresh compact">
          The pasted text changed after confirmation. Review it and confirm again before applying.
        </p>
      )}
    </div>
  );

  const renderCharacterAdvancedPanel = (panelKey, title, summary, children, extraLabel = '') => (
    <div className={`character-advanced-panel ${characterAdvancedPanels[panelKey] ? 'open' : ''}`}>
      <button
        type="button"
        className="character-advanced-toggle"
        onClick={() => toggleCharacterAdvancedPanel(panelKey)}
        aria-expanded={characterAdvancedPanels[panelKey]}
      >
        <span className="character-advanced-title-wrap">
          <span className="character-advanced-title">{title}</span>
          <span className="character-advanced-summary">{summary}</span>
        </span>
        <span className="character-advanced-meta">
          {extraLabel ? <span className="character-advanced-pill">{extraLabel}</span> : null}
          <span className="character-advanced-caret">{characterAdvancedPanels[panelKey] ? '−' : '+'}</span>
        </span>
      </button>
      {characterAdvancedPanels[panelKey] && (
        <div className="character-advanced-body">
          {children}
        </div>
      )}
    </div>
  );

  const renderCharacterSuggestionRow = (suggestion, index) => {
    const checked = selectedSuggestionIndexes.includes(index);
    const isApplied = appliedSuggestionIndexes.includes(index);
    const targetField = suggestionFieldOverrides[index] || normalizeCharacterReviewField(suggestion.field);
    const suggestionData = Object.prototype.hasOwnProperty.call(suggestionDataOverrides, index)
      ? suggestionDataOverrides[index]
      : suggestion.data;
    const isReassignable = reassignableFieldSet.has(suggestion.field) && Array.isArray(suggestionData) && suggestionData.length > 0;
    const isScalarEditable = !Array.isArray(suggestionData) && (editableScalarFieldSet.has(targetField) || targetField === 'abilities');
    const reassignMap = suggestionReassignments[index] || {};
    const suggestionKnowledgeMeta = getSuggestionKnowledgeMeta(suggestion);
    const suggestionKnowledgeInsight = getSuggestionKnowledgeInsight(suggestion, index);
    const suggestionKnowledgeStatusMeta = getSuggestionKnowledgeStatusMeta(suggestionKnowledgeInsight);
    const conflictMap = Array.isArray(suggestionData)
      ? suggestionData.map((item, itemIdx) => {
          const targetField = reassignMap[itemIdx] || suggestion.field;
          if (targetField === SMART_PASTE_SKIP_REASSIGNMENT) return [];
          return getImportItemConflicts(item, targetField);
        })
      : [];
    const conflictCount = conflictMap.filter((conflicts) => conflicts.length > 0).length;
    const fieldLabel = formatCharacterFieldLabel(targetField);
    const fieldValue = getCharacterSuggestionValueSummary({ ...suggestion, field: targetField, data: suggestionData });
    const hasDetails = checked && Array.isArray(suggestionData) && suggestionData.length > 0;
    const scalarDestinationOptions = getScalarDestinationOptions(suggestion.field);
    const scalarInputValue = (() => {
      if (targetField === 'ac' && suggestionData && typeof suggestionData === 'object') {
        return suggestionData.value ?? '';
      }
      if (targetField === 'hp' && suggestionData && typeof suggestionData === 'object') {
        return suggestionData.average ?? suggestionData.value ?? suggestionData.current ?? '';
      }
      if (targetField === 'speed') return formatMovementSummary(suggestionData);
      return suggestionData ?? '';
    })();

    return (
      <div key={`${suggestion.field}-${index}`} className={`character-review-row ${checked ? 'selected' : ''} ${isApplied ? 'applied' : ''}`}>
        <label className="character-review-row-main">
          <input
            type="checkbox"
            checked={checked || isApplied}
            onChange={() => handleSuggestionToggle(index)}
            disabled={isApplied}
            aria-label={suggestion.preview}
          />
          <div className="character-review-row-copy">
            <div className="character-review-row-heading">
              <span className="character-review-field-label">{fieldLabel}</span>
              {fieldValue && <span className="character-review-field-value">{fieldValue}</span>}
            </div>
            <div className="character-review-preview-text">
              {targetField === normalizeCharacterReviewField(suggestion.field)
                ? suggestion.preview
                : `Found under ${formatCharacterFieldLabel(suggestion.field)}; it will be added to ${fieldLabel}.`}
            </div>
          </div>
          <div className="character-review-badges">
            {suggestionKnowledgeMeta && (
              <span
                className={`review-meta-chip suggestion-knowledge-chip ${suggestionKnowledgeMeta.className}`}
                title={suggestionKnowledgeMeta.note || suggestionKnowledgeMeta.title}
              >
                {suggestionKnowledgeMeta.label}
              </span>
            )}
            {suggestionKnowledgeStatusMeta && (
              <span
                className={`review-meta-chip suggestion-knowledge-chip ${suggestionKnowledgeStatusMeta.className}`}
                title={suggestionKnowledgeStatusMeta.title}
              >
                {suggestionKnowledgeStatusMeta.label}
              </span>
            )}
            {conflictCount > 0 && (
              <span className="review-meta-chip conflict-hint same-name">
                {formatCountLabel(conflictCount, 'possible match')}
              </span>
            )}
            {isApplied && <span className="review-meta-chip remembered-hint">Added!</span>}
          </div>
        </label>

        {checked && !isApplied && isScalarEditable && (
          <div className="character-review-scalar-controls">
            <label className="character-review-control">
              <span>Add to</span>
              <select
                value={targetField}
                onChange={(event) => handleSuggestionFieldOverride(index, event.target.value)}
                className="character-review-select wide"
                aria-label={`Add ${formatCharacterFieldLabel(suggestion.field)} to`}
              >
                {scalarDestinationOptions.map((target) => (
                  <option key={target.value} value={target.value}>{target.label}</option>
                ))}
              </select>
            </label>
            {targetField === 'abilities' && suggestionData && typeof suggestionData === 'object' ? (
              <div className="character-review-ability-editor" aria-label="Edit ability scores">
                {['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'].map((ability) => (
                  <label key={ability} className="character-review-ability-field">
                    <span>{ability.slice(0, 3).toUpperCase()}</span>
                    <input
                      type="number"
                      min="1"
                      max="30"
                      value={suggestionData[ability] ?? ''}
                      onChange={(event) => handleAbilityScoreChange(index, ability, event.target.value, suggestionData)}
                      aria-label={formatCharacterFieldLabel(ability)}
                    />
                  </label>
                ))}
              </div>
            ) : (
              <label className="character-review-control character-review-value-control">
                <span>Value</span>
                <input
                  type={numericCharacterFieldSet.has(targetField) ? 'number' : 'text'}
                  value={scalarInputValue}
                  onChange={(event) => handleScalarSuggestionValueChange(index, targetField, event.target.value)}
                  aria-label={`Value for ${fieldLabel}`}
                />
              </label>
            )}
          </div>
        )}

        {hasDetails && (
          <div className="character-review-item-list">
            {suggestionData.map((item, itemIdx) => {
              const itemName = item?.name || item?.label || `Item ${itemIdx + 1}`;
              const currentTarget = reassignMap[itemIdx] || '';
              const targetField = currentTarget && currentTarget !== SMART_PASTE_SKIP_REASSIGNMENT
                ? currentTarget
                : suggestion.field;
              const itemKnowledgeMeta = getItemKnowledgeMeta(item);
              const conflicts = currentTarget === SMART_PASTE_SKIP_REASSIGNMENT
                ? []
                : (conflictMap[itemIdx] || []);
              const conflictResolution = getConflictResolutionValue(index, itemIdx, item, targetField, conflicts);
              const conflictTargetValue = getConflictTargetValue(index, itemIdx, item, targetField, conflicts);
              const rememberedConflictResolution = getRememberedSuggestionConflictResolution(item, targetField, conflicts);
              const rememberedConflictTarget = getRememberedSuggestionConflictTarget(item, targetField, conflicts);
              const hasRememberedConflictChoice = !suggestionConflictResolutions?.[index]?.[itemIdx] && Boolean(rememberedConflictResolution);
              const hasRememberedConflictTarget = !suggestionConflictTargets?.[index]?.[itemIdx] && Boolean(rememberedConflictTarget);

              return (
                <div key={`${index}-${itemIdx}`} className="character-review-item">
                  <div className="character-review-item-topline">
                    <span className="character-review-item-name" title={itemName}>{itemName}</span>
                    {itemKnowledgeMeta && (
                      <span
                        className={`review-meta-chip suggestion-knowledge-chip ${itemKnowledgeMeta.className}`}
                        title={itemKnowledgeMeta.note || itemKnowledgeMeta.title}
                      >
                        {itemKnowledgeMeta.label}
                      </span>
                    )}
                    {isReassignable && (
                      <select
                        value={currentTarget}
                        onChange={(event) => handleReassignItem(index, itemIdx, event.target.value)}
                        className="character-review-select"
                        title="Choose where this item belongs"
                      >
                        {reassignmentTargets
                          .filter((target) => target.value !== suggestion.field || target.value === '' || target.value === SMART_PASTE_SKIP_REASSIGNMENT)
                          .map((target) => (
                            <option key={target.value || 'keep'} value={target.value}>{target.label}</option>
                          ))}
                      </select>
                    )}
                  </div>
                  {currentTarget === SMART_PASTE_SKIP_REASSIGNMENT && (
                    <div className="character-review-inline-note">This item will not be added to the character sheet.</div>
                  )}
                  {conflicts.length > 0 && currentTarget !== SMART_PASTE_SKIP_REASSIGNMENT && (
                    <div className="character-review-conflict-row">
                      <span className="character-review-conflict-label">
                        {conflicts[0].matchType === 'exact' ? 'Exact existing match' : 'Same name already exists'}: {conflicts[0].name}
                        {conflicts.length > 1 ? ` (+${conflicts.length - 1} more)` : ''}
                      </span>
                      {conflicts.length > 1 && (
                        <select
                          value={conflictTargetValue}
                          onChange={(event) => handleConflictTargetChange(index, itemIdx, event.target.value)}
                          className="character-review-select wide"
                          title="Choose which existing sheet entry this matches"
                        >
                          {conflicts.map((conflict) => (
                            <option key={conflict.fingerprint || `${conflict.name}-${conflict.index}`} value={conflict.fingerprint || ''}>
                              {conflict.name}
                            </option>
                          ))}
                        </select>
                      )}
                      <select
                        value={conflictResolution}
                        onChange={(event) => handleConflictResolutionChange(index, itemIdx, event.target.value)}
                        className="character-review-select"
                        title={conflictResolutionTargets.find((target) => target.value === conflictResolution)?.description || 'Choose what to do with this match'}
                      >
                        {conflictResolutionTargets.map((target) => (
                          <option key={target.value} value={target.value}>{target.label}</option>
                        ))}
                      </select>
                      {hasRememberedConflictChoice && <span className="character-review-inline-note">Remembered choice</span>}
                      {!hasRememberedConflictChoice && hasRememberedConflictTarget && <span className="character-review-inline-note">Match remembered</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderCharacterReviewItem = (item) => {
    const insight = buildReviewItemInsight(item);
    const selectedCategory = insight.selectedCats[0] || '';
    const preview = buildReviewOutcomePreview(item, insight.selectedCats);
    const knowledgeMeta = getItemKnowledgeMeta(item);
    const knowledgeStatusMeta = getReviewKnowledgeStatusMeta(insight);
    const activeConflict = selectedCategory
      ? insight.reviewConflictEntries.find((entry) => entry.category === selectedCategory) || null
      : null;
    const sourceLabel = formatReviewSource(item);
    const locationLabel = formatReviewLocation(item);
    const isExpanded = Boolean(expandedReviewItems[item.key]);

    return (
      <div key={item.key} className={`character-review-extra ${isExpanded ? 'expanded' : ''}`}>
        <div className="character-review-extra-topline">
          <button
            type="button"
            className="character-review-extra-title"
            onClick={() => toggleExpandReviewItem(item.key, item)}
          >
            {preview.title || item.name}
          </button>
          <select
            value={selectedCategory}
            onChange={(event) => setCategoriesForReviewItems([item], event.target.value ? [event.target.value] : [])}
            className="character-review-select"
            aria-label={`Choose where ${item.name} should go`}
          >
            {primaryReviewDestinationOptions.map((option) => (
              <option key={option.value || 'skip'} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className="character-review-extra-summary">{preview.summary}</div>
        <div className="character-review-badges">
          {selectedCategory && (
            <span className="review-meta-chip remembered-hint">
              {preview.destinationLabel || formatCatalogKindLabel(selectedCategory)}
            </span>
          )}
          {knowledgeMeta && (
            <span
              className={`review-meta-chip suggestion-knowledge-chip ${knowledgeMeta.className}`}
              title={knowledgeMeta.note || knowledgeMeta.title}
            >
              {knowledgeMeta.label}
            </span>
          )}
          {knowledgeStatusMeta && (
            <span
              className={`review-meta-chip suggestion-knowledge-chip ${knowledgeStatusMeta.className}`}
              title={knowledgeStatusMeta.title}
            >
              {knowledgeStatusMeta.label}
            </span>
          )}
          {item.isSectionFragment && <span className="review-meta-chip section-leftover-chip">Unplaced detail</span>}
          {insight.reviewConflictHints.map((hint) => (
            <span key={hint.key} className={`review-meta-chip conflict-hint ${hint.isExact ? 'exact-match' : 'same-name'}`} title={hint.title}>
              {hint.summary}
            </span>
          ))}
          {insight.hasRememberedReviewConflictChoice && <span className="review-meta-chip remembered-hint">Remembered choice</span>}
        </div>

        {isExpanded && (
          <div className="character-review-extra-details">
            {(sourceLabel || locationLabel) && (
              <div className="character-review-context-row">
                {sourceLabel && (
                  <span className="character-review-context">
                    <span className="context-label">{item.isSectionFragment ? 'Unplaced detail from:' : 'From paste:'}</span>
                    <span className="context-value">{sourceLabel}</span>
                  </span>
                )}
                {locationLabel && (
                  <span className="character-review-context">
                    <span className="context-label">Location:</span>
                    <span className="context-value">{locationLabel}</span>
                  </span>
                )}
              </div>
            )}
            {activeConflict && (
              <div className="character-review-conflict-row expanded">
                <span className="character-review-conflict-label">
                  {activeConflict.conflicts[0]?.matchType === 'exact' ? 'Exact existing match' : 'Same name already exists'}: {activeConflict.conflicts[0]?.name}
                </span>
                {activeConflict.conflicts.length > 1 && (
                  <select
                    value={activeConflict.conflictTargetValue}
                    onChange={(event) => handleReviewConflictTargetChange(item.key, activeConflict.category, event.target.value)}
                    className="character-review-select wide"
                    title="Choose which existing entry SmartPaste should target"
                  >
                    {activeConflict.conflicts.map((conflict) => (
                      <option key={conflict.fingerprint || `${conflict.name}-${conflict.index}`} value={conflict.fingerprint || ''}>
                        {conflict.name}
                      </option>
                    ))}
                  </select>
                )}
                <select
                  value={activeConflict.conflictResolution}
                  onChange={(event) => handleReviewConflictResolutionChange(item.key, activeConflict.category, event.target.value)}
                  className="character-review-select"
                >
                  {conflictResolutionTargets.map((target) => (
                    <option key={target.value} value={target.value}>{target.label}</option>
                  ))}
                </select>
                {activeConflict.hasRememberedConflictChoice && <span className="character-review-inline-note">Remembered choice</span>}
                {!activeConflict.hasRememberedConflictChoice && activeConflict.hasRememberedConflictTarget && <span className="character-review-inline-note">Match remembered</span>}
              </div>
            )}
            <div className="character-review-extra-preview">
              <div className="character-review-extra-preview-label">Will be added to:</div>
              <div className="character-review-extra-preview-body">{preview.destinationLabel || 'Skipped for now'}</div>
            </div>
            {preview.detailChips.length > 0 && (
              <div className="character-review-extra-chip-row">
                {preview.detailChips.map((chip) => (
                  <span key={`${item.key}-${chip}`} className="character-review-detail-chip">{chip}</span>
                ))}
              </div>
            )}
            {preview.rawSourceText && (
              <div className="character-review-source-block">
                <div className="character-review-extra-preview-label">Pasted text:</div>
                <div className="character-review-source-text">{preview.rawSourceText}</div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderRiderAssociationSections = () => (
    <>
      {parsedContent?.data?.weaponRiders?.length > 0 && (
        <div className="weapon-riders-section needs-review-section">
          <div className="needs-review-header">
            <h4>
              <span className="header-icon">🔗</span>
              Choose a Weapon for These Effects
            </h4>
            <p className="review-intro">
              These weapon effects may belong to a specific weapon. Choose where each one goes:
            </p>
          </div>
          <div className="review-items-list">
            {parsedContent.data.weaponRiders.map((rider, idx) => {
              const riderKey = `weapon_rider_${idx}`;
              const assoc = riderAssociations[riderKey];
              const rememberedChoice = assoc?.status === 'remembered' ? assoc : null;
              const weaponCandidates = rankAssociationCandidates(
                rider,
                getAssociationCandidatesFromData(parsedContent.data, 'weapon_rider'),
                'weapon_rider',
                rememberedChoice
              );
              const rememberedCandidate = rememberedChoice?.parentFingerprint
                ? weaponCandidates.find((candidate) => candidate.fingerprint === rememberedChoice.parentFingerprint)
                : weaponCandidates.find((candidate) => candidate.name === rememberedChoice?.parentName);
              const suggestedCandidate = rider?.suggestedParentFingerprint
                ? weaponCandidates.find((candidate) => candidate.fingerprint === rider.suggestedParentFingerprint)
                : weaponCandidates.find((candidate) => candidate.name === rider?.suggestedParent);
              const contextualCandidate = weaponCandidates.find((candidate) => (
                candidate.associationScore >= 45 &&
                candidate.fingerprint !== suggestedCandidate?.fingerprint &&
                candidate.fingerprint !== rememberedCandidate?.fingerprint
              ));
              const isHandled = assoc?.status === 'applied' || assoc?.status === 'skipped';

              if (isHandled) {
                const appliedLabel = assoc?.status === 'applied'
                  ? (assoc?.mode === 'standalone'
                    ? 'Standalone item'
                    : (assoc?.parentName ? `Assigned to ${assoc.parentName}` : 'Assigned!'))
                  : 'Skipped';
                return (
                  <div key={riderKey} className="review-item applied">
                    <div className="review-item-content">
                      <span className="applied-check">✓</span>
                      <span className="review-item-name">{rider.name}</span>
                      <span className="applied-label">{appliedLabel}</span>
                    </div>
                  </div>
                );
              }

              return (
                <div key={riderKey} className="review-item actionable expanded">
                  <div className="review-item-content">
                    <div className="review-item-name">{'"'}{rider.name}{'"'}</div>
                    {rememberedChoice && (
                      <div className="suggested-category-badge">
                        <span className="badge-label">Remembered choice:</span>
                        <span className="badge-value">
                          {rememberedChoice.mode === 'standalone'
                            ? 'Keep as standalone item'
                            : (rememberedCandidate?.name || rememberedChoice.parentName)}
                        </span>
                        <button
                          className="accept-suggestion-btn"
                          onClick={() => setRiderAssociations(prev => ({
                            ...prev,
                            [riderKey]: {
                              status: 'applied',
                              parentName: rememberedCandidate?.name || rememberedChoice.parentName || '',
                              parentFingerprint: rememberedCandidate?.fingerprint || rememberedChoice.parentFingerprint || '',
                              mode: rememberedChoice.mode || 'attach'
                            }
                          }))}
                        >
                          Use remembered choice
                        </button>
                      </div>
                    )}
                    {suggestedCandidate && (
                      <div className="suggested-category-badge">
                        <span className="badge-label">Likely belongs with:</span>
                        <span className="badge-value">{suggestedCandidate.name}</span>
                        <button
                          className="accept-suggestion-btn"
                          onClick={() => setRiderAssociations(prev => ({
                            ...prev,
                            [riderKey]: {
                              status: 'applied',
                              parentName: suggestedCandidate.name,
                              parentFingerprint: suggestedCandidate.fingerprint || '',
                              mode: 'attach'
                            }
                          }))}
                        >
                          Yes, attach it
                        </button>
                      </div>
                    )}
                    {!suggestedCandidate && contextualCandidate && (
                      <div className="suggested-category-badge">
                        <span className="badge-label">Possible match:</span>
                        <span className="badge-value">{contextualCandidate.name}</span>
                        <button
                          className="accept-suggestion-btn"
                          onClick={() => setRiderAssociations(prev => ({
                            ...prev,
                            [riderKey]: {
                              status: 'applied',
                              parentName: contextualCandidate.name,
                              parentFingerprint: contextualCandidate.fingerprint || '',
                              mode: 'attach'
                            }
                          }))}
                        >
                          Use this match
                        </button>
                      </div>
                    )}
                    <div className="action-buttons-row" style={{ marginTop: '8px' }}>
                      {weaponCandidates
                        .filter((weapon) => (
                          weapon?.name &&
                          weapon.fingerprint !== suggestedCandidate?.fingerprint &&
                          weapon.fingerprint !== rememberedCandidate?.fingerprint &&
                          weapon.fingerprint !== contextualCandidate?.fingerprint
                        ))
                        .map((weapon, weaponIndex) => (
                          <button
                            key={weaponIndex}
                            className="action-btn equipment"
                            onClick={() => setRiderAssociations(prev => ({
                              ...prev,
                              [riderKey]: {
                                status: 'applied',
                                parentName: weapon.name,
                                parentFingerprint: weapon.fingerprint || '',
                                mode: 'attach'
                              }
                            }))}
                          >
                            {`Attach to ${weapon.name}`}
                          </button>
                        ))}
                      <button
                        className="action-btn notes"
                        onClick={() => setRiderAssociations(prev => ({
                          ...prev,
                          [riderKey]: { status: 'applied', parentName: '', parentFingerprint: '', mode: 'standalone' }
                        }))}
                      >
                        Keep as standalone item
                      </button>
                      <button
                        className="action-btn skip"
                        onClick={() => setRiderAssociations(prev => ({ ...prev, [riderKey]: { status: 'skipped' } }))}
                      >
                        Skip
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {parsedContent?.data?.equipmentRiders?.length > 0 && (
        <div className="weapon-riders-section needs-review-section">
          <div className="needs-review-header">
            <h4>
              <span className="header-icon">🔗</span>
              Choose an Item for These Effects
            </h4>
            <p className="review-intro">
              These effects may belong to a specific item. Choose where each one goes:
            </p>
          </div>
          <div className="review-items-list">
            {parsedContent.data.equipmentRiders.map((rider, idx) => {
              const riderKey = `equip_rider_${idx}`;
              const assoc = riderAssociations[riderKey];
              const rememberedChoice = assoc?.status === 'remembered' ? assoc : null;
              const itemCandidates = rankAssociationCandidates(
                rider,
                getAssociationCandidatesFromData(parsedContent.data, 'equipment_rider'),
                'equipment_rider',
                rememberedChoice
              );
              const rememberedCandidate = rememberedChoice?.parentFingerprint
                ? itemCandidates.find((candidate) => candidate.fingerprint === rememberedChoice.parentFingerprint)
                : itemCandidates.find((candidate) => candidate.name === rememberedChoice?.parentName);
              const suggestedCandidate = rider?.suggestedParentFingerprint
                ? itemCandidates.find((candidate) => candidate.fingerprint === rider.suggestedParentFingerprint)
                : itemCandidates.find((candidate) => candidate.name === rider?.suggestedParent);
              const contextualCandidate = itemCandidates.find((candidate) => (
                candidate.associationScore >= 45 &&
                candidate.fingerprint !== suggestedCandidate?.fingerprint &&
                candidate.fingerprint !== rememberedCandidate?.fingerprint
              ));
              const isHandled = assoc?.status === 'applied' || assoc?.status === 'skipped';

              if (isHandled) {
                const appliedLabel = assoc?.status === 'applied'
                  ? (assoc?.mode === 'standalone'
                    ? 'Standalone item'
                    : (assoc?.parentName ? `Assigned to ${assoc.parentName}` : 'Assigned!'))
                  : 'Skipped';
                return (
                  <div key={riderKey} className="review-item applied">
                    <div className="review-item-content">
                      <span className="applied-check">✓</span>
                      <span className="review-item-name">{rider.name}</span>
                      <span className="applied-label">{appliedLabel}</span>
                    </div>
                  </div>
                );
              }

              return (
                <div key={riderKey} className="review-item actionable expanded">
                  <div className="review-item-content">
                    <div className="review-item-name">{`"${rider.name}"`}</div>
                    {rememberedChoice && (
                      <div className="suggested-category-badge">
                        <span className="badge-label">Remembered choice:</span>
                        <span className="badge-value">
                          {rememberedChoice.mode === 'standalone'
                            ? 'Keep as standalone item'
                            : (rememberedCandidate?.name || rememberedChoice.parentName)}
                        </span>
                        <button
                          className="accept-suggestion-btn"
                          onClick={() => setRiderAssociations(prev => ({
                            ...prev,
                            [riderKey]: {
                              status: 'applied',
                              parentName: rememberedCandidate?.name || rememberedChoice.parentName || '',
                              parentFingerprint: rememberedCandidate?.fingerprint || rememberedChoice.parentFingerprint || '',
                              mode: rememberedChoice.mode || 'attach'
                            }
                          }))}
                        >
                          Use remembered choice
                        </button>
                      </div>
                    )}
                    {suggestedCandidate && (
                      <div className="suggested-category-badge">
                        <span className="badge-label">Likely belongs with:</span>
                        <span className="badge-value">{suggestedCandidate.name}</span>
                        <button
                          className="accept-suggestion-btn"
                          onClick={() => setRiderAssociations(prev => ({
                            ...prev,
                            [riderKey]: {
                              status: 'applied',
                              parentName: suggestedCandidate.name,
                              parentFingerprint: suggestedCandidate.fingerprint || '',
                              mode: 'attach'
                            }
                          }))}
                        >
                          Yes, attach it
                        </button>
                      </div>
                    )}
                    {!suggestedCandidate && contextualCandidate && (
                      <div className="suggested-category-badge">
                        <span className="badge-label">Possible match:</span>
                        <span className="badge-value">{contextualCandidate.name}</span>
                        <button
                          className="accept-suggestion-btn"
                          onClick={() => setRiderAssociations(prev => ({
                            ...prev,
                            [riderKey]: {
                              status: 'applied',
                              parentName: contextualCandidate.name,
                              parentFingerprint: contextualCandidate.fingerprint || '',
                              mode: 'attach'
                            }
                          }))}
                        >
                          Use this match
                        </button>
                      </div>
                    )}
                    <div className="action-buttons-row" style={{ marginTop: '8px' }}>
                      {itemCandidates
                        .filter((candidate) => (
                          candidate?.name &&
                          candidate.fingerprint !== suggestedCandidate?.fingerprint &&
                          candidate.fingerprint !== rememberedCandidate?.fingerprint &&
                          candidate.fingerprint !== contextualCandidate?.fingerprint
                        ))
                        .map((candidate, candidateIndex) => (
                          <button
                            key={candidateIndex}
                            className="action-btn equipment"
                            onClick={() => setRiderAssociations(prev => ({
                              ...prev,
                              [riderKey]: {
                                status: 'applied',
                                parentName: candidate.name,
                                parentFingerprint: candidate.fingerprint || '',
                                mode: 'attach'
                              }
                            }))}
                          >
                            {`Attach to ${candidate.name}`}
                          </button>
                        ))}
                      <button
                        className="action-btn notes"
                        onClick={() => setRiderAssociations(prev => ({
                          ...prev,
                          [riderKey]: { status: 'applied', parentName: '', parentFingerprint: '', mode: 'standalone' }
                        }))}
                      >
                        Keep as standalone item
                      </button>
                      <button
                        className="action-btn skip"
                        onClick={() => setRiderAssociations(prev => ({ ...prev, [riderKey]: { status: 'skipped' } }))}
                      >
                        Skip
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );

  const renderCharacterPreviewSection = () => {
    const isCompleteDndBeyondImport = parsedContent?.data?.sourceFormat === DND_BEYOND_SOURCE_FORMAT
      && parsedContent?.data?.completeCharacterImport === true;
    const completeImportSuggestionIndex = isCompleteDndBeyondImport
      ? (suggestions || []).findIndex((suggestion) => suggestion?.field === 'all')
      : -1;
    const completeImportSuggestion = completeImportSuggestionIndex >= 0
      ? suggestions[completeImportSuggestionIndex]
      : null;
    const groupedSuggestions = (suggestions || []).reduce((accumulator, suggestion, index) => {
      if (!suggestion || isStandaloneImportSuggestion(suggestion)) return accumulator;
      const groupKey = getCharacterGroupKeyForField(suggestion.field);
      if (!accumulator[groupKey]) accumulator[groupKey] = [];
      accumulator[groupKey].push({ suggestion, index });
      return accumulator;
    }, {});
    const actionableReviewItems = getAllUncategorizedItems().filter((item) => {
      const decision = uncategorizedDecisions[item.key];
      return decision !== 'applied' && decision !== 'skipped';
    });
    const groupedReviewItems = actionableReviewItems.reduce((accumulator, item) => {
      const selectedCategories = normalizeReviewCategories(uncategorizedDecisions[item.key]);
      const inferredCategories = selectedCategories.length > 0 ? selectedCategories : deriveInitialCategoriesFromItem(item);
      const groupKey = getCharacterGroupKeyForField(inferredCategories[0] || 'notes');
      if (!accumulator[groupKey]) accumulator[groupKey] = [];
      accumulator[groupKey].push(item);
      return accumulator;
    }, {});
    const selectedSectionCount = selectedSuggestionIndexes.filter((index) => (
      suggestions[index] &&
      !isStandaloneImportSuggestion(suggestions[index]) &&
      !appliedSuggestionIndexes.includes(index)
    )).length;
    const unresolvedReviewCount = actionableReviewItems.filter((item) => {
      const insight = buildReviewItemInsight(item);
      return insight.needsCategorySelection || insight.needsConflictAttention || insight.needsKnowledgeConfirmation;
    }).length;
    const advancedReviewCount = (isCompleteDndBeyondImport ? 0 : (suggestions || []).filter(isStandaloneImportSuggestion).length)
      + (parsedContent?.data?.weaponRiders?.length || 0)
      + (parsedContent?.data?.equipmentRiders?.length || 0);
    const summaryName = parsedContent?.data?.name ? `${parsedContent.data.name}` : 'this character';

    return (
      <div className="character-smart-paste">
        <div className="character-smart-paste-summary">
          <div>
            <h4>Review Character Details</h4>
            <p>
              We found these details for {summaryName}. Check each one and change anything that does not look right.
            </p>
          </div>
          <div className="character-smart-paste-summary-meta">
            <span className={`confidence-badge ${getConfidenceClass(parsedContent?.confidence || 0)}`}>
              {getConfidenceLabel(parsedContent?.confidence || 0)}
            </span>
            <span className="character-summary-chip">{formatCountLabel(selectedSectionCount, 'detail ready', 'details ready')}</span>
            {unresolvedReviewCount > 0 && (
              <span className="character-summary-chip warning">
                {unresolvedReviewCount === 1 ? '1 detail needs attention' : `${unresolvedReviewCount} details need attention`}
              </span>
            )}
            {advancedReviewCount > 0 && (
              <span className="character-summary-chip">{formatCountLabel(advancedReviewCount, 'more choice')}</span>
            )}
          </div>
        </div>

        {completeImportSuggestion && (
          <section className="character-primary-card dnd-beyond-complete-import">
            <div className="character-primary-card-header">
              <div>
                <h5>D&D Beyond character ready</h5>
                <p>
                  Smart Paste matched the complete character, including every class level. Review the fields below, then choose the complete character.
                </p>
              </div>
              <div className="character-primary-card-meta">
                <span className="character-primary-count">Complete import</span>
              </div>
            </div>
            <div className="character-primary-card-body">
              {renderCharacterSuggestionRow(completeImportSuggestion, completeImportSuggestionIndex)}
              {renderWhatWeFound()}
            </div>
          </section>
        )}

        <div className="character-primary-grid">
          {characterPrimaryGroups.map((group) => {
            const groupSuggestions = groupedSuggestions[group.key] || [];
            const groupReviewItems = groupedReviewItems[group.key] || [];
            if (groupSuggestions.length === 0 && groupReviewItems.length === 0) return null;

            const warningCount = groupReviewItems.filter((item) => {
              const insight = buildReviewItemInsight(item);
              return insight.needsCategorySelection || insight.needsConflictAttention || insight.needsKnowledgeConfirmation;
            }).length;

            return (
              <section key={group.key} className="character-primary-card">
                <div className="character-primary-card-header">
                  <div>
                    <h5>{group.title}</h5>
                    <p>{group.description}</p>
                  </div>
                  <div className="character-primary-card-meta">
                    <span className="character-primary-count">
                      {formatCountLabel(groupSuggestions.length + groupReviewItems.length, 'item')}
                    </span>
                    {warningCount > 0 && (
                      <span className="character-primary-warning">
                        {warningCount === 1 ? '1 item needs attention' : `${warningCount} items need attention`}
                      </span>
                    )}
                  </div>
                </div>
                <div className="character-primary-card-body">
                  {groupSuggestions.map(({ suggestion, index }) => renderCharacterSuggestionRow(suggestion, index))}
                  {groupReviewItems.map((item) => renderCharacterReviewItem(item))}
                </div>
              </section>
            );
          })}
        </div>

        {(!isCompleteDndBeyondImport || advancedReviewCount > 0) && renderCharacterAdvancedPanel(
          'reviewTools',
          'More Options',
          'Review repeated details, whole-sheet choices, matching items, and anything SmartPaste adjusted.',
          <>
            {governanceReport && <GovernanceReportPanel report={governanceReport} />}
            {!isCompleteDndBeyondImport && renderSuggestions()}
            {renderRiderAssociationSections()}
            {renderNeedsReview()}
            <div className="character-advanced-actions">
              <button
                type="button"
                onClick={handleApplySelectedAndReady}
                disabled={!canApplySelectedAndReady() || isApplying}
                className="btn btn-secondary"
              >
                Add Ready Details
              </button>
            </div>
          </>,
          advancedReviewCount > 0 ? `${advancedReviewCount}` : ''
        )}

        {renderCharacterAdvancedPanel(
          'simulator',
          'Combat Check',
          'Check this character’s actions, movement, weapons, and anything still needed for combat.',
          <>
            {renderCombatActionEconomySection(combatActionEconomyPreview)}
            {renderSimulatorFinishLine(simulatorFinishLine)}
          </>
        )}

        {renderCharacterAdvancedPanel(
          'previewHelp',
          'Paste Help',
          'See what SmartPaste found and try example character formats.',
          <>
            {renderExamplesPanel(true)}
            {renderContentPreview()}
            {renderWhatWeFound()}
          </>
        )}

        <div className="character-smart-paste-footer">
          {renderCompactContentUseNotice()}

          <div className="action-buttons">
            <button
              onClick={handleApplyChanges}
              disabled={!canApplyChanges() || isApplying}
              className="btn btn-primary btn-apply"
            >
              <span className="btn-icon">✓</span>
              {isApplying ? 'Adding…' : 'Add to Character Sheet'}
            </button>
            <button
              onClick={() => {
                setIsSourceExpanded(true);
                requestAnimationFrame(() => textareaRef.current?.focus());
              }}
              className="btn btn-secondary"
              disabled={isApplying}
            >
              Edit Paste
            </button>
          </div>
        </div>
      </div>
    );
  };

  const combatActionEconomyPreview = buildCombatActionEconomyPreview();
  const simulatorFinishLine = buildSimulatorFinishLine();

  return (
    <div className={`smart-paste-interface ${(context.entityType === 'character' || parsedContent?.type === 'character') ? 'character-import' : ''} ${disabled ? 'disabled' : ''} ${isDragOver ? 'drag-over' : ''}`}>
      {/* Header with title and help toggle */}
      <div className="smart-paste-header">
        <h3 className="smart-paste-title">
          <span className="title-icon">📋</span>
          {(context.entityType === 'character' || parsedContent?.type === 'character') ? 'Review Character Details' : 'Smart Paste'}
        </h3>
        <button 
          className={`help-toggle ${(isCharacterPrimaryReview ? characterAdvancedPanels.previewHelp : showExamples) ? 'active' : ''}`}
          onClick={() => {
            if (isCharacterPrimaryReview) {
              toggleCharacterAdvancedPanel('previewHelp');
            } else {
              setShowExamples(!showExamples);
            }
          }}
          title={isCharacterPrimaryReview ? 'Show preview and example formats' : 'Show example formats'}
        >
          <span>?</span> {isCharacterPrimaryReview ? 'Help' : 'Examples'}
        </button>
      </div>

      {/* Examples Panel */}
      {!isCharacterPrimaryReview && renderExamplesPanel()}

      {/* Main input area with drag-drop support */}
      {isCharacterPrimaryReview && !isSourceExpanded ? (
        <div className="character-source-summary">
          <div className="character-source-summary-copy">
            <span className="character-source-summary-label">Pasted character details</span>
            <span className="character-source-summary-text">
              {normalizedPastedText.length > 180 ? `${normalizedPastedText.slice(0, 180)}…` : normalizedPastedText}
            </span>
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              setIsSourceExpanded(true);
              requestAnimationFrame(() => textareaRef.current?.focus());
            }}
          >
            Edit pasted text
          </button>
        </div>
      ) : (
        <div
          ref={dropZoneRef}
          className={`paste-input-section ${isDragOver ? 'drag-active' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
        {isDragOver && (
          <div className="drop-overlay">
            <span className="drop-icon">📄</span>
            <span className="drop-text">Drop your text file here</span>
          </div>
        )}
        
        {!pastedText && !isDragOver && (
          <div className="empty-state-hints">
            <span className="hint-item">📋 Paste text with <kbd>Ctrl+V</kbd></span>
            <span className="hint-divider">or</span>
            <span className="hint-item">📄 Drag & drop a .txt file</span>
          </div>
        )}

        <textarea
          ref={textareaRef}
          value={pastedText}
          onChange={handleTextChange}
          onPaste={handlePaste}
          placeholder={placeholder}
          disabled={disabled}
          className="paste-textarea"
          rows={6}
        />

        <div className="input-controls">
          <div className="left-controls">
            <button
              onClick={handleClear}
              disabled={!pastedText}
              className="btn btn-secondary btn-sm"
            >
              🗑️ Clear
            </button>
            {pastedText && (
              <span className="char-count">{pastedText.length} characters</span>
            )}
            {isCharacterPrimaryReview && pastedText && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setIsSourceExpanded(false)}
              >
                Return to review
              </button>
            )}
          </div>
          {isProcessing && (
            <div className="processing-indicator">
              <span className="spinner"></span>
              <span className="processing-text">Analyzing content...</span>
            </div>
          )}
        </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {showPreview && parsedContent && (
        <div className="preview-section">
          {/* date_of_change: 2026-05-03
              rule_reference: D&D Character Data Governance Standards - Show validation report */}
          {governanceReport && parsedContent?.type !== 'character' && (
            <GovernanceReportPanel report={governanceReport} />
          )}
          {isCharacterPrimaryReview ? (
            renderCharacterPreviewSection()
          ) : (
            <>
              {renderContentPreview()}
              
              {/* What We Found Summary - @date_of_change 2025-12-05 */}
              {renderWhatWeFound()}
              {renderCombatActionEconomySection(combatActionEconomyPreview)}
              {renderRiderAssociationSections()}
              {/* Needs Human Review - @date_of_change 2025-12-06 */}
              {renderNeedsReview()}
              
              {renderSuggestions()}

              {renderSimulatorFinishLine(simulatorFinishLine)}
              {renderCompactContentUseNotice()}

              <div className="action-buttons">
                <button
                  onClick={handleApplySelectedAndReady}
                  disabled={!canApplySelectedAndReady() || isApplying}
                  className="btn btn-secondary"
                >
                  Add Ready Details
                </button>
                <button
                  onClick={handleApplyChanges}
                  disabled={!canApplyChanges() || isApplying}
                  className="btn btn-primary btn-apply"
                >
                  <span className="btn-icon">✓</span>
                  {isApplying ? 'Adding…' : 'Add to Character Sheet'}
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div className="history-section">
          <h4>Added This Session ({history.length})</h4>
          <div className="history-list">
            {history.slice(-3).reverse().map((change, index) => (
              <div key={index} className="history-item">
                <span className="change-icon">{renderTypeIcon(change.parsedContent.type)}</span>
                <span className="change-text">{formatRecentChange(change)}</span>
                <span className="change-time">
                  {new Date(change.timestamp).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartPasteInterface;
